/**
 * /app — authenticated Workspace mount.
 *
 * Architecture refs:
 *   architecture.md §4.22 — BackendApi contract
 *   architecture.md §4.4  — CRDT + sync DO
 *   architecture.md §4.8  — media pipeline (mint-put-url + finalize)
 *   design.md §6          — full-viewport Workspace shell
 *   .plan/implementation/2026-04-26-phase-4-handover.md §4.1–§4.5, §4.9.1
 *
 * Session gate (§4.1):
 *   1. GET /api/auth/get-session on mount.
 *   2a. Valid session → mount Workspace with RemoteBackend.
 *   2b. No session → render SignInGate (signup + login forms).
 *   2c. Network error → render offline banner (no silent retry).
 *
 * After sign-in:
 *   - Track 4.E: if users.onboarding_completed_at is null, render
 *     <Wizard> before the Workspace.
 *
 * Sync DO (§4.2):
 *   The WorkspaceContainer subscribes to the sync DO for the current draft
 *   doc key and wires `syncState` to the Workspace shell.
 *
 * Media upload (§4.4):
 *   The media pipeline (mint-put-url + finalize) re-wires in R3 (photos wave).
 *   The upload-client adapter lives in @proof/client-core (media/upload.ts);
 *   this route constructs no media client until that wave.
 *
 * Feedback (§4.9.1):
 *   `onFeedbackSubmit` and `onFeedbackPresignUrl` are wired from the typed
 *   oRPC procedures so the BugIcon becomes visible in the TopBar.
 *
 * Demo mode is deferred post-MVP per T3.1 (2026-05-07 architectural review).
 * This file handles only the authenticated /app path.
 *
 * CLAUDE.md Rule 7 — mobile + tablet + desktop verified before ship.
 * CLAUDE.md honesty rule — no fake data, no silent stub wiring.
 */

import {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
  useMemo,
  lazy,
  Suspense,
} from "react";
import { flushSync } from "react-dom";
import { toast } from "sonner";
import { AppToaster } from "~/features/shell/AppToaster";
import { useLocation, useNavigate, useParams } from "react-router";
import "@proof/ui/tokens";
import { RELOAD_FLAG_KEY } from "~/features/pwa/reload";
// R2 funnel instrumentation — display-mode split + `appinstalled` beacon.
import { useFunnelBeacon } from "~/features/analytics/useFunnelBeacon";
// Wave PERF-1 — recipe-open boundary marks.
import { mark as perfMark, reset as perfReset, RECIPE_OPEN_MARKS } from "~/lib/perf";
// Wave PERF-3a — F4: chunk prefetch alongside the data prefetch on hover.
import { prefetchChunksOnIntent } from "~/lib/prefetch-chunks";
import { isNavigationAbort } from "~/lib/nav-abort";
// PERF: session cache — eliminates the 200–350 ms GET /api/auth/get-session
// cold-load wait by reading a localStorage entry synchronously on mount and
// revalidating in the background. See ~/lib/session-cache.ts for the full
// stale-while-revalidate strategy and multi-tab sign-out via storage events.
import {
  readSessionCache,
  writeSessionCache,
  clearSessionCache,
  onSessionCacheCleared,
} from "~/lib/session-cache";
import { createBackend } from "@proof/client-core/backend";
import { createApiClient } from "@proof/client-core/api";
import { updateRecipe } from "@proof/client-core/api/recipes";
import { createAuthClient, getCsrfHeader } from "@proof/client-core/auth";
import { withCache, invalidate, invalidateAll, settleInvalidate } from "@proof/client-core/cache";
import { createCollectionsClient, slugifyCollectionName } from "@proof/client-core/api/collections";
// family-groups (plan 2026-07-02 §4.5) — the typed GroupsApi wrapper (seam to
// the `group.*` namespace being built in parallel by track G-B).
import { createGroupsApi } from "~/features/groups/api";
import type { Backend } from "@proof/client-core/backend";
import { FeedbackModal } from "~/features/feedback/FeedbackModal";
import type { FeedbackKind } from "~/features/feedback/FeedbackModal";
// Pip governor (Wave 2 keystone) — the singleton PipMomentsProvider owns the
// single moment-Pip render slot beside the sonner Toaster. Placements deeper in
// the tree call usePipMoments().fire(<moment>); they never mount their own
// animating <PipMascot>. See .plan/features/2026-06-10-pip-mascot-placement.md §3.
import { PipMomentsProvider, PipMomentsBridge, type PipFire } from "~/components/pip";
// Wave PERF-2.A — Workspace is lazy-loaded so the `/app` (Library) landing
// no longer ships the full Workspace tree (TipTap, ProseMirror glue,
// DiffView, HistoryList, RecipeRail). Saves ~23 kB gz on the
// Library first-paint critical path. Read on a recipe still pays for the
// Workspace chunk, but only after the user has clicked into a recipe — which
// is the same path that needs the editor anyway.
//
// `currentRecipeUuid === null` is the only state where Workspace is hidden
// (display:none); when null we never render Workspace at all so Vite never
// fetches its chunk for users who only browse the Library.
const Workspace = lazy(() =>
  import("~/features/workspace/Workspace").then((m) => ({ default: m.Workspace })),
);
// Type-only import — erased at build time, so the Workspace chunk stays lazy.
import type { CurrentAttempt } from "~/features/workspace/Workspace";
// Shell-persistence fix (2026-07-16) — the secondary tray destinations (Feed,
// Cooks/ledger, Groups, Custom logic, Syntax help) render INSIDE this shell's
// content column instead of standalone routes that unmounted the wordmark top
// bar + NavTray. Each is the existing route wrapper, reused verbatim (they read
// their params via useParams, which resolves under the `/app` child match, and
// resolve session via useGroupSession). Lazy-imported so their bundles stay off
// the `/app` first-paint critical path (same PERF-2.A pattern as Workspace).
// deriveAppView (routes/app-view.ts) maps the pathname to the surface to render.
import { deriveAppView, isSecondaryAppView } from "~/routes/app-view";
const FeedRoute = lazy(() => import("~/routes/feed"));
const LedgerRoute = lazy(() => import("~/routes/ledger"));
const DiscoverShellRoute = lazy(() => import("~/routes/discover"));
const GroupsIndexRoute = lazy(() => import("~/routes/groups"));
const GroupRoute = lazy(() => import("~/routes/group"));
const GroupShareRoute = lazy(() => import("~/routes/group-share"));
const CustomLogicPage = lazy(() => import("~/routes/custom-logic"));
const HelpPage = lazy(() => import("~/routes/help"));
// TopBar retired — tablet-collapse wave. Library-landing chrome at ≥768 px
// is now DesktopTopbar; no library-landing legacy TopBar is needed.
// Library-landing DesktopTopbar — renders at desktop ≥1280 px so the avatar
// trigger (Settings / Sign out / Profile / Exports) is reachable when no
// recipe is open. The Workspace-internal DesktopTopbar handles the
// recipe-open desktop path and the two are mutually exclusive on
// `currentRecipeUuid` so they never render simultaneously.
// design.md §6.0a — top-level features must be discoverable on every route.
import { DesktopTopbar } from "~/features/shell/DesktopTopbar";
import { NavTray, deriveNavTrayActiveKey } from "~/features/shell/NavTray";
import { deriveMenuLabel } from "~/features/user-menu/UserMenu";
import { SignInGate } from "~/features/auth/SignInGate";
// Onboarding revival (plan 2026-07-02) — the guide brain (T2) is instantiated
// once here so cross-device suppression + funnel logs are wired from beat one;
// the GuideStrip render site lands in T4 inside Workspace and reads the guide
// via useGuideContext(). The empty-library "Open a worked example" arm (T3)
// adopts this seed.
import { GuideProvider } from "~/features/guide/GuideContext";
import {
  adoptWorkedExample,
  workedExampleToastMessage,
  type WorkedExampleRecipeClient,
} from "~/features/library/workedExampleAdoption";
// Wave 2 (onboarding-empty-r1): guided wizard removed — wishlisted per
// `.plan/wishlist/onboarding-wizard.md`. New-user landing is the
// EmptyLibraryState in LibraryView (Frame 21). No wizard imports remain.
import { connectSyncDO, purgeWebYjsCache } from "~/sync/connect";
import type { SyncState } from "~/sync/connect";
// Phase 1.C — the body-snapshot fast-paint namespace sits outside both the RPC
// cache prefix and the proof-yjs DB, so sign-out must purge it explicitly (see
// handleSignOut) or the next user on a shared device sees the previous body.
import { purgeAllSnapshots } from "~/features/editor/BodySnapshot";
import { resetFindingsAggregateCache } from "~/features/findings/api";
import type { Collection, Recipe } from "~/features/explorer/types";
import { resolveOpenVariation } from "~/features/workspace/resolve-variation";
import { parseConsumes } from "@proof/grammar/client";
import { parse as parseGrammar } from "@proof/grammar";
import type { UnresolvedRef } from "~/features/spin-off/UnresolvedArtefactBanner";
// P3.LIBRARY — LibraryView is the default landing surface at /app.
// Mounts when no recipe is open (currentRecipeUuid === null).
// design.md §6, L6 (Library is the mobile landing route), D-LAND.
import { LibraryView } from "~/features/library/LibraryView";
import { DashboardHero } from "~/features/dashboard/DashboardHero";
// Nav-shell R2 — the /app landing loads the friends feed lane (non-blocking,
// after first paint) so DashboardHero's "From friends" strip shows real
// activity. loadFriendStrip reuses loadFeedLane("friends") (the same feed
// client /app/feed uses) behind its own dashboard-local ≤30s freshness cache —
// the /app/feed surface is untouched — and degrades a failure to [] (Rule 8).
import { loadFriendStrip } from "~/features/dashboard/loadFriendStrip";
// The friendless boolean signal for DashboardHero's "From friends" CTA gating.
// Loaded lazily (post-paint) alongside the friends strip; degrades to `undefined`.
import { loadHasFriends } from "~/features/dashboard/loadHasFriends";
import type { FeedItem } from "~/features/feed/api";
// Adaptive lineage breadcrumb (nav-chrome-contextual-create §4.4): the same
// helper the Workspace uses for the RecipeHeader "spun off from" label, reused
// here to build the adopted-shared library-card caption.
import { deriveSpunOffFromLabel } from "~/features/workspace/spunOffFromLabel";
// P0-7 (Wave 2 — identity): the route-level new-recipe modal. Every "New recipe"
// affordance on the Library landing (empty-state CTA, LibraryView title-row ghost
// control) opens this so the user always types a human name — the machine
// "Untitled recipe N" fallback is retired.
import { RecipeCreationModal } from "~/features/explorer/RecipeCreationModal";
import { createPasteConvert } from "~/features/explorer/pasteConvert";
// Change-recipe-emoji modal (library page). Heavyweight emoji picker is itself
// lazy inside the modal, so this static import only pulls the modal chrome.
import { RecipeEmojiModal } from "~/features/library/RecipeEmojiModal";
// Wave 2 Package C — InProgressSection removed (Wave 3 R-2; in_progress status gone).
// Library-landing search palette mount (P1 fix 2026-05-09).
// The Workspace also mounts <Palette>, but only when a recipe is open; without
// this route-level mount the `proof:open-palette` event dispatched by the
// DesktopTopbar Search button on the Library landing has no listener and is
// silently dropped. The two mounts are mutually exclusive on
// `currentRecipeUuid` so they never render simultaneously.
import { Palette } from "~/features/command-palette/Palette";
// Wishlist exception (2026-06-14): modifier-prefixed ⌘K / Ctrl+K opens Search.
// Single window keydown listener; mounted once in WorkspaceContainer so it
// covers BOTH the Library landing and the Workspace. See
// .plan/features/2026-06-14-cmdk-open-search.md.
import { useOpenPaletteShortcut } from "~/features/command-palette/useOpenPaletteShortcut";
import type { PaletteResult } from "@proof/client-core/palette";
// Vite `?worker` import — Vite compiles and bundles the evaluator worker at
// build time. The resulting class is a Worker constructor: `new EvaluatorWorker()`
// produces a ready Web Worker running the compiled JS bundle.
//
// Do NOT use `new URL("...worker-entry", import.meta.url)` — that pattern
// resolves to the raw .ts source at dev time and Vite serves it with HTTP 405.
import EvaluatorWorker from "@proof/grammar/evaluator/worker-entry?worker";

// ── Dev-anonymous gate (T0.1) ──────────────────────────────────────────────────
// `DEV_ANONYMOUS` is a module-level boolean that is statically `false` in every
// production build (Vite replaces `import.meta.env.DEV` with `false` at build
// time so Rollup dead-code eliminates the entire branch).
//
// When true (dev build + `?dev=anonymous` in the URL), `AppRoute` returns the
// `DevAnonymousShell` immediately — no session fetch, no backend initialisation.
// The shell is lazy-imported so it is NOT included in the production bundle.
// design.md §2 — dev tooling must not leak into the user-facing surface.
import { DEV_ANONYMOUS } from "~/features/auth/dev-anonymous-gate";
// DevAnonymousShell is a lazy import behind the DEV guard so Rollup tree-shakes
// it from production bundles. The `import.meta.env.DEV` guard mirrors the
// ShellPrototypePage pattern in entry.client.tsx.
//
// React.lazy is used so Vite emits a separate chunk for the shell code — it
// will never appear in the main bundle in production (DEV_ANONYMOUS is false
// in prod so the conditional below never reaches the lazy mount).
const DevAnonymousShell = import.meta.env.DEV
  ? lazy(() =>
      import("~/features/auth/DevAnonymousShell").then((m) => ({
        default: m.DevAnonymousShell,
      })),
    )
  : null;

// ── Constants ──────────────────────────────────────────────────────────────────

// Module-level constant keeps the reference stable across re-renders
// (required by useResolvedValues' empty-deps guard — extensibility-design §5.4).
const WORKER_URL = EvaluatorWorker;

/**
 * The API base URL.
 * The router Worker fronts the API at "/api". The path-split router strips
 * no prefix — the api Worker mounts at root, so everything under /api/*
 * on the SPA origin maps to /* on the api Worker.
 *
 * For same-origin deployments (SPA + API behind the router Worker) this is
 * always "". For split-origin dev the VITE_API_BASE_URL env var overrides.
 *
 * architecture.md §3 — router Worker path-split.
 */
const API_BASE_URL =
  (typeof import.meta.env !== "undefined" &&
    (import.meta.env as Record<string, string>)["VITE_API_BASE_URL"]) ||
  "";

/**
 * The sync Worker base URL.
 * Usually the same as API_BASE_URL since the router Worker forwards /sync/*
 * to the sync Worker. Override with VITE_SYNC_BASE_URL if needed.
 */
const SYNC_BASE_URL =
  (typeof import.meta.env !== "undefined" &&
    (import.meta.env as Record<string, string>)["VITE_SYNC_BASE_URL"]) ||
  API_BASE_URL;

// ── Session fetch ─────────────────────────────────────────────────────────────

type SessionState =
  | { kind: "loading" }
  | {
      kind: "authenticated";
      userId: string;
      email: string;
      /**
       * BetterAuth user.name — the editable display name. Surfaced to the user
       * menu trigger as a fallback when the public-slug `handle` is not set
       * (PH2 fix 2026-05-03 — UserMenu derives label as @handle → name → email).
       */
      name: string | null;
      /** Unix-ms timestamp when the user completed onboarding; null if not yet done. */
      onboardingCompletedAt: number | null;
      /**
       * Public-profile slug from the `handles` table. Surfaced via the
       * /api/auth/get-session response (extended in apps/api/src/index.ts on
       * 2026-05-02 per followup §2.1 P0). Null when no handle row is present.
       */
      handle: string | null;
      /**
       * Server-derived admin flag from the get-session payload (A9 fix, QA
       * 2026-06-11). Cached so useIsAdmin never needs the admin.ping probe
       * that 403s by design for non-admins.
       */
      isAdmin: boolean;
      /**
       * Server-authoritative plan tier (entitlements v0). Injected into the
       * get-session payload; cached so useTier resolves zero-network. Display
       * only — never the gate (the server enforces caps at the write path).
       */
      tier: "free" | "paid";
      /**
       * Server-authoritative four-plan id (Entitlements four-tier amendment,
       * C-WP0). Injected alongside `tier`; cached so usePlanId resolves
       * zero-network. Display only — never the gate.
       */
      planId: "free" | "plus" | "pro" | "max";
    }
  | { kind: "unauthenticated" }
  | { kind: "network-error"; message: string };

async function fetchSession(): Promise<SessionState> {
  try {
    const res = await fetch("/api/auth/get-session", {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (res.status === 200) {
      const data = (await res.json().catch(() => null)) as {
        session?: { userId?: string } | null;
        user?: {
          id?: string;
          email?: string;
          /** BetterAuth display name (editable from Settings → Handle field). */
          name?: string | null;
          // BetterAuth additionalFields — declared in apps/api/src/auth.ts
          onboardingCompletedAt?: number | null;
          /** Followup §2.1 P0 — public-slug from the `handles` table. */
          handle?: string | null;
          /** A9 fix — server-derived admin flag (apps/api/src/index.ts). */
          isAdmin?: boolean;
          /** Entitlements v0 — server-authoritative plan tier. */
          tier?: "free" | "paid";
          /** C-WP0 — server-authoritative four-plan id. */
          planId?: "free" | "plus" | "pro" | "max";
        } | null;
      } | null;
      // BetterAuth returns { session: { userId, ... }, user: { id, email, ... } }
      const userId = data?.session?.userId ?? data?.user?.id;
      const email = data?.user?.email ?? "";
      if (userId) {
        return {
          kind: "authenticated",
          userId,
          email,
          name: data?.user?.name ?? null,
          onboardingCompletedAt: data?.user?.onboardingCompletedAt ?? null,
          handle: data?.user?.handle ?? null,
          isAdmin: data?.user?.isAdmin === true,
          tier: data?.user?.tier === "paid" ? "paid" : "free",
          planId:
            data?.user?.planId === "plus" ||
            data?.user?.planId === "pro" ||
            data?.user?.planId === "max"
              ? data.user.planId
              : "free",
        };
      }
      return { kind: "unauthenticated" };
    }
    if (res.status === 401 || res.status === 403) {
      return { kind: "unauthenticated" };
    }
    // Unexpected status — server is reachable but returned something unusual.
    // branding.md §5: precise, unsentimental, no exclamations.
    return {
      kind: "network-error",
      message: `The server returned an unexpected response (${res.status}). Reload to retry.`,
    };
  } catch (err) {
    // branding.md §5: name the failure mode accurately.
    const message =
      err instanceof Error && err.message.includes("Failed to fetch")
        ? "No connection. Check your network and try again."
        : "Couldn't reach the server. Reload to retry.";
    return { kind: "network-error", message };
  }
}

// ── WorkspaceContainer — manages backend + sync for the authenticated shell ───

interface WorkspaceContainerProps {
  userId: string;
  email: string;
  /**
   * BetterAuth display name. Threaded to the UserMenu as `accountName`; the
   * trigger label is derived as `@handle → name → email-local-part` (PH2
   * fix 2026-05-03 — locked priority per operator).
   */
  name: string | null;
  onboardingCompletedAt: number | null;
  /** Public-slug for /u/<handle> navigation. Null when no handle row exists. */
  handle: string | null;
  /**
   * Re-fetches the session from /api/auth/get-session and updates the parent's
   * SessionState. Called after mutations that invalidate the cached payload —
   * specifically `handleUpdateHandle` (writes to user.name) so the user-menu
   * trigger reflects the new value within the same React render cycle without
   * waiting for a page reload (PH2 fix 2026-05-03).
   */
  onSessionRefresh: () => void;
  /**
   * Called by WorkspaceContainer after a successful logout so AppRouteImpl can
   * flip the session state to `unauthenticated` and show SignInGate — without
   * navigating away from /app. Navigating to "/" bounces back to /app via the
   * root index redirect, leaving the user stuck (bug: step 8 sign-out).
   */
  onSignedOut: () => void;
}

// ── Slug generation ───────────────────────────────────────────────────────────

/**
 * Derive a URL-safe slug from a title string.
 * Falls back to a timestamp-based slug if the title produces no valid chars.
 * The slug regex on the server is: /^[a-z0-9][a-z0-9-]{0,98}[a-z0-9]$|^[a-z0-9]$/
 */
function titleToSlug(title: string): string {
  const base = title
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 99);
  if (base.length >= 1) return base;
  return `recipe-${Date.now()}`;
}

// ── Content emptiness predicate ───────────────────────────────────────────────

/**
 * Returns true when `body` is absent, empty, or scaffold-only (contains no
 * meaningful content nodes per the grammar parser).
 *
 * Mirrors the `editorHasContent` predicate in Workspace.tsx exactly so both
 * surfaces share the same definition of "empty". A node counts as content when:
 *   - section-block with ≥1 child
 *   - scope-heading with non-empty text
 *   - prose-line with non-whitespace text
 *
 * Used by fetchDraftAttempt to decide whether to fall back to the most recent
 * frozen attempt for the cook view (Defect 1 fix).
 */
function isBodyScaffoldOrEmpty(body: string | null | undefined): boolean {
  if (!body || body.trim().length === 0) return true;
  try {
    const ast = parseGrammar(body).ast;
    const hasContent = ast.children.some((node) => {
      if (node === undefined) return false;
      if (node.kind === "section-block") return node.children.length > 0;
      if (node.kind === "scope-heading") return node.text.trim().length > 0;
      if (node.kind === "prose-line") return node.text.trim().length > 0;
      return false;
    });
    return !hasContent;
  } catch {
    // Parser error → treat as non-empty (safe fallback; the raw text is not
    // blank so a broken AST is better treated as content than lost).
    return false;
  }
}

// ── Library list payload shapes ───────────────────────────────────────────────

/**
 * Wire shape of `recipe.list` as consumed by the library mapping.
 * Bug F1 (2026-05-03) — recipe.list returns variations[] inline, metadata-only
 * ({ uuid, slug, name, isGoTo }). Body content lives on attempts and is loaded
 * lazily by the editor.
 *
 * Shared by the mount fetch AND the withCache revalidation hook so a
 * stale-served list is re-applied identically once fresh data lands.
 */
interface RecipeListPayload {
  recipes?: Array<{
    uuid: string;
    slug: string;
    title: string;
    created_at: number;
    /** Stored display glyph; NULL when unset → client falls back to 🍳. */
    emoji?: string | null;
    fork_upstream_recipe_uuid?: string | null;
    /** A-WP3 — feature-photo media UUID; null/absent → emoji/gradient fallback. */
    feature_media_uuid?: string | null;
    /**
     * Group-share adoption provenance — present ONLY when the recipe was adopted
     * from a group share (folded from `source_handle` / `source_group_name` by
     * the DO's `toRecipeWire`). Absent for created / self-duplicated recipes.
     * Drives the "Spun off from @handle · Group" library-card breadcrumb.
     */
    spunOffFrom?: { handle: string | null; groupName: string | null };
    variations?: Array<{
      uuid: string;
      slug: string;
      name: string;
      isGoTo: boolean;
    }>;
    /**
     * Per-recipe library-surface stats (library-surface redesign R2). Computed
     * in-DO over the returned page. Absent on rows served from an old cache
     * snapshot that predates the stats deploy — the mapper leaves `Recipe.stats`
     * undefined in that case (never fabricates zeros). The wire carries
     * `lastCookedAt` (epoch ms, from cook_ts); the mapper renames it to
     * `lastCookedAtMs` on the client `Recipe.stats`.
     */
    stats?: {
      cooks: number;
      goToVersion: number | null;
      rating: number | null;
      lastCookedAt: number | null;
    };
  }>;
}

/** Wire shape of `collection.list` as consumed by the library mapping. */
interface CollectionListPayload {
  collections?: Array<{ uuid: string; slug: string; name: string }>;
}

/**
 * Wire shape of `collection.listAllMembers` (perf plan §2.C — batched). Each
 * row pairs a collection with one of its member recipes; the library derives a
 * `recipe_uuid → collection_uuid[]` map from the flat list client-side.
 */
interface CollectionMembershipPayload {
  members?: Array<{ collection_uuid: string; recipe_uuid: string }>;
}

/**
 * Stale-empty guard (operator-observed library wipe). An empty `recipe.list`
 * payload is only authoritative when it comes off the wire: a cache-sourced
 * (fresh/stale hit) empty payload may be a stale first-run snapshot that never
 * got invalidated, and applying it would wipe a populated library. A
 * network-sourced empty still applies so genuinely-empty / first-run accounts
 * render the empty state; a cache-sourced empty against an already-empty state
 * also applies (no-op). Exported for unit tests.
 *
 * @param payloadRecipeCount recipes in the incoming payload.
 * @param source where the payload came from (see withCache `onServe`).
 * @param currentRecipeCount recipes currently in state.
 * @returns true when the payload must be IGNORED (a stale cache-sourced empty
 *   against a populated library).
 */
export function isStaleEmptyRecipeListPayload(
  payloadRecipeCount: number,
  source: "cache" | "network",
  currentRecipeCount: number,
): boolean {
  return payloadRecipeCount === 0 && source === "cache" && currentRecipeCount > 0;
}

/**
 * Order-independent equality for two collection-membership UUID lists. Used by
 * the membership restamp (`applyMemberships`) to preserve a recipe object's
 * identity when its membership set is unchanged, so a periodic
 * `collection.listAllMembers` revalidation restamp does not churn every card.
 */
export function sameMembership(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  if (a.length === 0) return true;
  const bSet = new Set(b);
  for (const x of a) if (!bSet.has(x)) return false;
  return true;
}

/**
 * Pure payload → `Recipe[]` base mapping for `applyRecipeListPayload` (extracted
 * so the stats mapping is unit-testable without rendering the workspace). The
 * per-variation cook count / rating stay baseline placeholders (the list
 * endpoint does not compute them — P0-5 honesty) and the carry-over merge with
 * prior state runs at the call site.
 *
 * Library-surface stats (R2): the wire row's `stats` maps straight onto
 * `Recipe.stats`, renaming the epoch-ms cook timestamp `lastCookedAt` →
 * `lastCookedAtMs`. When the wire row carries NO `stats` (a stale pre-deploy
 * cache snapshot), the `stats` key is OMITTED so `Recipe.stats` stays
 * `undefined` = "unknown" — zeros are never fabricated.
 *
 * Exported for unit tests.
 */
/**
 * Adaptive lineage breadcrumb for the library card (nav-chrome-contextual-create
 * §4.4). Two provenance shapes, discriminated by `spunOffFrom` (the DO's fold of
 * the internal `source_handle` / `source_group_name` columns):
 *   - Adopted from a group share (`spunOffFrom` present) → "Spun off from @handle
 *     · Group" (branding.md §8 "Spun off"; reuses the Workspace's
 *     `deriveSpunOffFromLabel`, capitalised for the standalone card caption).
 *   - Self-duplicate within the library (`fork_upstream_recipe_uuid` set, no
 *     `spunOffFrom`) → "Based on <upstream name>", resolved from the same page.
 *     An upstream not present on this page yields NO breadcrumb (never a bare
 *     UUID) — mirrors the workspace LineageBanner's orphan behaviour.
 *   - Neither → undefined (original recipe, no breadcrumb).
 */
function deriveLibraryCardLineage(
  row: {
    fork_upstream_recipe_uuid?: string | null;
    spunOffFrom?: { handle: string | null; groupName: string | null };
  },
  nameByUuid: Map<string, string>,
): string | undefined {
  if (row.spunOffFrom) {
    const label = deriveSpunOffFromLabel(row.spunOffFrom);
    if (label === undefined) return undefined;
    // deriveSpunOffFromLabel returns lower-case "spun off from …" for the inline
    // RecipeHeader line; the card caption stands alone, so capitalise the lead.
    return label.charAt(0).toUpperCase() + label.slice(1);
  }
  if (row.fork_upstream_recipe_uuid) {
    const upstreamName = nameByUuid.get(row.fork_upstream_recipe_uuid);
    if (upstreamName === undefined || upstreamName.length === 0) return undefined;
    return `Based on ${upstreamName}`;
  }
  return undefined;
}

export function mapRecipeListPayloadToRecipes(
  rows: NonNullable<RecipeListPayload["recipes"]>,
): Recipe[] {
  // uuid → display-name map, built first so a self-duplicate can resolve its
  // in-library upstream's name for the "Based on <name>" breadcrumb (§4.4).
  const nameByUuid = new Map<string, string>();
  for (const r of rows) {
    nameByUuid.set(r.uuid, r.title || r.slug);
  }
  return rows.map((r) => ({
    uuid: r.uuid,
    slug: r.slug,
    title: r.title,
    // Stored display glyph → client Recipe.emoji. NULL/absent on the row means
    // the user never set one; omit the key (exactOptionalPropertyTypes) so
    // RecipeCard renders the 🍳 fallback.
    ...(r.emoji ? { emoji: r.emoji } : {}),
    // A-WP3 — feature-photo pointer. Omit when absent (exactOptionalPropertyTypes)
    // so RecipeCard falls back to emoji/gradient byte-identically.
    ...(r.feature_media_uuid ? { featureMediaUuid: r.feature_media_uuid } : {}),
    // Enriched from previous state at the call site, then by the membership
    // fetch on mount (Bug 10 fix).
    collectionUuids: [],
    // Variations come straight from the API. The list endpoint does not compute
    // cookCount + rating (those join through attempts/ratings and would defeat
    // the O(1) query goal); the variations view fetches the cook count on demand
    // when a recipe's reveal opens (P0-5). `cookCountLoaded: false` marks the
    // count as a baseline placeholder so the reveal renders an honest "… cooks"
    // loading state instead of a fabricated 0 until the on-demand fetch resolves.
    variations: (r.variations ?? []).map((v) => ({
      uuid: v.uuid,
      name: v.name,
      cookCount: 0,
      cookCountLoaded: false,
      rating: null,
      isGoTo: v.isGoTo,
    })),
    lastEditedAt: r.created_at ?? Date.now(),
    fork_upstream_recipe_uuid: r.fork_upstream_recipe_uuid ?? null,
    // Group-share adoption provenance → Workspace RecipeHeader "spun off from"
    // line (via deriveSpunOffFromLabel). Omit when absent (exactOptional).
    ...(r.spunOffFrom ? { spunOffFrom: r.spunOffFrom } : {}),
    // Adaptive library-card breadcrumb (§4.4). Omit when there is no lineage so
    // RecipeCard renders no sub-caption for original recipes.
    ...(() => {
      const lineage = deriveLibraryCardLineage(r, nameByUuid);
      return lineage !== undefined ? { lineage } : {};
    })(),
    ...(r.stats
      ? {
          stats: {
            cooks: r.stats.cooks,
            goToVersion: r.stats.goToVersion,
            rating: r.stats.rating,
            lastCookedAtMs: r.stats.lastCookedAt,
          },
        }
      : {}),
  }));
}

function WorkspaceContainer({
  userId,
  email,
  name,
  // onboardingCompletedAt: threaded into the guide brain (plan 2026-07-02 T2).
  // The guide is armed only while this is null; completion/dismiss stamps it
  // cross-device via account.completeOnboarding (see completeOnboardingStamp).
  onboardingCompletedAt,
  handle,
  onSessionRefresh,
  onSignedOut,
}: WorkspaceContainerProps) {
  const navigate = useNavigate();

  // ── Global ⌘K / Ctrl+K → open Search ─────────────────────────────────────────
  // One window keydown listener, mounted here (the authenticated component that
  // renders BOTH the Library landing and the Workspace) so the shortcut works
  // everywhere in /app. Dispatches `proof:open-palette`, which the mounted
  // <Palette> already listens for. Mounting here rather than in the
  // unauthenticated AppRouteImpl shell means we never preventDefault ⌘K on the
  // sign-in gate / loader where no palette listener exists.
  useOpenPaletteShortcut();

  // ── URL-based recipe restore (D-NAV) ─────────────────────────────────────────
  // Reads :slug from /app/r/:slug* routes so a hard reload or direct link
  // to a recipe URL opens the correct recipe once the library data resolves.
  // The :slug param holds the recipe uuid in the spin-off flow and may also
  // hold a human slug on newly-created recipes (both shapes are supported by
  // the lookup below).
  const routeParams = useParams<{ slug?: string }>();
  const recipeSlugFromRoute = routeParams.slug ?? null;
  // Router location — read by the URL-restore effect so canonicalisation can
  // preserve the mode sub-segment (A10 fix, QA 2026-06-11).
  const location = useLocation();

  // ── Dashboard / Library split (2026-07-11) ───────────────────────────────────
  // `/app` (no recipe) renders the DASHBOARD (DashboardHero + the condensed
  // "Your library" preview). The FULL library grid lives on its own surface at
  // `/app/library`. This route-driven boolean mirrors `recipeSlugFromRoute`: it is
  // pure-derived from the pathname on every render, so deep-link, refresh, and the
  // back button all resolve without any imperative state to hydrate.
  //
  // CORRECTED (route unification, 2026-07-24): an earlier note here claimed that
  // `/app/r/:slug → /app/library` crossed top-level route objects and REMOUNTED
  // AppRoute, and that the remount — not the close-reconciliation effect — was
  // what cleared the open recipe. Measured with a mount-count probe: AppRoute
  // mounted exactly ONCE across `library → A → library → B` on that route table
  // too (React reuses two same-type elements rendered at the same depth), so the
  // close-reconciliation effect below has always been the only writer that
  // clears the open recipe, for every destination. `/app`, `/app/library` and
  // `/app/r/:slug` are now children of one route object, which makes the single
  // instance structural rather than incidental (routes/app-routes.ts).
  const libraryRouteActive =
    location.pathname === "/app/library" || location.pathname.startsWith("/app/library/");

  // ── Secondary tray destinations in-shell (shell-persistence fix, 2026-07-16) ──
  // deriveAppView maps the pathname to the surface to render in the content
  // column. Feed / Cooks (ledger) / Groups / group detail / group share /
  // Custom logic / Syntax help are the "secondary" destinations that used to be
  // standalone routes (unmounting the wordmark top bar + NavTray). They now
  // render inside this shell so the header + menu stay static — one move to any
  // destination (the operator "two moves" bug). `secondaryActive` also gates the
  // dashboard/library shell OFF so the two never render at once.
  const appView = deriveAppView(location.pathname);
  const isSecondaryView = isSecondaryAppView(appView);

  // ── Chrome viewport gating removed (nav-chrome-contextual-create) ────────────
  // The unified DesktopTopbar now renders on EVERY breakpoint (its own CSS hides
  // the Groups control + title <768 px), so the route no longer tracks an
  // `isMobileViewport` flag to swap between a desktop bar and a mobile capsule.
  // The MobileCapsule is gone; there is one chrome surface at all widths.
  // Plan ref: .plan/features/2026-07-04-nav-chrome-contextual-create.md

  // #221 Track D (d1): capsuleAccountRef removed. The Account trigger lives in the
  // route-level DesktopTopbar avatar, which toggles the lifted `userMenuOpen`
  // state directly (the Workspace-internal DesktopTopbar avatar toggles the same
  // setter via `onUserMenuOpenChange`) — a single source of truth, not an
  // imperative escape-hatch ref.

  /**
   * P0 sign-out fix (2026-05-10) — STRUCTURAL CONSOLIDATION (third attempt).
   *
   * The desktop UserMenu dropdown open-state is lifted here so that BOTH
   * DesktopTopbar instances dispatch their avatar click to the SAME setter:
   *   • Route-level Library landing DesktopTopbar (currentRecipeUuid === null)
   *   • Workspace-internal DesktopTopbar (recipe-open path)
   *
   * Three patches in 24 h on the same surface (PR #165 regression → PR #170 →
   * PR #173) all fixed ONE of the two DesktopTopbar paths and missed the other.
   * This lift eliminates the "two avatars, one fix" footgun for good — the
   * single <UserMenu> mount lives below at route level (above the workspace-shell
   * wrapper) and is the only avatar-click target across the whole app.
   *
   * Workspace's WorkspaceProps accepts `userMenuOpen` + `onUserMenuOpenChange`
   * (controlled-mode contract) and forwards its DesktopTopbar avatar click to
   * this setter when the props are provided. When undefined (the existing
   * Workspace.test.tsx fixture path), Workspace falls back to its internal
   * state — preserves backwards compatibility.
   */
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  /**
   * P0 sign-out fix (2026-05-10) — imperative-escape-hatch refs for the
   * route-level UserMenu's action rows that need to reach Workspace-internal
   * state (Settings panel mount, Exports panel, Feedback modal).
   *
   * Same pattern as `capsuleAccountRef` above. Workspace populates these in a
   * useLayoutEffect, so by the time the user can click the UserMenu's action
   * rows the refs are guaranteed populated (Workspace mounts unconditionally
   * inside the workspace-shell wrapper below — even when display:none on the
   * Library landing, the layout effect still fires).
   */
  const settingsOpenerRef = useRef<(() => void) | null>(null);
  const exportsOpenerRef = useRef<(() => void) | null>(null);
  const feedbackOpenerRef = useRef<(() => void) | null>(null);

  // P3 (Pip — list of moments §4) — imperative handle on the governor's `fire`.
  // handleCreateRecipe is declared on this component, which RENDERS the
  // <PipMomentsProvider> in its JSX but sits OUTSIDE the provider subtree, so it
  // cannot call usePipMoments() directly. <PipMomentsBridge> (mounted inside the
  // provider below) populates this ref; the create handler calls
  // pipFireRef.current?.("first-recipe") to play `congratulate` once-ever. The
  // governor owns the once-ever ledger + the collision rule — this only re-exposes
  // its already-built `fire`. Same idiom as exportsOpenerRef above.
  const pipFireRef = useRef<PipFire | null>(null);

  /**
   * Bug #221 fix (2026-05-23) — Library-landing visibility override for
   * Workspace-mounted account-menu surfaces (Settings, Feedback, Exports).
   *
   * Diagnosis: the route-level UserMenu (mounted in this file) delegates
   * Settings / Feedback / Exports through `*OpenerRef` refs populated by
   * Workspace's useLayoutEffect. Those refs DO fire correctly on the Library
   * landing — but the surfaces they open (FeedbackModal, Exports panel,
   * SettingsView) all live INSIDE the Workspace tree, and the
   * workspace-shell wrapper below is `display: none` whenever
   * `currentRecipeUuid === null`. The modal renders into a hidden ancestor
   * subtree and the user sees nothing happen.
   *
   * Fix: when the user opens one of these surfaces from the Library landing
   * route-level UserMenu, flip the override so the workspace-shell wrapper
   * renders with `display: contents` even though no recipe is selected.
   * The override clears on Library navigation or when the surface dismisses.
   *
   * Class of bug: a primary-nav surface firing into a mounted-but-ancestor-
   * hidden component, producing the silent-fail mode the operator described
   * in bug #221 ("nothing happens when I am on the library page but then if
   * I click on a recipe it does show up").
   */
  const [workspaceVisibleOverride, setWorkspaceVisibleOverride] = useState(false);

  // #221 Track D (d2) — route-level Feedback modal, single-sourced at the shell
  // so "Send feedback" in the Account menu works on EVERY route (the Library
  // list route included). The prior wiring delegated through `feedbackOpenerRef`,
  // populated only by the lazy Workspace's layout effect, so it silently no-op'd
  // before the Workspace chunk resolved. The route-level UserMenu opens this
  // state directly — no ref indirection, no Workspace mount-timing dependency.
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);

  // #221 Track D (d3) — open Settings from the route-level Account menu.
  // Settings renders inside the lazy Workspace (no react-router route exists to
  // move it to). `settingsOpenerRef` is populated by Workspace's useLayoutEffect;
  // on a cold Library landing it can be null at click time, so retry until it
  // registers. The visibility override is set synchronously so Settings is not
  // rendering into a display:none ancestor.
  const [pendingSettingsOpen, setPendingSettingsOpen] = useState(false);
  useEffect(() => {
    if (!pendingSettingsOpen) return;
    if (settingsOpenerRef.current) {
      settingsOpenerRef.current();
      setPendingSettingsOpen(false);
      return;
    }
    const id = setInterval(() => {
      if (settingsOpenerRef.current) {
        settingsOpenerRef.current();
        setPendingSettingsOpen(false);
        clearInterval(id);
      }
    }, 16);
    return () => clearInterval(id);
  }, [pendingSettingsOpen]);
  const openSettingsFromAccountMenu = useCallback(() => {
    setWorkspaceVisibleOverride(true);
    if (settingsOpenerRef.current) {
      settingsOpenerRef.current();
    } else {
      setPendingSettingsOpen(true);
    }
  }, []);

  // NavTray "Friends" row (nav-shell R1) — the friends surface is the Settings →
  // Friends section (FriendsSection). There is no standalone /app/friends route
  // this increment (brief: do NOT invent one), so the row opens Settings via the
  // same opener as the Settings row, then best-effort scrolls the Friends
  // section into view once Settings has mounted. The scroll is a progressive
  // enhancement — if Settings is slow to mount the row still lands the user on
  // Settings with Friends below (design.md §6.0a: the entry delivers its
  // destination).
  // Open Settings and best-effort scroll a Friends SUBSECTION into view. `target`
  // is a data-testid inside FriendsSection (e.g. "friends-invite", "friends-search"),
  // falling back to the whole "section-friends" block if the precise subsection
  // has not mounted within the window (FriendsSection renders its subsections only
  // after its own data load). Progressive enhancement — if nothing mounts, the
  // user still lands on Settings with Friends reachable (design.md §6.0a).
  const openFriendsSection = useCallback(
    (target: string) => {
      openSettingsFromAccountMenu();
      let attempts = 0;
      const id = setInterval(() => {
        attempts += 1;
        const el = document.querySelector(`[data-testid="${target}"]`);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
          clearInterval(id);
          return;
        }
        if (attempts > 60) {
          const fallback = document.querySelector('[data-testid="section-friends"]');
          if (fallback) fallback.scrollIntoView({ behavior: "smooth", block: "start" });
          clearInterval(id);
        }
      }, 32);
    },
    [openSettingsFromAccountMenu],
  );
  const openFriendsFromNav = useCallback(
    () => openFriendsSection("section-friends"),
    [openFriendsSection],
  );
  // Dashboard friendless-CTA deep-links: Invite → the invite subsection, Find
  // friends → the handle-search subsection (FriendsSection.tsx).
  const openFriendsInvite = useCallback(
    () => openFriendsSection("friends-invite"),
    [openFriendsSection],
  );
  const openFriendsFind = useCallback(
    () => openFriendsSection("friends-search"),
    [openFriendsSection],
  );

  // ── Recipes + collections state ──────────────────────────────────────────────
  // Recipes and collections are managed here and passed to Workspace.
  // `currentRecipeUuid` is set when a recipe is opened or created.
  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [collections, setCollections] = useState<Collection[]>([]);
  const [currentRecipeUuid, setCurrentRecipeUuid] = useState<string | null>(null);

  // ── Friends strip (nav-shell R2) ─────────────────────────────────────────────
  // Recent friend activity for DashboardHero's "From friends" strip on the
  // no-recipe landing. Loaded lazily by the effect below (after first paint,
  // never gating the route) and kept across a recipe open→close so returning to
  // the landing shows the last-known strip instead of a doorway flash. Empty
  // until the first load resolves → the hero shows its calm doorway meanwhile.
  const [friendStripItems, setFriendStripItems] = useState<FeedItem[]>([]);

  // Whether the signed-in user has any ACTIVE friend — a boolean-only signal that
  // gates DashboardHero's "From friends" THIRD state (the friendless CTA vs the
  // passive doorway). `undefined` until the first load resolves → the hero shows
  // its passive doorway meanwhile (never flashes the CTA at an active user).
  // Loaded the SAME lazy/post-paint way as the friends strip (effect below).
  const [hasFriends, setHasFriends] = useState<boolean | undefined>(undefined);

  // Load the friends lane ONLY on the no-recipe landing, and only from an effect
  // (post-paint) so it never delays the dashboard or the library grid's first
  // paint — the route renders immediately and the strip fills in when the data
  // lands. Skipped while a recipe is open (currentRecipeUuid !== null). Reuses
  // loadFeedLane("friends") via loadFriendStrip, which keeps its OWN
  // dashboard-local ≤30s cache so a recipe open→close→landing round-trip does
  // not re-fetch (the /app/feed surface is deliberately left untouched).
  // loadFriendStrip owns the Rule-8 degrade: a failure logs and resolves to []
  // (→ doorway), so this effect never has an unhandled rejection and the strip
  // never blanks.
  useEffect(() => {
    if (currentRecipeUuid !== null) return;
    let cancelled = false;
    void loadFriendStrip().then((items) => {
      if (!cancelled) setFriendStripItems(items);
    });
    return () => {
      cancelled = true;
    };
  }, [currentRecipeUuid]);

  // Load the friendless signal ONLY on the no-recipe landing, post-paint (an
  // effect, never gating the route), mirroring the friends-strip effect above.
  // loadHasFriends owns the Rule-8 degrade: a failed read logs and resolves to
  // `undefined` (→ passive doorway), so this effect never has an unhandled
  // rejection and an active user never sees the CTA flash on a transient failure.
  useEffect(() => {
    if (currentRecipeUuid !== null) return;
    let cancelled = false;
    void loadHasFriends().then((value) => {
      if (!cancelled) setHasFriends(value);
    });
    return () => {
      cancelled = true;
    };
  }, [currentRecipeUuid]);

  // ── Miss-proof title cache (WRONG-TITLE-ON-SWITCH, 2026-06-15) ──────────────
  // The recipe-header title prop is resolved from `recipes.find(uuid ===
  // currentRecipeUuid)?.title`. `recipes` is a mutable array that a background
  // `recipe.list` SWR revalidation REPLACES wholesale (applyRecipeListPayload →
  // setRecipes(mapped)). On a freshly-created recipe an eventually-consistent /
  // edge-cached list snapshot can momentarily EXCLUDE the just-activated recipe,
  // so the lookup misses and the header falls back to the "this recipe"
  // placeholder while `currentRecipeUuid` is correctly pointed at that recipe.
  //
  // This cache makes the title resolution miss-proof: every activation (and
  // create) records the recipe's title keyed by its RECIPE uuid. The cache is
  // append/update-only and is never wiped by a list revalidation, so once a
  // recipe has been seen its title survives a transient list-array drop. The
  // header reads `recipes.find(...)?.title ?? titleCache.get(uuid)` so a fresh
  // list value always wins, and the cache only fills the transient gap.
  const recipeTitleCacheRef = useRef<Map<string, string>>(new Map());
  const cacheRecipeTitle = useCallback((uuid: string, title: string | undefined) => {
    if (title) recipeTitleCacheRef.current.set(uuid, title);
  }, []);

  // Pending-emoji overlay (Bug #1 — library emoji clobbered by list refetch).
  // handleChangeRecipeEmoji updates the card optimistically, then persists via
  // the raw recipe client. A background recipe.list SWR revalidation re-applies
  // applyRecipeListPayload, which rebuilds each Recipe from the server row — an
  // eventually-consistent / edge-cached snapshot can still carry the OLD emoji,
  // clobbering the optimistic glyph before the server echoes it back. This ref
  // holds the optimistic value per recipe uuid (string = set, null = cleared);
  // applyRecipeListPayload re-applies it ON TOP of every server merge, and the
  // entry self-clears once the server payload echoes the same value. Mirrors the
  // recipeTitleCacheRef miss-proofing pattern above.
  const pendingEmojiRef = useRef<Map<string, string | null>>(new Map());

  // P0-7 (Wave 2 — identity): drives the route-level RecipeCreationModal in
  // `new` mode. Every bare "+ New recipe" affordance on the Library landing
  // opens this instead of minting an "Untitled recipe N" name with no prompt.
  const [newRecipeModalOpen, setNewRecipeModalOpen] = useState(false);

  // Change-recipe-emoji modal (library page) — holds the UUID of the recipe
  // whose emoji is being changed, or null when closed.
  const [emojiModalRecipeUuid, setEmojiModalRecipeUuid] = useState<string | null>(null);

  // Wave 3 R-2: in-progress rows removed (in_progress status gone from 4-value union).

  /**
   * Live Y.Doc for the current sync connection, TAGGED with the docKey it was
   * connected for. Set (atomically, in one setState) whenever connectSyncDO opens
   * a new connection. Passed to TipTapEditor via Workspace to activate the CRDT
   * binding — but ONLY when its docKey matches the active variation's expected
   * key (see Workspace's doc-identity gate).
   *
   * DOC-IDENTITY (perf recipe-switch, Mechanism 2): the doc and its docKey MUST
   * move together. On an in-app switch A→B the parent sets `currentDocKey` to
   * `draft:B` synchronously, but the `[currentDocKey]` effect that swaps the doc
   * runs AFTER paint. If the editor (already re-keyed to B) received the still-A
   * doc from a bare `Doc | null` state, TipTap would seed ProseMirror from A's
   * blocks under B's identity for a frame (the #335 wrong-body class at the ydoc
   * layer). Storing `{ docKey, doc }` as a single atomic value means there is no
   * render where a NEW docKey is observable alongside an OLD doc via this state,
   * and the Workspace gate withholds the doc whenever the pair is stale.
   *
   * Architecture ref: arch review §5 Fix #1.3 — thread ydoc to TipTap.
   */
  const [currentYDocEntry, setCurrentYDocEntry] = useState<{
    docKey: string;
    doc: import("yjs").Doc;
  } | null>(null);
  // Phase 1.C — true once the live doc's WS sync handshake completes
  // (provider.synced). Threaded to Workspace as `bodyHydrated`; the fast-paint
  // BodySnapshot overlay uses it to dissolve on a synced-but-empty body.
  const [bodyHydrated, setBodyHydrated] = useState(false);

  /**
   * Text content derived from TipTapEditor's onContentChange callback, TAGGED
   * with the variation it belongs to. Acts as the single source of truth for
   * downstream consumers (ValuesPanel, ReadView, tab-title derivation,
   * evaluator).
   *
   * COOK-FIRST-PAINT race fix (2026-06-16): the body was a bare untagged string
   * written by three async writers (docKey clear, draft seed, frozen-fallback
   * seed) plus the live onContentChange stream, and consumed by an unkeyed Cook
   * ReadView. Because identity (currentVariationUuid) and body live in separate
   * useState updated on different ticks, a rapid A→B switch could pair recipe
   * A's body with recipe B's identity for one frame. Tagging the body with its
   * variation + gating the consumer on identity (recipeContentForActive below)
   * makes that pairing structurally impossible — no setTimeout, no race window.
   *
   * Architecture ref: arch review §5 Fix #1.4 — lift recipeContent to state;
   * replaces the hard-coded recipeContent="" prop.
   */
  const [derivedContent, setDerivedContent] = useState<{
    variationUuid: string | null;
    text: string;
  }>({ variationUuid: null, text: "" });
  // NOTE: every BODY consumer routes through `recipeContentForActive` (the
  // identity-gated projection of derivedContent.text) — see below. There is no
  // ungated `derivedRecipeContent` read left, so the stale-body window is closed
  // by construction, not by convention.

  /**
   * P0-2 follow-up — loading vs empty distinction for the cook shell.
   *
   * "loading": the attempt.list fetch for the current variation has not yet
   *   resolved. Cleared to "loading" whenever currentVariationUuid changes
   *   (same timing as the effect that clears currentAttempt).
   *
   * "ready": the attempt.list fetch settled (resolve or error). A settled
   *   fetch with empty content is "empty"; a loading fetch is NEVER "empty".
   *
   * Rule: never show the "empty" message during the initial fetch.
   * Only the cook surface consumes this — Workspace passes it through to
   * ReadView as `bodyLoadState`.
   */
  const [bodyLoadState, setBodyLoadState] = useState<"loading" | "ready">("ready");

  /**
   * Cold-load empty-state flash fix — loading vs empty distinction for the
   * Library landing.
   *
   * `recipes` initialises to [] and the mount `recipe.list` fetch resolves later.
   * LibraryView derives `isEmpty` from `recipes.length === 0`, so before the
   * fetch settles a POPULATED account paints the empty-state (its Pip + "Your
   * library is empty." copy) for the fetch latency on every cold load.
   *
   * "loading": the initial recipe.list fetch has not settled yet — render
   *   neither the empty-state nor the grid (a sub-second blank beats a false
   *   "empty"). Mirrors the bodyLoadState pattern above.
   * "ready": the initial recipe.list fetch settled (success OR failure — a
   *   rejected / offline fetch must settle too so a failed load never strands a
   *   permanent skeleton). A settled fetch with zero recipes is genuinely empty
   *   and gets the first-run onboarding callout; a settled fetch with N recipes
   *   renders the grid.
   *
   * Only the Library landing consumes this — threaded to LibraryView as
   * `recipesLoadState`.
   */
  const [recipesLoadState, setRecipesLoadState] = useState<"loading" | "ready">("loading");

  /**
   * #375 — settled-failed vs settled-empty. When the initial recipe.list read
   * REJECTS and no cached snapshot could be served (recipes stays []), the
   * settled state would otherwise render the "Your library is empty." callout to
   * a user whose library simply failed to load. `recipesLoadError` distinguishes
   * the two so the library shell can surface a quiet retry affordance (Rule 8:
   * a failed read must be visible, never a silent false-empty) instead of the
   * empty state. Reset to false whenever a read succeeds (a served snapshot,
   * cache or wire, populates via onServe).
   *
   * NOTE (team lead): a first-class failed branch belongs in LibraryView
   * (`recipesLoadState: "failed"`), but LibraryView.tsx is owned by a parallel
   * agent this wave — the failed surface is therefore rendered app-side in the
   * library shell. See handoff.
   */
  const [recipesLoadError, setRecipesLoadError] = useState(false);

  /**
   * The current Draft attempt on the open variation — uuid AND lifecycle
   * status as one value (Workspace's required `currentAttempt` prop).
   *
   * The uuid gates RecipeHeader's trailingAction slot (SaveAsVersionButton +
   * SaveStateDot; BUG-01). The status gates every soft-lock surface:
   * rate-affordance chips, SectionRatingSheet, VersionActions, EditLink.
   * QA 2026-06-11 bug A7: status used to be a separate optional Workspace
   * prop that this route never passed (PR #181 deleted it claiming sourcing
   * that was never implemented), so those surfaces never rendered in
   * production. uuid and status now travel together — the fetch below
   * already knows both.
   *
   * Lifecycle:
   *   - Set to null when no recipe is open or when the variation changes (cleared in
   *     the currentVariationUuid effect below, then populated by the attempt.list fetch).
   *   - Populated immediately after `recipe.create` by an inline `attempt.list` call
   *     on the new variation so the save-as-version button renders without delay.
   *   - Repopulated whenever `currentVariationUuid` changes (switching variations via
   *     the explorer, loading an existing recipe).
   */
  const [currentAttempt, setCurrentAttempt] = useState<CurrentAttempt | null>(null);

  // createBackend is synchronous (no await in the body) — initialise on first
  // render via the lazy-initializer form so `backend` is never null.
  const [backend] = useState<Backend>(
    () => createBackend({ mode: "remote", apiBaseUrl: API_BASE_URL }).current,
  );
  const [syncState, setSyncState] = useState<SyncState>("syncing");
  const [attemptInFlight, setAttemptInFlight] = useState(false);

  // #221 Track D — the route-level toast (state, timer ref, cleanup effect, and
  // its rendered surface) is removed: after the account-menu rewiring nothing
  // drove it (its only writer, `showRouteToast`, had no remaining call sites).
  // Keeping unreachable UI is the fake-wiring anti-pattern this wave is removing.
  // Re-introduce a route-level toast together with a real caller when one is
  // needed (Rule 8 — surface mutation failures with user-visible feedback).

  // ── Library payload appliers ─────────────────────────────────────────────
  // Map a raw `recipe.list` / `collection.list` payload onto library state.
  // Shared by the mount fetch below AND the withCache revalidation hook on
  // `cachedApi`: when the SWR cache serves a stale list on mount, the
  // background refresh re-applies the fresh payload here so the stale render
  // is corrected. Enriched fields the list endpoint does not provide
  // (collection memberships, on-demand cook counts) are carried over from the
  // previous state by uuid rather than reset.
  //
  // `source` records where the payload came from (see withCache `onServe`):
  // "network" is a live wire read (cold miss OR a background revalidation);
  // "cache" is a fresh/stale hit replayed from IndexedDB. An EMPTY list is only
  // authoritative from the wire — a cache-sourced empty payload may be a stale
  // first-run snapshot that never got invalidated, so applying it would wipe a
  // populated library (operator-observed). A network-sourced empty list still
  // applies so genuinely-empty / first-run accounts render the empty state.
  const applyRecipeListPayload = useCallback(
    (data: RecipeListPayload | undefined, source: "cache" | "network"): void => {
      // Defensive: an undefined / malformed payload is a failed read, not an
      // empty library — leave the current state untouched rather than wiping it.
      if (!data?.recipes) return;
      // Keep the miss-proof title cache populated from every list payload so a
      // later array-replacement revalidation that transiently drops a recipe
      // cannot strand the header on the "this recipe" placeholder.
      for (const r of data.recipes) {
        if (r.title) recipeTitleCacheRef.current.set(r.uuid, r.title);
      }
      const mapped: Recipe[] = mapRecipeListPayloadToRecipes(data.recipes);
      setRecipes((prev) => {
        // Stale-empty guard (operator-observed wipe): a cache-sourced empty
        // recipe.list must never overwrite a populated library — the payload may
        // be a stale first-run snapshot that never got invalidated. The
        // background revalidation (source "network") delivers the authoritative
        // list moments later. A network-sourced empty still applies (account
        // genuinely empty / emptied elsewhere), and a first-run empty falls
        // through harmlessly because `prev` is already [].
        if (isStaleEmptyRecipeListPayload(mapped.length, source, prev.length)) {
          console.warn("[proof.library] stale cache-sourced empty recipe.list ignored", {
            event: "library_stale_empty_ignored",
            prevCount: prev.length,
          });
          return prev;
        }
        const prevByUuid = new Map(prev.map((p) => [p.uuid, p]));
        const pendingEmoji = pendingEmojiRef.current;
        // Re-apply any optimistic emoji on top of the server merge (Bug #1).
        // `null` overlay = "cleared" (emoji key absent). When the server payload
        // already echoes the optimistic value, the overlay has done its job and
        // self-clears so a later genuine server change is never overridden.
        const applyEmojiOverlay = (recipe: Recipe): Recipe => {
          if (!pendingEmoji.has(recipe.uuid)) return recipe;
          const want = pendingEmoji.get(recipe.uuid) ?? null;
          const serverEmoji = recipe.emoji ?? null;
          if (serverEmoji === want) {
            // Server confirmed the optimistic value — stop overriding.
            pendingEmoji.delete(recipe.uuid);
            return recipe;
          }
          const overlaid = { ...recipe };
          if (want === null) {
            delete overlaid.emoji;
          } else {
            overlaid.emoji = want;
          }
          return overlaid;
        };
        return mapped.map((next) => {
          const existing = prevByUuid.get(next.uuid);
          if (!existing) return applyEmojiOverlay(next);
          const prevVariations = new Map(existing.variations.map((v) => [v.uuid, v]));
          // Library-surface stats carry-over (R2): FRESH stats on `next` always
          // win. Only when the fresh payload OMITS stats (a stale pre-deploy
          // cache serve or a mid-revalidation payload) do we carry the prior
          // known stats forward so the cards don't blank between serves. Stale
          // `existing` stats NEVER override a fresh `next.stats`.
          const carriedStats = next.stats ?? existing.stats;
          return applyEmojiOverlay({
            ...next,
            collectionUuids: existing.collectionUuids,
            ...(carriedStats ? { stats: carriedStats } : {}),
            variations: next.variations.map((v) => {
              const pv = prevVariations.get(v.uuid);
              return pv?.cookCountLoaded
                ? { ...v, cookCount: pv.cookCount, cookCountLoaded: true, rating: pv.rating }
                : v;
            }),
          });
        });
      });
    },
    [],
  );

  const applyCollectionListPayload = useCallback(
    (data: CollectionListPayload | undefined): void => {
      // Defensive: see applyRecipeListPayload — failed read leaves state as-is.
      if (!data?.collections) return;
      setCollections(data.collections.map((c) => ({ uuid: c.uuid, slug: c.slug, name: c.name })));
    },
    [],
  );

  // Perf plan §2.C — RESTAMP recipes with their collection memberships from the
  // ONE batched `collection.listAllMembers` payload (was N× listMembers). Builds
  // a reverse map `recipe_uuid → Set<collection_uuid>` from the flat rows, then
  // stamps `collectionUuids` onto every recipe in state. Shared between the mount
  // read and the withCache `onUpdate` revalidation hook so a stale-served map
  // self-corrects — identical policy to applyCollectionListPayload above.
  //
  // Authoritative + full-restamp (review 0dbf171 SHOULD-FIX): the payload covers
  // the WHOLE user, so a recipe ABSENT from it is cleared to `[]`. Additive-only
  // merging would let a background revalidation add a chip but never remove one,
  // stranding an orphan membership after a removal. Only a malformed/absent
  // payload (`!members`, a failed read) is a no-op; a defined-but-empty `members`
  // clears everyone.
  const applyMemberships = useCallback((data: CollectionMembershipPayload | undefined): void => {
    const members = data?.members;
    if (!members) return; // malformed / failed read — leave state untouched
    const recipeCollectionMap = new Map<string, Set<string>>();
    for (const member of members) {
      const existing = recipeCollectionMap.get(member.recipe_uuid);
      if (existing) {
        existing.add(member.collection_uuid);
      } else {
        recipeCollectionMap.set(member.recipe_uuid, new Set([member.collection_uuid]));
      }
    }
    setRecipes((prev) =>
      prev.map((r) => {
        const colSet = recipeCollectionMap.get(r.uuid);
        const next = colSet ? Array.from(colSet) : [];
        // Preserve object identity when membership is unchanged (avoids churning
        // every card on a periodic restamp).
        return sameMembership(r.collectionUuids, next) ? r : { ...r, collectionUuids: next };
      }),
    );
  }, []);

  // Records the SOURCE (cache vs network) of the most recent library-snapshot
  // `recipe.list` serve, set by the withCache `onServe` hook below. The mount
  // read applies the payload itself (so a pass-through withCache in tests still
  // works) and consults this ref to pass the right source into
  // applyRecipeListPayload. `onServe` fires synchronously inside the awaited
  // read, so the ref holds the correct value by the time the mount applies.
  // Defaults to "network" — a payload of unknown provenance is treated as
  // authoritative (never wrongly suppressed).
  const librarySnapshotSourceRef = useRef<"cache" | "network">("network");

  // ── Read-side cache wrapper (perf ladder Path A) ─────────────────────────
  // `cachedApi` mirrors the BackendApi shape but routes the four hot read
  // procedures (recipe.list, recipe.get, attempt.list, collection.list)
  // through an in-memory + IndexedDB SWR cache. Mutating procedures
  // automatically invalidate the relevant cache family on success.
  // userId is the cache namespace — sign-out clears every entry under it via
  // `invalidateAll(userId)` in handleSignOut below.
  // Ref: tasks/pr-body-p4-followup-3.md Path A.
  //
  // The `onUpdate` hook fires when a stale hit's background revalidation lands
  // fresh data (never on fresh hits / cold misses) — re-applying the payload
  // corrects the stale-served library render. Its payloads are always fresh off
  // the wire, so they carry source "network".
  //
  // `attemptListRefreshSignal` is bumped on an attempt.list revalidation and
  // threaded to Workspace, whose readAttempts load effect re-runs and re-reads
  // the now-fresh cache entry (see the onUpdate arm below).
  const [attemptListRefreshSignal, setAttemptListRefreshSignal] = useState(0);
  //
  // The `onServe` hook fires on EVERY served read (cold miss, fresh hit, stale
  // hit) carrying `meta.fromCache`. It records the source of the library
  // snapshot into `librarySnapshotSourceRef` so the mount read (which applies
  // the payload directly) can tell a cache-replayed empty from a wire empty. It
  // is scoped to the full library snapshot (`!input?.cursor`) so a paginated
  // Palette page (cursor set) never overwrites the recorded source.
  const cachedApi = useMemo(() => {
    return withCache(backend.api, userId, {
      onUpdate: (procedure, data) => {
        if (procedure === "recipe.list") {
          applyRecipeListPayload(data as RecipeListPayload, "network");
        } else if (procedure === "collection.list") {
          applyCollectionListPayload(data as CollectionListPayload);
        } else if (procedure === "collection.listAllMembers") {
          applyMemberships(data as CollectionMembershipPayload);
        } else if (procedure === "attempt.list") {
          // A stale-served attempt.list just revalidated. The hook carries no
          // input (no branch key), so rather than applying the payload we bump
          // a signal that re-runs Workspace's readAttempts load effect — the
          // re-read is a fresh cache hit for whatever branch is active. Without
          // this the versions shelf keeps the stale-served snapshot (e.g. a
          // pre-publish `friends_shared` bit) until an unrelated dependency
          // re-fires the effect (B-FR-W2 stale-audience fix, dev QA 2026-07-28).
          setAttemptListRefreshSignal((v) => v + 1);
        }
      },
      onServe: (procedure, _data, meta) => {
        if (procedure !== "recipe.list") return;
        // Only the full library snapshot ({ limit }, no cursor) drives the
        // library grid; ignore paginated Palette pages (cursor set).
        const input = meta.input as { cursor?: unknown } | null | undefined;
        if (input?.cursor != null) return;
        librarySnapshotSourceRef.current = meta.fromCache ? "cache" : "network";
      },
    });
  }, [backend, userId, applyRecipeListPayload, applyCollectionListPayload, applyMemberships]);

  // ── Recipe API client — raw oRPC client for mutations + procedures not on
  // the BackendApi surface (recipe.create/update/delete, recipe.setVisibility,
  // branch.create, export.obsidianRecipe, batch.create, account.delete.*).
  // Reads (recipe.list / collection.list / recipe.get / attempt.list) route
  // through `cachedApi` above. Mutations issued on this raw client MUST pair
  // with an explicit `invalidate(userId, family)` call (Bug #186 pattern) —
  // withCache's automatic invalidation never sees them.
  const recipeApiClient = useMemo(() => createApiClient({ baseUrl: API_BASE_URL }), []);

  // ── Groups API client (family-groups, plan 2026-07-02 §4.5) ─────────────────
  // Typed wrapper over the `group.*` namespace. Passed to LibraryView's Groups
  // section and (below) the ShareToGroupSheet. Same base URL as every client.
  const groupsApi = useMemo(() => createGroupsApi(API_BASE_URL), []);

  // ── Collections API client ────────────────────────────────────────────────
  // Reuses the same base URL as the recipe client. createCollectionsClient
  // wraps the oRPC `collection.*` procedures with typed convenience methods.
  const collectionsClient = useMemo(() => {
    /* eslint-disable @typescript-eslint/no-explicit-any */
    const rawApi = createApiClient({ baseUrl: API_BASE_URL }) as any;
    /* eslint-enable @typescript-eslint/no-explicit-any */
    return createCollectionsClient(rawApi);
  }, []);

  // ── Sync DO connection ────────────────────────────────────────────────────
  // Declared early so handleCreateRecipe (and the URL-restore effect below)
  // can call setCurrentDocKey.
  // The current draft doc key is "draft:<variationUuid>" for the open variation.
  // For the initial empty state (no recipe open) we don't open a sync connection.
  const [currentDocKey, setCurrentDocKey] = useState<string | null>(null);
  const syncDestroyRef = useRef<(() => void) | null>(null);
  // Live sync connection for the current draft. Held so the save-as-version
  // flow can freeze via the editor's OWN provider (the FIFO barrier that makes
  // the frozen version capture the authored content — empty-Share-body v2 fix).
  const syncConnRef = useRef<ReturnType<typeof connectSyncDO> | null>(null);

  /**
   * UUID of the currently-open variation (branch). Derived from currentDocKey
   * (pattern A — arch review §5 Fix #1.2) so it is impossible to forget to set
   * it in a new entry point. currentDocKey is "draft:<variationUuid>" for every
   * open variation — parsing it yields the UUID without a separate state setter.
   *
   * Before this change, currentVariationUuid was a separate useState that had
   * to be set in every handler (handleCreateRecipe, onVariationSelect). The
   * existing-recipe-open path missed the setter because `recipe.variations` was
   * empty when recipe.list ran, so handleSelectVariation never fired. Deriving
   * from currentDocKey makes the relationship structurally unbreakable.
   *
   * IMPORTANT: this memo MUST be declared AFTER currentDocKey useState above —
   * the hook rules require stable ordering and currentDocKey must be in scope.
   */
  const currentVariationUuid = useMemo<string | null>(() => {
    if (!currentDocKey) return null;
    const match = /^draft:(.+)$/.exec(currentDocKey);
    return match ? (match[1] ?? null) : null;
  }, [currentDocKey]);

  // COOK-FIRST-PAINT race fix (2026-06-16): a ref mirror of currentVariationUuid
  // assigned during render (a render-time derived-value cache — safe because the
  // value is purely derived from currentVariationUuid and is only READ later,
  // from async closures + the onContentChange wrapper that run after the commit).
  // Used to STAMP the tagged derivedContent where the closure-captured
  // currentVariationUuid would be stale by the time the live stream fires for
  // the next variation. Reads always see the latest committed value.
  const currentVariationUuidRef = useRef<string | null>(currentVariationUuid);
  currentVariationUuidRef.current = currentVariationUuid;

  // COOK-FIRST-PAINT race fix (2026-06-16): the body the workspace actually
  // renders. The tagged derivedContent only counts when its variationUuid
  // matches the currently-open variation; on a mismatch (the one-frame window
  // after a rapid A→B switch where B's identity has committed but A's body is
  // still in state) we hand the Cook ReadView an EMPTY body. Combined with
  // bodyLoadState='loading' (set on every variation change at the effect below),
  // the ReadView routes to its loading shell (aria-busy) instead of painting the
  // stale body. This makes "recipe A's body under recipe B's identity"
  // structurally impossible — no setTimeout, no race window.
  const recipeContentForActive = useMemo<string>(
    () => (derivedContent.variationUuid === currentVariationUuid ? derivedContent.text : ""),
    [derivedContent, currentVariationUuid],
  );

  // COOK-FIRST-PAINT (2026-06-16): the live onContentChange stream from TipTap
  // stamps each body update with the variation that is open AT THE MOMENT it
  // fires (currentVariationUuidRef holds the latest committed value). A late
  // event for a just-closed variation therefore arrives tagged for the OLD
  // variation and is gated out by recipeContentForActive — it never paints under
  // the new variation's identity. Stable identity (empty dep array) — it only
  // reads refs/setters, both stable.
  const handleDerivedContentChange = useCallback((text: string) => {
    setDerivedContent({ variationUuid: currentVariationUuidRef.current, text });
  }, []);

  // ── URL-restore: open the recipe encoded in the URL after library loads ────
  // Fires once when `recipes` becomes non-empty and a route slug is present.
  // Handles both the uuid shape (spin-off resume) and the slug shape (new
  // recipe creation, where handleCreateRecipe navigates to /app/r/<slug>/edit
  // and the slug may differ from the uuid).
  //
  // Guarded so a subsequent SWR revalidation that re-applies the recipe list
  // does not re-open the recipe after the user has navigated away. Unknown slug
  // → navigate to /app + quiet toast.
  //
  // GUARD SCOPE (route unification audit, 2026-07-24). The guard used to be a
  // bare per-MOUNT boolean, on the assumption that leaving a recipe crossed a
  // route object and remounted AppRoute. It does not: AppRoute mounts once per
  // SPA session (measured — see app.route-unification.test.tsx), so the boolean
  // latched `true` for the whole session and every LATER URL-driven open was
  // silently dropped. Reproduced on the pre-change route table:
  //
  //   • browser-Back onto a recipe URL after closing that recipe → the library
  //     stayed painted underneath a recipe URL;
  //   • any second URL-driven open in one session → nothing happened.
  //
  // (In-library clicks were unaffected: those go through onActivateRecipe, which
  // sets the identity itself and never consults this effect.)
  //
  // The guard is now keyed on the slug it attempted, and re-arms whenever the
  // URL carries no recipe. It still swallows repeat attempts for the SAME
  // unresolved slug — the revalidation loop it exists for — while every genuine
  // open gets its turn.
  //
  // design.md §6 — state preserved on back/forward; restore uses replace:true
  // so the direct URL is not added as a second history entry on top of itself.
  const urlRestoreAttemptedSlugRef = useRef<string | null>(null);
  useEffect(() => {
    // No recipe in the URL: re-arm for the next open and stand down.
    if (!recipeSlugFromRoute) {
      urlRestoreAttemptedSlugRef.current = null;
      return;
    }
    // Only run when no recipe is currently open.
    if (currentRecipeUuid !== null) return;
    if (recipes.length === 0) return;
    // Only attempt once per slug — prevents re-triggering on SWR revalidation.
    if (urlRestoreAttemptedSlugRef.current === recipeSlugFromRoute) return;
    urlRestoreAttemptedSlugRef.current = recipeSlugFromRoute;

    // Match by UUID first (spin-off resume navigates with UUID), then by slug
    // (handleCreateRecipe navigates with the human slug).
    const match =
      recipes.find((r) => r.uuid === recipeSlugFromRoute) ??
      recipes.find((r) => r.slug === recipeSlugFromRoute);

    if (!match) {
      // Unknown / foreign recipe: normalize the URL back to /app and surface a
      // quiet notice. Privacy-safe — the copy never confirms whether the recipe
      // exists (it may be another user's). branding.md §5: no exclamation marks,
      // no banned words.
      void navigate("/app", { replace: true });
      toast.error("That recipe isn’t available.", { duration: 3000 });
      return;
    }

    // Activate the matched recipe through the shared activateRecipe callback
    // so race guard and Rule 8 logging are centralised.
    //
    // URL-restore specifics: `recipesRef.current` may still be stale (empty
    // array) because the recipesRef-sync effect (declared later, runs later in
    // the same flush) has not fired yet. Pre-resolve the variation here from
    // `match` (which comes from the `recipes` closure — always fresh because
    // `recipes` is in this effect's deps) so activateRecipe's recipesRef lookup
    // is bypassed. This is identical logic to resolveOpenVariation; we use the
    // import directly to avoid duplicating the helper call.
    //
    // Note: activateRecipe is stable (only recipeApiClient in deps) and is safe
    // to call from inside this effect without adding it to the deps array — the
    // effect intentionally re-runs only on the listed deps (slug / recipe list /
    // current recipe / location).
    const urlRestoreVariation = resolveOpenVariation(match);
    activateRecipe(
      match.uuid,
      urlRestoreVariation ? { variationUuid: urlRestoreVariation.uuid } : undefined,
    );
    // Replace the URL to use the canonical UUID so subsequent refreshes
    // use the stable identifier even if the human slug changes later.
    //
    // A10 fix (QA 2026-06-11): PRESERVE the mode sub-segment (/edit, /diff)
    // when canonicalising. The previous version dropped it on a documented-
    // but-wrong assumption that Workspace's pathSubModeSegment logic made
    // preservation unnecessary — in fact this replace-navigation rewrote the
    // URL to the bare slug and Workspace's path-sync effect then mapped the
    // bare URL to Cook, discarding the Edit mode the user reloaded into.
    // Invariant: the URL is the source of truth for topMode on slug-keyed
    // routes; canonicalisation may swap the identifier, never the mode.
    // Regression net: app.recipe-url-routing.test.tsx Gate 5.
    const pathSegments = location.pathname.split("/").filter(Boolean);
    // Shape: ["app", "r", "<slug>", "<sub?>"] — index 3 is the mode segment.
    const modeSegment = pathSegments[3];
    void navigate(
      `/app/r/${encodeURIComponent(match.uuid)}${modeSegment ? `/${encodeURIComponent(modeSegment)}` : ""}`,
      { replace: true },
    );
  }, [recipeSlugFromRoute, recipes, currentRecipeUuid, navigate, location.pathname]);

  // ── Cold-reload empty-list fallback (data-loss-shaped window, 2026-06-16) ───
  //
  // The URL-restore effect above is gated on `recipes.length > 0`: it resolves
  // the open recipe out of the recipe.list snapshot. If recipe.list ever returns
  // an EMPTY snapshot on a cold hard-reload while a recipe URL is open — e.g. the
  // fire-and-forget create-time `void invalidate(...)` lost its race and the IDB
  // SWR cache served an empty list before the network refresh landed — restore
  // never fires and the page shows "Your library is empty." even though the
  // recipe exists server-side. That is a data-loss-SHAPED window: the user's
  // recipe is intact on the server but invisible on a deep-link reload.
  //
  // Hardening: when the URL carries a recipe identifier AND the list snapshot is
  // empty AND no recipe is open, resolve that one recipe DIRECTLY via
  // `backend.api.recipe.get` (raw client — bypasses the empty list SWR cache) and
  // seed it into state so the deep-link renders. `recipe.get` resolves the URL
  // segment as a UUID, a current slug, OR a retired slug (DO
  // `resolveRecipeIdOrSlug`, 2026-06-16), so this fires for both shapes — a
  // human-slug deep-link with an empty snapshot now backfills instead of
  // stranding on "Your library is empty." activateRecipe's own branch-list fetch
  // fills the variations the bare RecipeRow omits, so the editor never binds to a
  // stale doc. The route segment is the recipe's uuid OR slug depending on the
  // URL; recipe.get resolves either to the canonical uuid (`row.uuid`), which the
  // seed + activateRecipe always use.
  //
  // Low-risk envelope: fires ONLY on (empty list + recipe URL + no recipe open),
  // once per slug; on any failure it logs (Rule 8) and leaves the empty-library
  // render untouched — strictly additive to today's behaviour. An unknown slug
  // lands in the catch (404) and is logged, same as an unknown uuid.
  //
  // Scoped per-slug (and re-armed on a recipe-free URL) for the same reason as
  // the URL-restore guard above: AppRoute does not remount on a route change, so
  // a per-mount boolean latched for the whole session and the SECOND deep-link
  // of a session stranded on the empty-library render with no fallback fetch.
  const coldReloadFallbackSlugRef = useRef<string | null>(null);
  useEffect(() => {
    if (!recipeSlugFromRoute) {
      coldReloadFallbackSlugRef.current = null;
      return;
    }
    if (currentRecipeUuid !== null) return;
    if (recipes.length > 0) return; // populated list → URL-restore effect owns it
    if (coldReloadFallbackSlugRef.current === recipeSlugFromRoute) return;

    coldReloadFallbackSlugRef.current = recipeSlugFromRoute;
    const targetIdOrSlug = recipeSlugFromRoute;
    // Superseded-guard: recipe.get can be slow; if the user navigates away before
    // it resolves, the effect cleanup flips this true (a dep changed → React runs
    // the cleanup) so a late miss does NOT yank them off the page they moved to.
    let cancelled = false;

    // Shared miss handler: normalize the URL back to /app (mirrors the close-
    // reconciliation convention) and surface a quiet, existence-non-confirming
    // notice. branding.md §5 — no exclamation marks, no banned words. Guards:
    //   - `cancelled`  — the user navigated away (this is the location re-check;
    //     `cancelled` tracks React Router's location, which `window.location`
    //     does not under a memory router).
    //   - `recipesRef` — the library populated meanwhile → URL-restore owns the
    //     miss (it toasts + navigates), so we stand down to avoid a double-bounce.
    const notifyRecipeUnavailable = () => {
      if (cancelled || recipesRef.current.length > 0) return;
      void navigate("/app", { replace: true });
      toast.error("That recipe isn’t available.", { duration: 3000 });
    };

    void (async () => {
      try {
        // Raw client → bypasses the (empty) recipe.list SWR cache entirely.
        // recipe.get accepts a uuid OR a slug (DO resolveRecipeIdOrSlug).
        const row = (await backend.api.recipe.get(targetIdOrSlug)) as {
          uuid: string;
          slug: string;
          title: string;
          emoji?: string | null;
          created_at?: number;
          fork_upstream_recipe_uuid?: string | null;
          feature_media_uuid?: string | null;
        };
        if (cancelled) return;
        if (!row?.uuid) {
          // The server answered but returned no recipe — an unknown or another
          // user's uuid (recipe.get is scoped to the caller's own library, and
          // does not confirm existence). Give feedback + normalize the URL back
          // to /app instead of leaving the dashboard silently under a bad URL
          // (notifyRecipeUnavailable applies the superseded + populated guards).
          notifyRecipeUnavailable();
          return;
        }
        // A concurrent list revalidation may have populated `recipes` while this
        // fetch was in flight — re-check via the ref so we don't clobber it.
        if (recipesRef.current.length > 0) return;

        // Seed a minimal Recipe. Variations are intentionally empty — the bare
        // RecipeRow omits them; activateRecipe fetches the default branch below.
        recipeTitleCacheRef.current.set(row.uuid, row.title);
        const seeded: Recipe = {
          uuid: row.uuid,
          slug: row.slug,
          title: row.title,
          ...(row.emoji ? { emoji: row.emoji } : {}),
          ...(row.feature_media_uuid ? { featureMediaUuid: row.feature_media_uuid } : {}),
          collectionUuids: [],
          variations: [],
          lastEditedAt: row.created_at ?? Date.now(),
          fork_upstream_recipe_uuid: row.fork_upstream_recipe_uuid ?? null,
        };
        setRecipes((prev) => (prev.length > 0 ? prev : [seeded]));
        // Open it — activateRecipe resolves the go-to/default variation (it
        // branch-fetches when local variations are empty, as here).
        activateRecipe(row.uuid);
      } catch (err) {
        // A genuinely missing / foreign recipe throws NOT_FOUND (404) — or a
        // malformed id BAD_REQUEST (400). Those are permanent misses: give the
        // same quiet feedback + URL normalization as the empty-row case above,
        // rather than stranding the user on a silent dashboard under a bad URL.
        // notifyRecipeUnavailable applies the superseded + library-populated
        // guards centrally (the prior inline `currentRecipeUuid === null` here
        // was a stale-closure no-op — this effect only runs when it is null).
        const status = (err as { status?: unknown } | null)?.status;
        if (status === 404 || status === 400) {
          notifyRecipeUnavailable();
          return;
        }
        // A nav-teardown abort (rapid route change aborted recipe.get) is
        // expected noise → debug. Anything else (network / 5xx / transient) is a
        // retryable failure — Rule 8: log at error and leave the empty-library
        // render in place (no worse than today).
        if (isNavigationAbort(err)) {
          console.debug({ event: "cold_reload_recipe_get_aborted", targetIdOrSlug, err });
        } else {
          console.error({
            event: "cold_reload_recipe_get_fallback_failed",
            targetIdOrSlug,
            err,
          });
        }
      }
    })();
    // Cleanup runs when a dep changes (the user navigated away / a recipe opened)
    // BEFORE the async recipe.get may resolve — flip the superseded flag so a
    // late miss does not navigate-replace out from under the new route.
    return () => {
      cancelled = true;
    };
    // activateRecipe is stable (recipeApiClient dep only) and is intentionally
    // omitted from the deps, mirroring the URL-restore effect above. The
    // react-hooks/exhaustive-deps lint rule is not enabled in this config.
    // `navigate` is stable (React Router) and `toast` is a module import.
  }, [recipeSlugFromRoute, recipes.length, currentRecipeUuid, backend, navigate]);

  // ── Cross-recipe URL change re-resolution (P0 remount fix, 2026-06-15) ──────
  //
  // Context: the entry.client.tsx route table was restructured so the sub-mode
  // segments (/edit, /diff, …) are CHILD routes of one parent `/app/r/:slug`
  // route. AppRoute now stays mounted across BOTH mode switches AND recipe
  // switches (the recipe switch already shared the `/app/r/:slug` pattern and
  // kept AppRoute mounted even before this change — the mode switch is what
  // used to cross a route-object boundary and remount).
  //
  // Because the URL-restore effect above is a one-shot (per slug —
  // urlRestoreAttemptedSlugRef) that bails while a recipe is open, browser
  // back/forward between two ALREADY-
  // KNOWN recipes (/app/r/A → /app/r/B → back to /app/r/A) would leave the open
  // recipe stale relative to the URL: the slug in the URL would say A while the
  // workspace still shows B. That is the wrong-body class this whole fix exists
  // to eliminate, so we react to a slug change that points at a DIFFERENT known
  // recipe and re-activate it.
  //
  // Guards that keep this minimal and non-fighting:
  //   - Only fires when a recipe IS open (currentRecipeUuid !== null). The cold
  //     restore (no recipe open) is owned by the one-shot effect above.
  //   - Resolves the slug to a recipe (uuid first, then human slug) and only
  //     acts when that recipe's uuid differs from the open one. This makes the
  //     canonicalising replace-nav above (slug → uuid for the SAME recipe) a
  //     no-op here — the resolved uuid equals currentRecipeUuid, so we bail.
  //   - Does NOT navigate: it only re-binds in-memory state to match the URL the
  //     browser already moved to (back/forward). Adding a navigate() here would
  //     fight the history stack.
  //
  // PRE-PAINT RECONCILIATION (perf recipe-switch, Mechanism 1): this MUST be a
  // useLayoutEffect, not useEffect. A URL-first navigation (browser back/forward
  // between recipes) commits a render with URL=B but identity/docKey/content still
  // A. A passive effect flushes AFTER paint, so a full frame of recipe A under
  // recipe B's URL always committed. Reconciling in a layout effect runs the
  // re-activation BEFORE the browser paints, so the stale frame can no longer
  // commit. The guards are unchanged and the dependency array is IDENTICAL to the
  // passive version — only the flush timing moves earlier. (activateRecipe's
  // internal latestActivationRef guard + the tagged-content identity gate keep the
  // synchronous cascade safe.)
  //
  // PUSH-AWARE ONE-SHOT (perf recipe-switch, in-app push): names the recipe an
  // in-app switch just pushed (set in the in-app onActivateRecipe handler, before
  // its flushSync). This reconciler is a layout effect on `currentRecipeUuid`, so
  // it fires INSIDE that flushSync commit — at which point the URL still resolves
  // to the recipe being LEFT. Without this guard it would bounce the freshly-
  // pushed identity back to the old recipe and defeat the pre-paint fix. Disarmed
  // by this effect once the URL catches up, and by the passive failsafe below on
  // any URL settle (so an aborted / superseded navigate can never leave it stuck).
  const pendingPushUuidRef = useRef<string | null>(null);
  useLayoutEffect(() => {
    if (!recipeSlugFromRoute) return;
    if (currentRecipeUuid === null) return;
    if (recipes.length === 0) return;

    const match =
      recipes.find((r) => r.uuid === recipeSlugFromRoute) ??
      recipes.find((r) => r.slug === recipeSlugFromRoute);

    // Unknown slug while a recipe is open: leave the current recipe in place.
    // The one-shot restore already toasted "That recipe isn't available." on cold
    // loads; a back/forward into an unknown slug (e.g. a deleted recipe) is rare
    // and re-navigating would be more disruptive than holding the last good view.
    if (!match) return;

    // PUSH-AWARE GUARD (perf recipe-switch — in-app push). The in-app switch
    // handler commits the new recipe identity synchronously (flushSync) and THEN
    // moves the URL (navigate). During that flushSync commit this layout effect
    // fires (currentRecipeUuid changed), but `match` still resolves from the STALE
    // URL to the recipe being LEFT. Bouncing here would re-activate the old recipe
    // and undo the pre-paint fix (reproduced on React 18.3.1). While a push is in
    // flight:
    //   • URL has caught up (match === pending) → disarm; fall through (the
    //     steady-state guard below then no-ops, identity === pending).
    //   • URL still resolves to the old recipe → the navigate has not landed; do
    //     NOT bounce and do NOT clear (a concurrent `recipes` revalidation firing
    //     this effect in the same gap must not revert the push either).
    // No pending push → normal back/forward reconciliation (URL is source of
    // truth), exactly as before.
    const pendingPush = pendingPushUuidRef.current;
    if (pendingPush !== null) {
      if (match.uuid === pendingPush) {
        pendingPushUuidRef.current = null;
      } else {
        return;
      }
    }

    // Same recipe (including the slug → uuid canonicalisation case): no-op.
    if (match.uuid === currentRecipeUuid) return;

    // Different known recipe — the URL moved (back/forward / deep link swap).
    // Re-activate so the open recipe + variation track the URL. activateRecipe
    // is stable (recipeApiClient dep only) and centralises the go-to / race
    // guard logic; intentionally omitted from this effect's deps.
    const nextVariation = resolveOpenVariation(match);
    activateRecipe(match.uuid, nextVariation ? { variationUuid: nextVariation.uuid } : undefined);
  }, [recipeSlugFromRoute, recipes, currentRecipeUuid]);

  // Failsafe disarm for the in-app push guard (perf recipe-switch). The
  // reconciler above clears `pendingPushUuidRef` the instant the URL resolves to
  // the pushed recipe. This passive effect additionally disarms whenever the
  // committed URL SETTLES on any new value, so a superseded / aborted navigate
  // (URL never reaches the push target) can never leave the guard armed and
  // permanently suppress the URL-source-of-truth reconciler (its back/forward
  // self-heal). Passive < layout, so on a normal push it only re-clears an
  // already-cleared guard; it never disarms mid-flushSync (the URL does not
  // change during the synchronous activation commit, so this effect does not run
  // then).
  useEffect(() => {
    pendingPushUuidRef.current = null;
  }, [recipeSlugFromRoute]);

  // ── Close-reconciliation (Bug #2 — one-click Library) ──────────────────────
  // The URL is the single source of truth for whether a recipe is open. When the
  // route slug TRANSITIONS from a recipe value to null (onCloseRecipe navigates to
  // /app/library, a tray destination, or the browser back-button leaves
  // /app/r/:slug) while a recipe is still open in memory, reconcile the recipe
  // identity down so the Workspace (mounted display:none) yields to the Library
  // view. This replaces the previous two-writer close handler whose state-clear
  // raced the navigate — keeping close purely URL-driven removes the "first click
  // only refreshes the recipe" bug class.
  //
  // EVERY DESTINATION, INCLUDING /app/library (route unification, 2026-07-24).
  // The effect keys on the SLUG, not on where the user landed, so /app/library
  // (the close affordance's target), /app (the dashboard) and every tray
  // destination are covered by the same transition. That is load-bearing now
  // that the route table states the single-AppRoute property explicitly:
  // NOTHING else clears the identity — there is no remount to fall back on, and
  // measurement showed there never was one. Clearing currentDocKey here is also
  // what tears the sync connection down (the `[currentDocKey]` effect's cleanup
  // destroys the provider and drops the y-doc).
  // Regression net: app.route-unification.test.tsx.
  //
  // CRITICAL: we key off the slug TRANSITION (prev !== null → null), not the
  // steady state (slug === null && uuid !== null). A steady-state guard also fires
  // during programmatic activate / async spin-off, where activateRecipe sets
  // currentRecipeUuid a commit before the navigate-to-slug lands — clearing there
  // would nuke a freshly-opened recipe (the fresh-recipe-vanish class). When the
  // slug was already null (any /app landing) and a recipe is activated, prevSlug is
  // null, so this effect never fires. (Settings' workspaceVisibleOverride is cleared
  // by onCloseRecipe directly: it is not URL-routed, so the slug is already null
  // when Settings opens and this effect would not distinguish it from a Library
  // landing.)
  //
  // PRE-PAINT RECONCILIATION (perf recipe-switch, Mechanism 1): useLayoutEffect,
  // not useEffect — same rationale as the cross-recipe effect above. A URL-first
  // close (browser back off /app/r/:slug) commits a render with URL=/app but the
  // recipe still open in memory; a passive flush would paint the stale recipe
  // under the Library URL for a frame. Reconciling before paint drops that frame.
  // Dependency array unchanged.
  const prevRecipeSlugRef = useRef<string | null>(recipeSlugFromRoute);
  useLayoutEffect(() => {
    const prevSlug = prevRecipeSlugRef.current;
    prevRecipeSlugRef.current = recipeSlugFromRoute;
    if (prevSlug === null) return; // never left a recipe URL — not a close
    if (recipeSlugFromRoute !== null) return; // moved between recipes, not away
    if (currentRecipeUuid === null) return;
    setCurrentRecipeUuid(null);
    setCurrentDocKey(null);
  }, [recipeSlugFromRoute, currentRecipeUuid]);

  // Nav-tray scroll-lock guard: the full docked tray only shows when no recipe
  // is open (currentRecipeUuid === null). The account-menu overlay engages a
  // body scroll-lock while open; if the view transitions to the no-recipe
  // (full-tray) state with the overlay still open, that lock can leak. Force it
  // shut whenever we land on the no-recipe view so the tray is the only nav
  // chrome and no stale overlay lock survives the transition.
  useEffect(() => {
    if (currentRecipeUuid === null) setUserMenuOpen(false);
  }, [currentRecipeUuid]);

  // SF-1 (perf recipe-switch) — reset the body-hydrated flag BEFORE paint on every
  // docKey change. `bodyHydrated` is a bare boolean carrying no identity; the
  // connection swap that sets it false again lives in the passive `[currentDocKey]`
  // effect below, which flushes AFTER paint. So on the first frame after an in-app
  // switch the OLD doc's completed-handshake `true` would still be committed under
  // the NEW identity and suppress the fast-paint overlay (isBodySnapshotOverlayVisible
  // returns false when bodyHydrated) — a one-frame BLANK editor per switch, the beat
  // the operator's "seamless" ask forbids. Resetting pre-paint keeps the overlay
  // covering the switch window; it dissolves again the moment the NEW doc completes
  // its own handshake (setBodyHydrated(true) in the passive effect's onSync).
  useLayoutEffect(() => {
    setBodyHydrated(false);
  }, [currentDocKey]);

  useEffect(() => {
    // Tear down any previous connection.
    if (syncDestroyRef.current) {
      syncDestroyRef.current();
      syncDestroyRef.current = null;
    }

    // V2 fix (2026-05-03): clear the derived body on every docKey change so
    // downstream consumers (ReadView, ValuesPanel, tab-title derivation) do not
    // show the previous variation's text in the gap between switch and WS sync +
    // server re-hydrate. The editor itself is independent — it reads from the
    // y-doc via BlocksBinding — but those siblings consume the body directly.
    // Without this reset, switching from variation A (recently edited "alpha") to
    // a different recipe / variation B briefly shows "alpha" in the read pane
    // before the new y-doc populates.
    //
    // COOK-FIRST-PAINT (2026-06-16): tag the empty body with the variation we are
    // switching TO (the ref holds the latest committed currentVariationUuid). The
    // identity gate (recipeContentForActive) then treats an empty body as
    // belonging to the new variation, so the Cook ReadView falls to its loading
    // shell rather than showing the old variation's text.
    setDerivedContent({ variationUuid: currentVariationUuidRef.current, text: "" });

    // Phase 1.C / SF-1: the body-hydrated flag is reset PRE-PAINT by the
    // useLayoutEffect keyed on currentDocKey (declared above) so the PREVIOUS
    // doc's completed-handshake `true` can never survive one frame under the NEW
    // identity and suppress the fast-paint overlay. It is deliberately NOT reset
    // here — a post-paint reset would be too late for that first post-switch
    // frame, which is exactly the blank-editor beat SF-1 removes.

    if (!currentDocKey) {
      setSyncState("saved"); // no doc open → settled state
      return;
    }

    // 4th arg (Phase 1.C): the real hydration-complete signal (provider.synced),
    // threaded to Workspace as `bodyHydrated` — the terminal dissolve signal for
    // the BodySnapshot overlay's synced-but-empty body case. Distinct from
    // syncState, which flips to "saved" on "connected", BEFORE syncStep2.
    const conn = connectSyncDO(currentDocKey, SYNC_BASE_URL, setSyncState, () =>
      setBodyHydrated(true),
    );
    // Fix #1.3: expose the Y.Doc so it can be threaded to TipTapEditor.
    // DOC-IDENTITY (Mechanism 2): tag the doc with THIS effect's docKey in one
    // atomic setState. `currentDocKey` is the effect's captured, non-null key
    // (guarded above), so the stored pair always belongs to the same variation.
    setCurrentYDocEntry({ docKey: currentDocKey, doc: conn.doc });
    syncConnRef.current = conn;
    syncDestroyRef.current = () => conn.destroy();

    return () => {
      conn.destroy();
      syncConnRef.current = null;
      syncDestroyRef.current = null;
      // Clear the ydoc reference on teardown so TipTap receives undefined
      // (local-only mode) rather than a destroyed doc.
      setCurrentYDocEntry(null);
    };
  }, [currentDocKey]);

  // ── saveAsVersion override — freeze via the live editor provider ────────────
  // empty-Share-body v2 fix: prefer the WS path (freezes the live draft y-doc via
  // the same-WS FIFO barrier) when an editor connection is live; fall back to the
  // HTTP RPC (legacy SQLite freeze) only when no live doc is open. The fallback
  // never captures live edits, so it is genuinely a last resort (offline / no
  // editor mounted) — the editor flow always has a live conn here.
  const handleSaveAsVersion = useCallback(
    async (input: { attemptUuid: string }) => {
      const conn = syncConnRef.current;
      // NEW-BUG-1: both arms carry `frozenAttemptUuid` (the just-frozen version)
      // so the post-freeze prompt amends the right version. The WS arm reads it
      // from the ack; the raw arm reads it from the server response and falls
      // back to the input attemptUuid (the raw path always freezes exactly that
      // attempt). Both are best-effort optional — a legacy server omits it and
      // the Workspace falls back to its local uuid.
      const result = conn
        ? await conn.sendSaveAsVersion().then((res) => ({
            ok: true as const,
            newAttemptUuid: res.newAttemptUuid,
            branchUuid: res.branchUuid,
            ...(res.frozenAttemptUuid !== undefined
              ? { frozenAttemptUuid: res.frozenAttemptUuid }
              : {}),
          }))
        : await backend.api.version.saveAsVersion(input).then((res) => ({
            ...res,
            frozenAttemptUuid: res.frozenAttemptUuid ?? input.attemptUuid,
          }));

      // BUG (dev review 2026-07-02): both arms above bypass `withCache`'s
      // `version.saveAsVersion` wrapper, which drops the attempt + recipe SWR
      // families after a freeze (with-cache.ts:310-313). Without invalidating
      // here, the shelf / version label / share-gate can read a ≤15s-stale
      // `attempt.list` right after "Save as version". Reuse the wrapper's exact
      // helper so the await-Promise.all-with-catch behaviour is identical.
      // Failure to invalidate must not fail the save (best-effort) — the freeze
      // already succeeded and stale data self-heals on the next read/refresh.
      await settleInvalidate(userId, ["attempt", "recipe"]).catch((err) => {
        console.error("[proof.cache] invalidate after saveAsVersion failed", err);
      });

      return result;
    },
    [backend, userId],
  );

  // Thin api shim passed to Workspace's SaveAsVersionButton so the editor's
  // "Save as version" routes through handleSaveAsVersion (live-WS freeze).
  const saveAsVersionApiShim = useMemo(
    () => ({ version: { saveAsVersion: handleSaveAsVersion } }),
    [handleSaveAsVersion],
  );

  // ── BUG-01 + A7: fetch the draft attempt whenever the open variation changes ─
  // TipTapEditor gates the Save-as-version button on `attemptUuid !== null`.
  // Workspace derives that from the combined `currentAttempt` prop. Without
  // this effect, `currentAttempt` is always null and the button never renders.
  //
  // We call `attempt.list({ branchUuid, status: "draft" })` and take the first
  // result — the API returns most-recent-first and a variation has at most one
  // Draft at any time (the other statuses are cooked / promoted / archived).
  //
  // Best-effort: failure logs and leaves `currentAttempt` at null, which
  // degrades gracefully (button hidden rather than app crash).
  //
  // P0-2 follow-up: this effect also drives `bodyLoadState`.
  //   - "loading" is set immediately when currentVariationUuid changes (cleared
  //     alongside currentAttempt), so the cook shell shows pulsing bars.
  //   - "ready" is set on resolve OR error — both are "settled". An error
  //     leaves the body empty but is still a settled state (no network retries).
  //   Rule: never show the "empty" message during the initial fetch window.
  useEffect(() => {
    // Clear immediately on variation change so stale attempts don't leak through.
    setCurrentAttempt(null);
    // P0-2: entering loading state — cook shell shows pulsing bars until settled.
    setBodyLoadState("loading");

    const api = cachedApi ?? backend.api;
    if (!api || !currentVariationUuid) {
      // No variation open: consider settled immediately (nothing to load).
      setBodyLoadState("ready");
      return;
    }

    let cancelled = false;
    async function fetchDraftAttempt() {
      try {
        // ── PERF-W1 (a): single attempt.list, no status filter ─────────────
        //
        // Collapses the former up-to-3 SERIAL RPCs (draft list → ensureDraft →
        // frozen-fallback list) into ONE call. Both the draft row AND the
        // frozen-body fallback are derived client-side from this single result.
        //
        // Cache-key parity (PERF-W1 b): this is the SAME call the hover /
        // pointerdown / focus prefetch warms (handlePrefetchRecipe →
        // attempt.list({ branchUuid, limit: 5 })). The input object matches
        // field-for-field, so cacheKey (sorted-key canonical JSON) produces the
        // IDENTICAL key and the 15s-TTL SWR entry dedupes this foreground
        // round-trip on a warm open. See the key-equality unit test in
        // packages/client-core/test/cache/key.test.ts.
        //
        // limit:5 truncation is preserved parity, not a regression: the
        // pre-collapse frozen-fallback used the same `attempt.list({ branchUuid,
        // limit: 5 })`. attempt.list orders ORDER BY a.uuid ASC (oldest-first,
        // attempts.ts), so this window is the oldest 5 rows and the fallback
        // reverse-finds the newest non-draft WITHIN that window — exactly as
        // before. (Caveat: the draft row is the branch head — the newest uuid —
        // so on a variation with >5 attempts it falls OUTSIDE this window; the
        // idempotent ensureDraft self-heal below covers that path without a
        // duplicate INSERT.)
        const { attempts } = await api!.attempt.list({
          branchUuid: currentVariationUuid!,
          limit: 5,
        });
        if (cancelled) return;

        // ── Draft-pick ──────────────────────────────────────────────────────
        // In all live client flows a variation has at most one draft at a time
        // (the server's startNextVersion CAN insert a draft without checking for
        // an existing one, so this is a client-flow invariant, not a hard DB
        // constraint). Find the draft in the single list regardless of position
        // (ASC order puts the newest draft last) — first match wins, which is
        // equivalent to the former status:"draft" list for any variation whose
        // draft is inside the limit:5 window.
        const draftRow = attempts.find((a) => a.status === "draft") ?? null;
        let resolvedAttemptUuid: string | null = draftRow?.uuid ?? null;
        let resolvedBodyMarkdown: string | null = draftRow?.body_markdown ?? null;

        // ── BUG-01 self-heal: draftless-branch repair (idempotent) ─────────
        //
        // Reached when NO draft row is present in the list — either a genuinely
        // draftless branch (pre-Wave-3 legacy commit flow consumed the draft
        // row: draft→cooked with no fresh draft inserted) OR a >5-attempt
        // variation whose draft (the branch head, newest uuid) fell outside the
        // ASC limit:5 window. ensureDraftAttempt is IDEMPOTENT: when a draft
        // already exists on the branch head it returns that row (created:false)
        // with its body and does NOT insert, so correctness holds on both paths
        // — no duplicate draft is minted. The raw backend.api client is used
        // (not cachedApi) so the write bypasses the SWR wrapper (Bug #186).
        //
        // Rule 8: errors are console.error'd and leave currentAttempt null
        // (degraded state: Save slot hidden). No user-facing toast — the
        // degraded state is the only symptom and it is not an actionable error.
        if (resolvedAttemptUuid === null) {
          try {
            const ensureResult = await backend.api.attempt.ensureDraft({
              branchUuid: currentVariationUuid!,
            });
            if (cancelled) return;
            resolvedAttemptUuid = ensureResult.attemptUuid;
            resolvedBodyMarkdown = ensureResult.bodyMarkdown;
            // Invalidate the SWR "attempt" family so the next attempt.list reads
            // fresh data (Bug #186 pattern — raw client bypasses withCache) —
            // but ONLY when ensureDraft actually inserted a row. On the
            // idempotent created:false heal path (every open of a >5-attempt
            // variation, whose draft fell outside the ASC limit:5 window)
            // NOTHING changed server-side, so a family-wide eviction would
            // pointlessly drop every attempt.* entry — including freshly
            // prefetched keys for OTHER recipes — forcing needless refetches.
            if (ensureResult.created) {
              void invalidate(userId, "attempt").catch((err) =>
                console.warn("[proof.cache] invalidate(attempt) after ensureDraft failed", { err }),
              );
            }
          } catch (ensureErr) {
            // Rule 8: log but do not throw — degrade to hidden Save slot.
            console.error({
              event: "ensure_draft_failed",
              variationUuid: currentVariationUuid,
              err: ensureErr,
            });
            // currentAttempt stays null; bodyLoadState settled below.
          }
        }

        if (cancelled) return;
        // A7 — thread uuid AND status together. Status is "draft" by
        // construction on both paths: the draft-pick above filters on
        // status === "draft", and ensureDraft mints/returns a draft. Discarding
        // the status here (storing only the uuid) is exactly what left
        // Workspace's status-gated surfaces unreachable in production pre-fix.
        setCurrentAttempt(
          resolvedAttemptUuid !== null ? { uuid: resolvedAttemptUuid, status: "draft" } : null,
        );

        const draftBody = resolvedBodyMarkdown;

        if (!isBodyScaffoldOrEmpty(draftBody)) {
          // Draft has real content — seed cook view from the draft body so
          // cook mode can paint immediately without waiting for the y-doc
          // WS handshake. The cachedApi (SWR 15s TTL) returns synchronously
          // on warm opens (~0 ms round-trip). When onContentChange fires later
          // (from TipTap's y-doc sync), it will overwrite this seed with the
          // live CRDT state — no stale-body risk.
          //
          // COOK-FIRST-PAINT (2026-06-16): tag with the closure-captured
          // currentVariationUuid. The cancelled-guard above already bailed for a
          // superseded run, so this seed can only land for the variation this
          // effect run owns.
          setDerivedContent({ variationUuid: currentVariationUuid, text: draftBody! });
          setBodyLoadState("ready");
          return;
        }

        // ── Defect 1: frozen-content fallback for cook (derived from the SAME
        //    single list — no second round-trip) ────────────────────────────
        //
        // The draft body is empty or scaffold-only (creation template). This
        // happens for any recipe whose live content is in the y-doc but the
        // draft row's body_markdown was never written (it's written at the
        // draft→X commit step), or whose draft was cleared after a freeze.
        //
        // Instead of seeding cook view with an empty body (which would trigger
        // the "This recipe is empty." message and the auto-edit redirect even
        // when frozen attempts hold real content), seed from the most recent
        // non-draft attempt in the list already in hand.
        //
        // Seed Cook from the MOST RECENT non-draft attempt. The list is
        // oldest-first (ORDER BY uuid ASC), so reverse-find lands on the newest
        // non-draft (cooked/promoted/archived) — never the oldest. This is the
        // pre-existing Defect-1 seeding semantic (app.tsx:1988-2006, pre-
        // collapse) unchanged — only the source list is now the single call
        // above rather than a second attempt.list round-trip. (Flipping the SQL
        // to DESC is unsafe: the variation-switcher vLabel mapping is positional
        // and assumes ASC ordering.)
        //
        // The fallback result only affects the COOK seed (recipeContentForActive
        // for ReadView). Edit mode always binds the live y-doc (TipTap/CRDT)
        // and is untouched by this change.
        const frozenAttempt = [...attempts].reverse().find((a) => a.status !== "draft");

        if (frozenAttempt && !isBodyScaffoldOrEmpty(frozenAttempt.body_markdown)) {
          // Frozen content found — seed cook view from it. The auto-edit
          // redirect in Workspace will not fire because the active body is
          // non-empty (editorHasContent becomes true).
          //
          // COOK-FIRST-PAINT (2026-06-16): tag with the closure-captured
          // currentVariationUuid (guard above already bailed for a superseded
          // run, so this seed only lands for the variation this run owns).
          setDerivedContent({
            variationUuid: currentVariationUuid,
            text: frozenAttempt.body_markdown,
          });
        }
        // Whether or not a frozen body was found, the fetch has settled.
        // bodyLoadState="ready" lets Workspace determine the true empty/non-empty
        // state.
        setBodyLoadState("ready");
      } catch (err) {
        // Non-fatal: degrade to hidden save-as-version button.
        console.error({
          event: "draft_attempt_fetch_failed",
          variationUuid: currentVariationUuid,
          err,
        });
        // P0-2: fetch settled with error → treat as ready (empty body).
        // The empty state is the honest fallback; don't leave the cook shell
        // spinning indefinitely on a network error.
        if (!cancelled) {
          setBodyLoadState("ready");
        }
      }
    }
    void fetchDraftAttempt();
    return () => {
      cancelled = true;
    };
  }, [currentVariationUuid, cachedApi, backend, userId]);

  // ── recipe.list + collection.list — co-issued on mount (F7 perf fix) ────────
  // `backend` is synchronously initialised (F1 fix — no more async createBackend),
  // so this effect fires on first render without waiting for a state update.
  //
  // F7: recipe.list and collection.list are issued in parallel via Promise.allSettled
  // so the two independent RPCs race rather than running in series (previously two
  // separate useEffect hooks — same microtask queue start, but each fetch was
  // unaware of the other). Using allSettled so a failure on either side is
  // handled independently — a failed collection list does not suppress the recipe
  // list and vice versa.
  //
  // Bug 10 fix: after recipe.list resolves, the collection list result (already in
  // hand from the parallel call) is re-used for the membership enrichment pass —
  // avoids the previous duplicate `collectionsClient.listCollections()` call
  // inside fetchLibrary.
  useEffect(() => {
    let cancelled = false;
    async function fetchLibraryAndCollections() {
      // Wave PERF-1 — stamp the dominant initial RPC roundtrip. recipe.list
      // is the load-bearing read on /app entry and the most likely candidate
      // for the operator-reported "few seconds" symptom. The end mark
      // lands inside both the success and the catch path so a failed RPC
      // still shows up on the timeline.
      perfMark(RECIPE_OPEN_MARKS.rpcStart);

      // F7: co-issue recipe.list and collection.list in parallel.
      //
      // PERF: both reads route through `cachedApi` (SWR cache — recipe.list
      // 30 s TTL, collection.list 60 s TTL, mutation invalidation). Warm
      // opens render the library from IDB in ~0 ms; a stale hit returns
      // immediately and the background revalidation re-applies fresh data via
      // the withCache `onUpdate` hook above. The wire payload is
      // RecipeListPayload (recipe.list returns variations[] inline,
      // metadata-only — Bug F1, 2026-05-03); the cast documents the runtime
      // shape, which is wider than BackendApi's RecipeRow[] return type.
      const [recipeResult, collectionResult] = await Promise.allSettled([
        cachedApi.recipe.list({ limit: 100 }) as Promise<RecipeListPayload>,
        cachedApi.collection.list(),
      ]);

      // Wave PERF-1 — RPC roundtrip resolved. Mark BEFORE the cancelled
      // early-return so the timeline still records the roundtrip duration
      // even if the component unmounted while the fetch was in flight.
      perfMark(RECIPE_OPEN_MARKS.rpcEnd);
      if (cancelled) return;

      // Settle the Library loading gate the moment recipe.list resolves —
      // success OR failure (Promise.allSettled never rejects, so an offline /
      // rejected recipe.list still lands here and never strands a permanent
      // skeleton). LibraryView renders neither empty-state nor grid until this
      // flips to "ready".
      setRecipesLoadState("ready");

      // ── Handle collection.list result ───────────────────────────────────
      const collectionList =
        collectionResult.status === "fulfilled" ? (collectionResult.value?.collections ?? []) : [];
      if (collectionResult.status === "fulfilled") {
        applyCollectionListPayload(collectionResult.value);
      } else if (isNavigationAbort(collectionResult.reason)) {
        // Rapid route change aborted the in-flight read → debug, not error.
        console.debug({ event: "collection_list_aborted", err: collectionResult.reason });
      } else {
        console.error({ event: "collection_list_failed", err: collectionResult.reason });
      }

      // ── Handle recipe.list result ────────────────────────────────────────
      if (recipeResult.status === "rejected") {
        // #375 — a rejected recipe.list with no cached snapshot leaves recipes
        // []. Flag the failure so the library shell shows a quiet retry surface
        // instead of the false "Your library is empty." (Rule 8: a failed read
        // must be visible). recipe.list is a GET; if CSRF blocks it that is a
        // separate bug (documented in acceptance criteria). A nav-teardown abort
        // (rapid route change) is expected noise → debug, not an error; the
        // retry-surface flag still flips (a harmless no-op if we're unmounting).
        if (isNavigationAbort(recipeResult.reason)) {
          console.debug({ event: "recipe_list_aborted", err: recipeResult.reason });
        } else {
          console.error({ event: "recipe_list_failed", err: recipeResult.reason });
        }
        setRecipesLoadError(true);
        return;
      }
      // Success: clear any prior load-error and apply the payload. Mapping +
      // setRecipes live in applyRecipeListPayload (shared with the SWR
      // revalidation hook). The source comes from the withCache `onServe` hook
      // (recorded in librarySnapshotSourceRef) so a stale cache-sourced empty
      // cannot wipe a populated library; a wire-sourced empty still applies.
      setRecipesLoadError(false);
      applyRecipeListPayload(recipeResult.value, librarySnapshotSourceRef.current);

      // Bug 10 fix / perf plan §2.C — enrich collectionUuids from ONE batched
      // membership read (was N× listRecipesInCollection, one RPC per collection
      // on every library mount). Routes through `cachedApi` — same SWR seam as
      // collection.list above (60 s TTL, `collection` mutation invalidation) —
      // so warm remounts serve from cache and the background revalidation
      // re-derives the map via the `collection.listAllMembers` onUpdate hook.
      // Non-fatal: if this fails the filter chip degrades to "no filter"
      // (collectionUuids stays []) rather than breaking the whole Library.
      try {
        if (collectionList.length === 0) return; // no collections → no memberships
        if (cancelled) return;

        const membersPayload = await cachedApi.collection.listAllMembers();
        if (cancelled) return;
        applyMemberships(membersPayload as CollectionMembershipPayload);
      } catch (memberErr) {
        // Non-fatal: collection memberships failed — filter chips degrade to empty.
        console.error({ event: "collection_membership_fetch_failed", err: memberErr });
      }
    }
    void fetchLibraryAndCollections();
    return () => {
      cancelled = true;
    };
  }, [cachedApi, applyRecipeListPayload, applyCollectionListPayload, applyMemberships]);

  // ── onCreateCollection ────────────────────────────────────────────────────
  // Optimistic-add first, then confirm with the server.
  // On server error: rollback optimistic entry and re-throw so the modal
  // surfaces the inline error (CollectionCreateModal handles 409 / generic).
  const handleCreateCollection = useCallback(
    async (name: string): Promise<void> => {
      // Derive slug client-side to check for local duplicates instantly.
      const slug = slugifyCollectionName(name);

      // Optimistic entry — visible immediately.
      const optimisticUuid = `col-optimistic-${Date.now()}`;
      const optimistic: Collection = { uuid: optimisticUuid, slug, name };
      setCollections((prev) => [...prev, optimistic]);

      try {
        const created = await collectionsClient.createCollection(name);
        // Replace optimistic entry with the server-confirmed record.
        setCollections((prev) =>
          prev.map((c) =>
            c.uuid === optimisticUuid
              ? { uuid: created.uuid, slug: created.slug, name: created.name }
              : c,
          ),
        );
        // The raw collectionsClient bypasses withCache's collection.create
        // wrapper, so drop the cached collection family here (Bug #186
        // pattern) — the library collection.list now reads through the cache.
        void invalidate(userId, "collection").catch((err) => {
          console.warn("[proof.cache] invalidate(collection) after create failed", { err });
        });
      } catch (err) {
        // Roll back optimistic entry on failure.
        setCollections((prev) => prev.filter((c) => c.uuid !== optimisticUuid));
        // Re-throw so CollectionCreateModal can display the inline error.
        // Map CONFLICT → slug_taken so the modal shows the right copy.
        const isConflict =
          err instanceof Error &&
          (err.message.toLowerCase().includes("conflict") ||
            err.message.toLowerCase().includes("slug_taken"));
        throw new Error(
          isConflict ? "slug_taken" : err instanceof Error ? err.message : "create_failed",
        );
      }
    },
    [collectionsClient, userId],
  );

  // ── onRenameCollection ────────────────────────────────────────────────────
  const handleRenameCollection = useCallback(
    async (uuid: string, newName: string): Promise<void> => {
      const prevCollections = [...collections];

      // Optimistic update.
      setCollections((prev) =>
        prev.map((c) =>
          c.uuid === uuid ? { ...c, name: newName, slug: slugifyCollectionName(newName) } : c,
        ),
      );

      try {
        await collectionsClient.renameCollection(uuid, newName);
        // Raw client bypasses withCache invalidation — drop the cached
        // collection family so the next cached read reflects the rename.
        void invalidate(userId, "collection").catch((err) => {
          console.warn("[proof.cache] invalidate(collection) after rename failed", { err });
        });
      } catch (err) {
        // Roll back.
        setCollections(prevCollections);
        const isConflict =
          err instanceof Error &&
          (err.message.toLowerCase().includes("conflict") ||
            err.message.toLowerCase().includes("slug_taken"));
        throw new Error(
          isConflict ? "slug_taken" : err instanceof Error ? err.message : "rename_failed",
        );
      }
    },
    [collectionsClient, collections, userId],
  );

  // ── onDeleteCollection ────────────────────────────────────────────────────
  const handleDeleteCollection = useCallback(
    async (uuid: string): Promise<void> => {
      const prevCollections = [...collections];

      // Optimistic removal.
      setCollections((prev) => prev.filter((c) => c.uuid !== uuid));

      try {
        await collectionsClient.deleteCollection(uuid);
        // Raw client bypasses withCache invalidation — drop the cached
        // collection family so the next cached read reflects the deletion.
        void invalidate(userId, "collection").catch((err) => {
          console.warn("[proof.cache] invalidate(collection) after delete failed", { err });
        });
      } catch (err) {
        // Roll back.
        setCollections(prevCollections);
        throw new Error(err instanceof Error ? err.message : "delete_failed");
      }
    },
    [collectionsClient, collections, userId],
  );

  // Ref kept in lockstep with `recipes` so create-time logic can read the
  // LATEST list synchronously even when two creates fire in quick succession
  // before React commits the in-memory setRecipes.
  // handleCreateRecipe also writes to this ref synchronously inside its
  // setRecipes updater so even tight back-to-back create calls within a single
  // render tick see the just-added recipe.
  const recipesRef = useRef(recipes);
  useEffect(() => {
    recipesRef.current = recipes;
  }, [recipes]);

  // ── activateRecipe — single consolidated activation callback ──────────────
  //
  // All six activation paths (Library click, URL-restore, palette (library
  // landing + workspace), spin-off success, batch-tab activate) route through
  // this one callback so the go-to logic, stale-closure guard, race guard, and
  // Rule 8 logging live in exactly one place.
  //
  // Race guard: `latestActivationRef` stores the uuid of the MOST RECENT
  // synchronous activation call. The async branch.list fallback reads it in its
  // .then() to bail when the user activated a different recipe while the fetch
  // was in flight — the weak previous guard (checking whether the uuid was still
  // in recipesRef) let a stale response clobber a newer selection if the user
  // switched from B→C while B's branch.list was in flight (C is in recipesRef;
  // the guard passed; B's response landed and overwrote C's docKey).
  //
  // activateRecipe does NOT navigate — every caller keeps its own navigate()
  // call because the target and method (push vs replace, URL shape) differ.
  const latestActivationRef = useRef<string | null>(null);

  const activateRecipe = useCallback(
    (recipeUuid: string, opts?: { variationUuid?: string }) => {
      // Synchronously stamp the latest activation so the async race guard is
      // always up-to-date, regardless of render cycle boundaries.
      latestActivationRef.current = recipeUuid;

      setCurrentRecipeUuid(recipeUuid);

      // Cache the activated recipe's title (miss-proof header source). Read from
      // recipesRef so it is fresh even on back-to-back activations.
      cacheRecipeTitle(recipeUuid, recipesRef.current.find((r) => r.uuid === recipeUuid)?.title);

      if (opts?.variationUuid) {
        setCurrentDocKey(`draft:${opts.variationUuid}`);
        return;
      }

      // resolveOpenVariation: prefer the go-to variation, fall back to first.
      // Read from recipesRef (not the `recipes` closure) to avoid stale-closure
      // misses on back-to-back activations before React commits the preceding
      // setRecipes call.
      const recipe = recipesRef.current.find((r) => r.uuid === recipeUuid);
      const resolved = recipe ? resolveOpenVariation(recipe) : undefined;
      if (resolved) {
        setCurrentDocKey(`draft:${resolved.uuid}`);
        return;
      }

      // Variations empty in local state (stale list or race between recipe.list
      // and the activation). Fetch the default branch from the backend so the
      // editor is never left bound to the previous recipe's doc.
      // Rule 8: log on failure — never silently swallow.
      //
      // INPUT-SHAPE (2026-06-16): `recipeApiClient` is the RAW oRPC client
      // (createApiClient), NOT the positional `BackendApi` wrapper. The raw
      // `branch.list` procedure validates its input against
      // `z.object({ recipeUuid, status? })` (apps/api/src/routes/branches/
      // index.ts:102) — so it MUST be called with the OBJECT form
      // `{ recipeUuid }`. Passing a bare string (the positional form the
      // `BackendApi` wrapper accepts) failed zod validation with "Input
      // validation failed", the `.catch` logged `activate_recipe_branch_list_
      // failed`, and `setCurrentDocKey` was never reached — so the editor never
      // bound to a y-doc and the body came back EMPTY on any activation that hit
      // this fallback. This path was latent until the cold-reload empty-list
      // fallback (above) began seeding recipes with `variations: []` and routing
      // them here; it surfaced as the post-reload empty editor on a UUID-keyed
      // `/edit` deep-link (cross-recipe-cook-edit.spec.ts step f). Mirrors the
      // raw-client object form already used by recipe.create / branch.create.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const branchApi = (recipeApiClient as any).branch as
        | {
            list: (input: {
              recipeUuid: string;
            }) => Promise<{ branches: Array<{ uuid: string; isGoTo?: boolean }> }>;
          }
        | undefined;
      if (!branchApi) {
        console.error({
          event: "activate_recipe_branch_api_missing",
          recipeUuid,
        });
        return;
      }
      void branchApi
        .list({ recipeUuid })
        .then(({ branches }: { branches: Array<{ uuid: string; isGoTo?: boolean }> }) => {
          // Race guard: bail if the user activated a different recipe while
          // this fetch was in flight. Comparing against latestActivationRef
          // (not recipesRef) catches the case where the newer recipe is ALSO
          // present in recipesRef — recipesRef membership only tells us the
          // recipe exists, not that it is still the pending activation.
          if (latestActivationRef.current !== recipeUuid) return;
          const branch = branches.find((b) => b.isGoTo) ?? branches[0];
          if (branch) setCurrentDocKey(`draft:${branch.uuid}`);
        })
        .catch((err: unknown) => {
          console.error({
            event: "activate_recipe_branch_list_failed",
            recipeUuid,
            err,
          });
        });
    },
    [recipeApiClient, cacheRecipeTitle],
  );

  // ── onCreateRecipe — creates a new draft recipe and activates it ──────────
  // Followup Bug B — signature now requires `params: { title, collectionUuids }`
  // (Workspace's RecipeCreationModal always passes both). The legacy `params?`
  // overload is gone — callers without details should use the modal flow.
  // The Wizard's onSaveRecipe path supplies a real title (no fallback default).
  // Returns the recipe UUID on success. Throws on failure (caller surfaces error).
  const handleCreateRecipe = useCallback(
    async (params: { title: string; collectionUuids?: string[] }): Promise<string> => {
      const title = params.title.trim();
      if (!title) {
        throw new Error("Recipe title is required.");
      }
      const slug = titleToSlug(title);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const result = (await (recipeApiClient as any).recipe.create({
        title,
        slug,
        visibility: "private",
      })) as {
        uuid: string;
        title: string;
        slug: string;
        created_at: number;
        /** Null only if the default-branch insert failed server-side (rare). */
        variationUuid: string | null;
      };
      // Bug F1 (2026-05-03) — pre-populate the just-created default
      // variation so the explorer tree shows it on first paint. The server
      // returns its UUID; the slug/name match the recipe slug at create-time
      // (recipe.create internally creates a default branch; see
      // apps/api/src/do/user-do/recipes.ts → createBranch path). isGoTo is
      // false because no attempt has been promoted yet.
      const initialVariations =
        result.variationUuid != null
          ? [
              {
                uuid: result.variationUuid,
                name: "main",
                cookCount: 0,
                rating: null,
                isGoTo: false,
              },
            ]
          : [];
      const newRecipe: Recipe = {
        uuid: result.uuid,
        // Populate the server-canonical slug at create time. handleCreateRecipe
        // navigates to /app/r/<slug>/edit, so a recipe that is opened, switched
        // away from, then returned to via browser Back lands on its slug-keyed
        // URL. The cross-recipe URL re-resolution effect (resolve by uuid, then
        // by slug) can only match this recipe by slug — omitting it here left
        // the just-created recipe unresolvable until a recipe.list revalidation
        // backfilled the slug (up to 30s later), stranding Back on the prior
        // recipe (WRONG-RECIPE-ON-BACK, cross-recipe-cook-edit.spec.ts step e).
        slug: result.slug,
        title: result.title,
        collectionUuids: params.collectionUuids ?? [],
        variations: initialVariations,
        lastEditedAt: result.created_at ?? Date.now(),
      };
      setRecipes((prev) => {
        const next = [newRecipe, ...prev];
        // Keep recipesRef in lockstep synchronously so create-time logic sees
        // the just-added recipe on the very next call, even if React has not
        // yet committed and run the recipesRef-syncing effect.
        recipesRef.current = next;
        return next;
      });
      // Bug #186 — invalidate the SWR recipe-list cache so the next Palette
      // open fetches fresh data and finds the just-created recipe.
      // `handleCreateRecipe` calls `recipeApiClient.recipe.create()` (the raw
      // oRPC client, not `cachedApi`), so `withCache`'s recipe.create wrapper
      // never fires `fireInvalidate`. Without this call the SWR cache for
      // `recipe.list` remains fresh for up to 30s and the Palette's
      // `loadSnapshot` returns a stale snapshot that omits the new recipe.
      void invalidate(userId, "recipe").catch((err) => {
        console.warn("[proof.cache] invalidate(recipe) after recipe.create failed", { err });
      });
      setCurrentRecipeUuid(result.uuid);
      // Cache the just-created title (miss-proof header source) so a list
      // revalidation that has not yet indexed the new recipe cannot strand the
      // header on the "this recipe" placeholder.
      cacheRecipeTitle(result.uuid, result.title);
      // Wire the sync DO to the new variation's draft doc key so the editor
      // activates immediately after create (doc key format: "draft:<variationUuid>").
      // onVariationSelect in Workspace picks this up via the prop.
      if (result.variationUuid) {
        // currentVariationUuid is derived from currentDocKey (pattern A) — setting
        // currentDocKey is sufficient; no separate setCurrentVariationUuid needed.
        setCurrentDocKey(`draft:${result.variationUuid}`);
      }
      // PR #214 followup — cook is now the default subMode on recipe open, but a
      // just-created recipe has no content to read; the user wants to write it.
      // Navigate to the slug-keyed /edit URL so the Workspace path-watcher lands
      // the user in editor mode. Library taps on existing recipes still default
      // to cook (no /edit segment).
      void navigate(`/app/r/${encodeURIComponent(result.slug)}/edit`);

      // P3 (Pip placement plan §4 P3 / Rev 3.2) — firsts confirmation toast.
      // The recipe is now committed (state + slug-keyed nav queued). Show the
      // new "Recipe created." confirmation (sentence case, period, no
      // exclamation, no banned words — branding.md §5). This is the anchor copy
      // Pip's congratulate sits beside; before this there was NO create toast
      // (create dropped the user straight into the editor). The toast renders on
      // EVERY successful create; Pip's congratulate plays only on the FIRST
      // (the governor's once-ever ledger gates `first-recipe`, not this toast).
      toast.success("Recipe created.");
      // Fire the firsts moment through the governor (via the bridge ref, since
      // this handler is outside the provider subtree — see pipFireRef above).
      // The governor records `first-recipe` once-ever and plays `congratulate`
      // beside the toast in its singleton slot; a second create is a no-op here.
      pipFireRef.current?.("first-recipe");

      // Apply collection memberships in the background after the recipe exists.
      // Failure is non-fatal at the entry point — the recipe is still created;
      // the user can re-edit memberships from the kebab. Log for diagnostics.
      const collectionUuids = params.collectionUuids ?? [];
      if (collectionUuids.length > 0) {
        await Promise.all(
          collectionUuids.map((collectionUuid) =>
            collectionsClient.addMembership(collectionUuid, result.uuid).catch((err: unknown) => {
              console.error({
                event: "recipe_create_membership_failed",
                recipeUuid: result.uuid,
                collectionUuid,
                err,
              });
            }),
          ),
        );
        // Raw client bypasses withCache invalidation (Bug #186 pattern). Drop the
        // batched collection.listAllMembers snapshot (perf §2.C) so the new
        // memberships are not masked by the pre-create cache within the 60 s TTL.
        void invalidate(userId, "collection").catch((err) =>
          console.warn("[proof.cache] invalidate(collection) after create-membership failed", {
            err,
          }),
        );
      }
      return result.uuid;
    },
    [recipeApiClient, collectionsClient, userId, navigate, cacheRecipeTitle],
  );

  // Paste-to-syntax (C-WP5) — the paste-convert wire, memoised once. Defaults to
  // a same-origin oRPC client (matching every other in-app caller).
  const pasteConvert = useMemo(() => createPasteConvert(), []);

  // Called after a successful paste-convert. The server has created the recipe
  // draft-only and seeded the converted body into its Draft; we drop the stale
  // recipe-list cache and open the new recipe's editor.
  //
  // F1 (review): open via `activateRecipe`, NOT a bare setCurrentRecipeUuid. The
  // converted recipe is not in local `recipes` yet, so the URL reconciler would
  // no-op and `currentDocKey` would stay bound to the previously-open recipe
  // (the doc-identity gate then compares stale-to-stale). activateRecipe's
  // branchApi.list fallback exists exactly for this not-in-list case: it fetches
  // the new recipe's default variation and sets `currentDocKey` to its draft key
  // so the seeded Draft actually opens.
  //
  // W4-INTEGRATION: the sibling route returns `{ recipeUuid }` today. If it is
  // enriched to also return `{ variationUuid }`, pass it as
  // `activateRecipe(recipeUuid, { variationUuid })` to skip the branch fetch; the
  // AgentProvenanceChip data rides the same recipe fetch.
  const navigateToConvertedRecipe = useCallback(
    (recipeUuid: string, title: string) => {
      // Seed the miss-proof header title cache BEFORE activation: the converted
      // recipe is not in local `recipes` yet, so activateRecipe's own
      // cacheRecipeTitle lookup misses and the open workspace header would fall
      // back to the "this recipe" placeholder until a recipe.list revalidation
      // lands (dev QA 2026-07-28). The typed title is the value the server
      // stored — the list payload overwrites it if they ever diverge.
      cacheRecipeTitle(recipeUuid, title);
      void invalidate(userId, "recipe").catch((err) => {
        console.warn("[proof.cache] invalidate(recipe) after paste-convert failed", { err });
      });
      activateRecipe(recipeUuid);
      void navigate(`/app/r/${encodeURIComponent(recipeUuid)}/edit`);
      toast.success("Recipe created.");
    },
    [userId, navigate, activateRecipe, cacheRecipeTitle],
  );

  // B-FR-W2 stale-audience fix — Workspace's audience seams await this after a
  // publish/retract succeeds so the attempt-family SWR entries (memory + IDB)
  // are dropped before the editor's busy state clears. `settleInvalidate`
  // already logs-and-swallows per-family failures, so this never rejects into
  // the editor's error path.
  const handleAudienceChanged = useCallback(() => settleInvalidate(userId, ["attempt"]), [userId]);

  // Route-level (library-landing) new-recipe modal: close it, then open the
  // converted recipe.
  const handlePasteConverted = useCallback(
    (recipeUuid: string, title: string) => {
      setNewRecipeModalOpen(false);
      navigateToConvertedRecipe(recipeUuid, title);
    },
    [navigateToConvertedRecipe],
  );

  // P0-7 (Wave 2 — identity): every bare "+ New recipe" affordance opens the
  // route-level RecipeCreationModal so the user types a human name. The prior
  // zero-arg `handleCreateRecipeLegacy` minted an "Untitled recipe N" title
  // with no prompt — retired. `openNewRecipeModal` is the single entry point;
  // the actual create runs on modal submit via `handleCreateRecipe`, whose
  // rejection surfaces inline in the modal (Rule 8).
  const openNewRecipeModal = useCallback(() => {
    setNewRecipeModalOpen(true);
  }, []);

  // ── Change recipe emoji (library page) ────────────────────────────────────
  // Optimistic: update the card glyph immediately, then persist. `null` clears
  // the emoji back to the 🍳 fallback; a string sets it. On failure, revert the
  // optimistic change and re-throw so the modal keeps its inline error visible
  // and stays open (Rule 8 — never silently swallow a mutate failure). The
  // RecipeEmojiModal calls onClose itself on resolve.
  const handleChangeRecipeEmoji = useCallback(
    async (recipeUuid: string, emoji: string | null) => {
      // Snapshot the prior glyph so we can revert exactly on failure.
      let prevEmoji: string | undefined;
      setRecipes((prev) =>
        prev.map((r) => {
          if (r.uuid !== recipeUuid) return r;
          prevEmoji = r.emoji;
          // null clears to the fallback (emoji undefined); a string sets it.
          const next = { ...r };
          if (emoji === null) {
            delete next.emoji;
          } else {
            next.emoji = emoji;
          }
          return next;
        }),
      );
      // Record the optimistic value in the overlay (Bug #1) so a list refetch
      // racing the persist cannot clobber the glyph. Kept until the server
      // payload echoes it (cleared in applyRecipeListPayload) or we revert below.
      pendingEmojiRef.current.set(recipeUuid, emoji);
      try {
        await updateRecipe(recipeApiClient, { uuid: recipeUuid, emoji });
        // Raw client bypasses withCache invalidation (Bug #186 pattern) — drop
        // the cached recipe.list so a later revalidation reflects the new glyph.
        void invalidate(userId, "recipe").catch((err) =>
          console.warn("[proof.cache] invalidate(recipe) after emoji change failed", { err }),
        );
      } catch (err) {
        // Drop the optimistic overlay so a later refetch reflects the truth.
        pendingEmojiRef.current.delete(recipeUuid);
        // Revert the optimistic glyph to its exact prior value.
        setRecipes((prev) =>
          prev.map((r) => {
            if (r.uuid !== recipeUuid) return r;
            const reverted = { ...r };
            if (prevEmoji === undefined) {
              delete reverted.emoji;
            } else {
              reverted.emoji = prevEmoji;
            }
            return reverted;
          }),
        );
        // Re-throw so RecipeEmojiModal surfaces its inline error (Rule 8).
        throw err;
      }
    },
    [recipeApiClient, userId],
  );

  // ── Feedback wiring (§4.9.1) ──────────────────────────────────────────────
  // Wire `onFeedbackSubmit` and `onFeedbackPresignUrl` so the BugIcon appears
  // in the TopBar chrome. Both call the oRPC feedback.* procedures.
  const feedbackApiClient = useMemo(() => createApiClient({ baseUrl: API_BASE_URL }), []);

  const onFeedbackSubmit = useCallback(
    async (data: {
      kind: FeedbackKind;
      body: string;
      screenshotR2Key: string | null;
      metadata: { url: string; ua: string; app_version: string; viewport: string; locale: string };
    }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      await (feedbackApiClient as any).feedback.submit(data);
    },
    [feedbackApiClient],
  );

  const onFeedbackPresignUrl = useCallback(async (): Promise<{
    putUrl: string;
    r2Key: string;
  }> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (feedbackApiClient as any).feedback.presignScreenshotUpload() as Promise<{
      putUrl: string;
      r2Key: string;
    }>;
  }, [feedbackApiClient]);

  // ── B-12: per-recipe export ───────────────────────────────────────────────
  // Calls the oRPC export.obsidianRecipe procedure (Track 4.G) for the active
  // recipe and triggers a browser download of the returned zip.
  // Rule 8: surfaces failure; no bare catch.
  const handleExportRecipe = useCallback(
    async (recipeUuid: string): Promise<void> => {
      // Fetch the full RecipeRow to get the canonical slug.
      const recipeRow = await (cachedApi ?? backend.api).recipe.get(recipeUuid);
      /* eslint-disable @typescript-eslint/no-explicit-any */
      const result = (await (recipeApiClient as any).export.obsidianRecipe({
        recipeSlug: recipeRow.slug,
      })) as {
        queued: boolean;
        filename?: string;
        mimeType?: string;
        data?: string;
      };
      /* eslint-enable @typescript-eslint/no-explicit-any */
      if (result.queued || !result.data || !result.filename) {
        // Queued large-export path: no inline download. Notify user and bail.
        throw new Error("Export queued — check your email when it is ready.");
      }
      const binary = atob(result.data);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: result.mimeType ?? "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = result.filename;
      a.click();
      URL.revokeObjectURL(url);
    },
    [cachedApi, recipeApiClient],
  );

  // Full-library export (Track 4.G) — drives the oRPC library-export procedures
  // (export.obsidianLibrary / export.notionLibrary), each returning
  // { queued, filename?, mimeType?, data?(base64), byteLength }. Mirrors
  // handleExportRecipe's base64 → Blob → download path. On the queued
  // large-export path the procedure returns { queued: true } with no inline
  // data; we throw a brand-voice message so the Workspace Export panel relays it
  // verbatim as a toast (Rule 8 — never a silent close). Both formats reuse this.
  const handleExportLibrary = useCallback(
    async (format: "obsidian" | "notion" | "raw"): Promise<void> => {
      /* eslint-disable @typescript-eslint/no-explicit-any */
      // A-WP2: "raw" → the plain canonical markdown-zip (one go-to body per recipe).
      const proc =
        format === "obsidian"
          ? (recipeApiClient as any).export.obsidianLibrary
          : format === "notion"
            ? (recipeApiClient as any).export.notionLibrary
            : (recipeApiClient as any).export.rawMarkdownLibrary;
      const result = (await proc({})) as {
        queued: boolean;
        filename?: string;
        mimeType?: string;
        data?: string;
        byteLength?: number;
      };
      /* eslint-enable @typescript-eslint/no-explicit-any */
      if (result.queued || !result.data || !result.filename) {
        // Queued large-export path: no inline download. The Export panel relays
        // this message to a toast (branding.md §5 — precise, no exclamation).
        throw new Error("Export queued — check your email when it is ready.");
      }
      const binary = atob(result.data);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: result.mimeType ?? "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = result.filename;
      a.click();
      URL.revokeObjectURL(url);
    },
    [recipeApiClient],
  );

  // ── Hover-prefetch handler (PERF-2.B target #1) ──────────────────────────
  // Fires `cachedApi.recipe.get(recipeUuid)` on sustained card-hover so the
  // SWR cache (packages/client-core/src/cache) is warm by the time the user
  // clicks. The cache layer's own staleTime + in-flight dedup makes the call
  // a no-op when an entry already exists fresh in IDB.
  //
  // Failure policy — best-effort: a failed prefetch has zero user-visible
  // impact (the click's foreground fetch will surface the real error via
  // Rule 8 channels). We still log at console.debug for diagnostics so a
  // systemic prefetch failure does not silently disappear during dev.
  //
  // NOTE — D-NAV migration: when path-based recipe routing lands
  // (`/app/r/<slug>`), this should fire the route loader's data fetch instead
  // of the state-only `recipe.get` call. The FlatRecipeList prop signature
  // stays the same; only this handler's body changes.
  const handlePrefetchRecipe = useCallback(
    (recipeUuid: string, opts?: { warmBody?: boolean }): void => {
      // PERF-3a F4: fire chunk prefetch immediately alongside the data prefetch.
      // The browser dedupes against any in-flight idle-priority download already
      // started by LibraryView's mount effect, so this is a priority upgrade,
      // not a duplicate download.
      prefetchChunksOnIntent();
      // PERF-3a F1: backend is now non-null on first render (createBackend sync),
      // so cachedApi ?? backend.api is always defined.
      const api = cachedApi ?? backend.api;
      // recipe.get warms the metadata SWR family. The open path does not read it
      // directly, but it may warm other surfaces (recipe header, share) — kept as
      // best-effort belt-and-braces.
      void api.recipe.get(recipeUuid).catch((err: unknown) => {
        console.debug({ event: "recipe_prefetch_failed", recipeUuid, err });
      });
      // PERF (PERF-W1 b — recipe-switch wave): the click's foreground body seed
      // is now the SINGLE collapsed open-path call
      //   attempt.list({ branchUuid, limit: 5 })   (app.tsx fetchDraftAttempt)
      // so warm THAT exact SWR key — not recipe.get, which the open path never
      // reads, and no longer the old status:"draft" draft-only key. The library
      // recipe.list payload carries each recipe's variations (incl. the isGoTo
      // marker) inline on recipesRef, so we resolve the go-to branchUuid without
      // a network hop. resolveOpenVariation is the SAME helper activateRecipe
      // uses to set currentDocKey, so the branchUuid we warm here is byte-
      // identical to the currentVariationUuid the click will fetch under. The
      // input object below matches the foreground call field-for-field
      // ({ branchUuid, limit: 5 }), so cacheKey (sorted-key canonical JSON)
      // produces the IDENTICAL key and the 15s-TTL SWR entry dedupes the
      // foreground round-trip. The key-equality unit test in
      // packages/client-core/test/cache/key.test.ts pins this shape.
      //
      // `warmBody` defaults TRUE (hover / pointerdown / focus — a click is
      // imminent, so the 15s TTL will still be live when it lands). The idle
      // top-3 precache opts OUT (`warmBody: false`): its 15s window lapses long
      // before any click, so warming the body there is dead-weight DO load — it
      // keeps its chunk + recipe.get-only behaviour.
      const warmBody = opts?.warmBody ?? true;
      if (!warmBody) return;
      // On a miss (variations not yet loaded → undefined) we skip; the click's
      // branch.list fallback covers that path and there is nothing to warm.
      const recipe = recipesRef.current.find((r) => r.uuid === recipeUuid);
      const variation = recipe ? resolveOpenVariation(recipe) : undefined;
      if (variation) {
        void api.attempt.list({ branchUuid: variation.uuid, limit: 5 }).catch((err: unknown) => {
          console.debug({ event: "attempt_prefetch_failed", recipeUuid, err });
        });
      }
    },
    [backend, cachedApi],
  );

  // ── Auth client for sign-out ──────────────────────────────────────────────
  const authClient = useMemo(() => createAuthClient({ baseUrl: "" }), []);

  // ── onSignOut — sign the user out and show the sign-in gate ─────────────────
  // branding.md §5: on error, surface a quiet message; stay on Workspace.
  // Wired into SettingsView via Workspace.onSignOut.
  //
  // After a successful logout the cookie is cleared by the server. Rather than
  // navigating to "/" (which the root index route immediately redirects back to
  // /app), we call onSignedOut() so AppRouteImpl flips its local session state
  // to `unauthenticated` — React re-renders with SignInGate while the URL stays
  // at /app. This avoids the redirect loop and is consistent with the
  // handleSessionEstablished pattern (sign-in calls fetchSession → setSession).
  const handleSignOut = useCallback(async (): Promise<void> => {
    try {
      // Drop every cached read for this user BEFORE clearing the auth state.
      // We still have the userId here so the prefix-based invalidation can
      // target the right namespace; after onSignedOut() the userId is gone
      // from this component's props on the next render.
      // Best-effort: a cache-invalidation failure must not block sign-out.
      try {
        await invalidateAll(userId);
      } catch (cacheErr) {
        console.warn("[proof.cache] sign-out invalidation failed", { cacheErr });
      }
      // Purge the Yjs IDB cache so the next user on this device cannot see
      // this user's draft content (privacy invariant — architecture §4.5).
      try {
        await purgeWebYjsCache();
      } catch (yjsErr) {
        console.warn("[proof.yjs] sign-out cache purge failed", { yjsErr });
      }
      // Purge the body-snapshot fast-paint cache (Phase 1.C). Its namespace
      // (proof.ydoc-snapshot.v1.*) is deliberately outside both the RPC-cache
      // prefix and the proof-yjs DB, so neither invalidateAll(userId) above nor
      // purgeWebYjsCache() clears it — without this the next user on this device
      // would see this user's recipe body. Best-effort: a purge failure must not
      // block sign-out (Rule 8: logged, not swallowed).
      try {
        await purgeAllSnapshots();
      } catch (snapErr) {
        console.warn("[proof.snapshot] sign-out purge failed", { snapErr });
      }
      // Drop any in-flight Findings aggregate promise: its dedupe map is module-
      // scoped and outlives the account, so an owner-A read still pending at
      // sign-out must not resolve into the next account's UI. Synchronous + total
      // (no per-user key), so no try/catch needed.
      resetFindingsAggregateCache();
      // Clear the session cache BEFORE authClient.logout() so that other tabs
      // receive the storage event while the key is still being cleared (the
      // storage event fires synchronously on removeItem in other tabs).
      clearSessionCache();
      await authClient.logout();
      onSignedOut();
    } catch (err) {
      console.error({ event: "sign_out_failed", err });
      // Propagate so Workspace can surface a toast.
      throw err;
    }
  }, [authClient, onSignedOut, userId]);

  // ── onUpdateBio — update BetterAuth user.bio ─────────────────────────────────
  // PR9: maps to POST /api/auth/update-user { bio }.
  // BetterAuth update-user supports arbitrary user-table fields declared in
  // additionalFields (see apps/api/src/auth.ts). The `bio` field is added by
  // migration 0009_user_bio.sql and must be registered as an additionalField.
  // Rule 8: throws on non-2xx so Workspace can surface a toast.
  //
  // F5 (wave 2 follow-up to PR #109) — refreshSession() inventory:
  //   `bio` is NOT a member of SessionState (see SessionState type above —
  //   only userId, email, name, onboardingCompletedAt, handle are tracked).
  //   Bio is fetched independently by Workspace into its own settings-account
  //   state. No UI surface in this tree reads bio from the session payload,
  //   so calling onSessionRefresh() here would re-fetch bytes that nothing
  //   in SessionState consumes. Skip per the F5 wiring criterion (mutation
  //   needs refresh only when a UI surface reads the field from session).
  //   If bio is later added to SessionState (e.g. for user-menu display),
  //   wire onSessionRefresh() here at the same time.
  const handleUpdateBio = useCallback(async (newBio: string): Promise<void> => {
    const csrfToken = getCsrfHeader();
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }
    const res = await fetch(`${API_BASE_URL}/api/auth/update-user`, {
      method: "POST",
      credentials: "include",
      headers,
      body: JSON.stringify({ bio: newBio }),
    });
    if (!res.ok) {
      const errText = await res.text().catch(() => String(res.status));
      throw new Error(`Bio update failed: ${errText}`);
    }
  }, []);

  // ── onUpdateHandle — update BetterAuth display name (user.name) ─────────────
  // Maps to POST /api/auth/update-user { name }.
  // Per O1: the "Handle" field in AccountSection maps to BetterAuth user.name,
  // not the public-slug handles.handle.
  // Rule 8: throws on non-2xx so Workspace can surface a toast.
  // PH2 (2026-05-03): on success, refresh the parent SessionState so the
  // user-menu trigger label updates within the same render cycle. Without this
  // the cached `name` (and `handle`) read at sign-in stays stale until reload.
  const handleUpdateHandle = useCallback(
    async (newName: string): Promise<void> => {
      const csrfToken = getCsrfHeader();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (csrfToken) {
        headers["X-CSRF-Token"] = csrfToken;
      }
      const res = await fetch(`${API_BASE_URL}/api/auth/update-user`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify({ name: newName }),
      });
      if (!res.ok) {
        const errText = await res.text().catch(() => String(res.status));
        throw new Error(`Update failed: ${errText}`);
      }
      // Invalidate cached session — the menu trigger reads `accountName` from
      // SessionState. Fire-and-forget; the refresh is best-effort and the
      // mutation has already succeeded server-side.
      onSessionRefresh();
    },
    [onSessionRefresh],
  );

  // ── onChangePassword — change password via BetterAuth ─────────────────────
  // Maps to POST /api/auth/change-password { currentPassword, newPassword }.
  // Rule 8: throws on non-2xx so Workspace can surface a toast.
  //
  // F5 (wave 2 follow-up to PR #109) — refreshSession() inventory:
  //   Password change writes the credentials row, not the user record. No
  //   field on SessionState changes; no UI surface needs invalidation. Skip.
  const handleChangePassword = useCallback(
    async (currentPassword: string, newPassword: string): Promise<void> => {
      const csrfToken = getCsrfHeader();
      const headers: Record<string, string> = {
        "Content-Type": "application/json",
      };
      if (csrfToken) {
        headers["X-CSRF-Token"] = csrfToken;
      }
      const res = await fetch(`${API_BASE_URL}/api/auth/change-password`, {
        method: "POST",
        credentials: "include",
        headers,
        body: JSON.stringify({ currentPassword, newPassword }),
      });
      if (!res.ok) {
        const errText = await res.text().catch(() => String(res.status));
        throw new Error(`Password change failed: ${errText}`);
      }
    },
    [],
  );

  // ── onDeletePreview — fetch deletion content counts ────────────────────────
  // Wave 4 / Track 11 (P0-9, GDPR Art.17). Calls account.delete.preview so the
  // confirmation modal shows what will be removed before the user commits. The
  // backend returns zeroes (never throws) if the UserDO is unreachable, so this
  // surfaces counts on a best-effort basis.
  const handleDeletePreview = useCallback(async (): Promise<{
    recipeCount: number;
    attemptCount: number;
    artefactCount: number;
    batchCount: number;
    mediaCount: number;
  }> => {
    return recipeApiClient.account.delete.preview();
  }, [recipeApiClient]);

  // ── onDeleteAccount — initiate account deletion (30-day grace) ─────────────
  // Wave 4 / Track 11 (P0-9, GDPR Art.17). Calls account.delete.initiate with
  // the re-auth password. The server sets pending_deletion_at and revokes ALL
  // sessions, so on success the user is effectively signed out: clear the local
  // cache + session state and flip the app to the signed-out gate, mirroring
  // handleSignOut. The user can cancel deletion by logging back in within 30 days.
  //
  // OAuth-only accounts: only emailAndPassword signup is enabled at MVP
  // (apps/api/src/auth.ts has no socialProviders block), so every account has a
  // password and the re-auth path always applies. If OAuth signup is added later,
  // the server returns UNAUTHORIZED "Password verification failed." for the
  // no-credential case (apps/api/src/routes/account/delete.ts), which the modal
  // surfaces as a password error — wire a dedicated "set a password first"
  // message here at that point.
  //
  // Rule 8: on failure this throws so AccountDeleteModal renders its inline
  // role="alert" error rather than closing silently.
  const handleDeleteAccount = useCallback(
    async (password: string): Promise<void> => {
      await recipeApiClient.account.delete.initiate({ password });
      // Deletion initiated: server revoked every session. Tear down local state
      // and route to the signed-out gate (best-effort cache clear must not block).
      try {
        await invalidateAll(userId);
      } catch (cacheErr) {
        console.warn("[proof.cache] account-deletion invalidation failed", { cacheErr });
      }
      clearSessionCache();
      onSignedOut();
    },
    [recipeApiClient, userId, onSignedOut],
  );

  // ── onSaveMemberships — save recipe→collection assignments ────────────────
  // Followup §2.2 P0 (A.1) — prop signature is now Promise<void>. The modal
  // (EditCollectionsModal via FlatRecipeList) awaits this before closing; on
  // rejection it surfaces the inline error and stays open (Rule 8).
  // collectionsClient is stable (useMemo with []).
  const handleSaveMemberships = useCallback(
    async (recipeUuid: string, newCollectionUuids: string[]): Promise<void> => {
      const data = await collectionsClient.listCollectionsForRecipe(recipeUuid);
      const currentSet = new Set(data.members.map((m) => m.collection_uuid));
      const newSet = new Set(newCollectionUuids);
      const toAdd = newCollectionUuids.filter((uuid) => !currentSet.has(uuid));
      const toRemove = data.members
        .filter((m) => !newSet.has(m.collection_uuid))
        .map((m) => m.collection_uuid);
      await Promise.all([
        ...toAdd.map((uuid) => collectionsClient.addMembership(uuid, recipeUuid)),
        ...toRemove.map((uuid) => collectionsClient.removeMembership(uuid, recipeUuid)),
      ]);
      // On success: optimistically update the in-memory recipes array so the
      // UI reflects the new memberships immediately.
      setRecipes((prev) =>
        prev.map((r) =>
          r.uuid === recipeUuid ? { ...r, collectionUuids: newCollectionUuids } : r,
        ),
      );
      // Raw client bypasses withCache invalidation (Bug #186 pattern). Drop the
      // batched collection.listAllMembers snapshot (perf §2.C) so a remount
      // within the 60 s TTL cannot re-serve the pre-edit memberships and revert
      // the user's change.
      void invalidate(userId, "collection").catch((err) =>
        console.warn("[proof.cache] invalidate(collection) after save-memberships failed", { err }),
      );
    },
    [collectionsClient, userId],
  );
  // ── commitAttempt — wraps backend.commitAttempt with in-flight tracking ───
  // Wave 3 R-2: no longer passed to Workspace (SpeedLog/startCook removed).
  // Kept for future cook-flow re-wire (Q1 wishlist). cookCount bump + cache
  // invalidation logic preserved for when it's re-wired.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _commitAttempt = useCallback(
    async (input: Parameters<NonNullable<Backend["commitAttempt"]>>[0]) => {
      setAttemptInFlight(true);
      try {
        const result = await backend.commitAttempt(input);
        // commit-ack arrived — drop the read-side cache for attempts and
        // recipes so the next list reflects the new attempt + cookCount
        // without serving the stale pre-commit slice. Best-effort,
        // fire-and-forget. Ref: cache/with-cache.ts.
        void invalidate(userId, "attempt").catch((err) =>
          console.warn("[proof.cache] invalidate(attempt) after commit failed", { err }),
        );
        void invalidate(userId, "recipe").catch((err) =>
          console.warn("[proof.cache] invalidate(recipe) after commit failed", { err }),
        );
        // commit-ack arrived — bump the cookCount for the committed variation.
        // currentVariationUuid is captured via closure; it identifies which
        // variation in the explorer tree just gained a new attempt.
        if (currentVariationUuid) {
          const varUuid = currentVariationUuid;
          setRecipes((prev) =>
            prev.map((r) => ({
              ...r,
              variations: r.variations.map((v) =>
                v.uuid === varUuid ? { ...v, cookCount: v.cookCount + 1 } : v,
              ),
            })),
          );
        }
        return result;
      } finally {
        setAttemptInFlight(false);
      }
    },
    [backend, currentVariationUuid, userId],
  );

  // Wave 3 R-2: fetchInProgress, handleInProgressMarkComplete, handleInProgressNavigate,
  // and currentInProgressRow all removed — the in_progress status value is gone from
  // the 4-value union. The InProgressSection and InProgressStrip components are
  // deleted. (QA 2026-06-11 bug A7: this comment used to claim the attempt
  // status was "sourced from the attempt fetch per recipe" while no code did
  // so — the status now genuinely travels with the uuid in `currentAttempt`,
  // set by the BUG-01 fetch effect above.)

  // ── lineage — derive upstream spin-off info from in-memory recipes ──────────
  // Resolves only when the active recipe is a spin-off (has fork_upstream_recipe_uuid)
  // AND the upstream recipe exists in the user's own library. Cross-library
  // spin-offs (upstream belongs to another user) are not resolved in-memory —
  // lineage is null for those cases at MVP (no extra RPC call).
  const lineage = useMemo<
    { upstreamHandle: string; upstreamSlug: string; upstreamPublicUuid: string } | null | undefined
  >(() => {
    if (!currentRecipeUuid) return undefined;
    const activeRecipe = recipes.find((r) => r.uuid === currentRecipeUuid);
    if (!activeRecipe?.fork_upstream_recipe_uuid) return undefined;
    const parent = recipes.find((r) => r.uuid === activeRecipe.fork_upstream_recipe_uuid);
    if (!parent) return null; // orphaned spin-off — upstream not in this user's library
    return {
      upstreamHandle: "", // handle not available from recipe.list; surface in banner as slug-only
      upstreamSlug: parent.slug ?? parent.title,
      upstreamPublicUuid: parent.uuid,
    };
  }, [currentRecipeUuid, recipes]);

  // ── unresolvedRefs — @consumes annotations that don't resolve in user's library ─
  // Built by parsing the active recipe draft body. Only populated when the active
  // recipe is a spin-off (has fork_upstream_recipe_uuid). existingOptions is always
  // empty at MVP — no artefact-library lookup is wired yet.
  const unresolvedRefs = useMemo<UnresolvedRef[] | undefined>(() => {
    if (!currentRecipeUuid) return undefined;
    const activeRecipe = recipes.find((r) => r.uuid === currentRecipeUuid);
    if (!activeRecipe?.fork_upstream_recipe_uuid) return undefined;
    // COOK-FIRST-PAINT (2026-06-16): parse the identity-gated body so @consumes
    // are never resolved from a stale (other-variation) body during a switch.
    if (!recipeContentForActive) return undefined;
    const entries = parseConsumes(recipeContentForActive);
    if (entries.length === 0) return undefined;
    return entries.map((entry) => ({
      upstreamName: entry.name,
      kind: entry.kind === "unknown" ? "artefact" : entry.kind,
      existingOptions: [],
    }));
  }, [currentRecipeUuid, recipes, recipeContentForActive]);

  // ── handleSpinOffSuccess — navigate to the new recipe after a spin-off ──────
  // Previously only called setCurrentRecipeUuid but never setCurrentDocKey —
  // the editor was left bound to the previous recipe's doc ("wrong body" on
  // spin-off). Route through activateRecipe so the doc key is always resolved.
  const handleSpinOffSuccess = useCallback(
    (newRecipeUuid: string) => {
      activateRecipe(newRecipeUuid);
      // D-NAV: push the URL so refresh opens the spun-off recipe.
      void navigate(`/app/r/${encodeURIComponent(newRecipeUuid)}`);
    },
    [activateRecipe, navigate],
  );

  // ── Beat-0 entry fork — "Open a worked example" (onboarding revival T3) ──────
  // Adopts the napoletana seed via recipe.spinOff({ sourcePublicUuid }) and opens
  // it in the workspace. Rule 8: this handler does NOT swallow — it re-throws so
  // the empty-state's WorkedExampleAction surfaces a visible inline error (it
  // owns the busy state + withTimeout). On success it reuses the GOVERNED
  // first-recipe Pip moment + "Recipe created." toast (plan adopted decision:
  // the worked example consumes that governed moment; NO new Pip placement).
  const handleOpenWorkedExample = useCallback(async () => {
    // Retry-safe seed adoption. WorkedExampleAction bounds the spin-off with a
    // 15s client timeout; if the server landed the adoption while the client
    // timed out, a naive retry would mint a SECOND slug-deduped copy.
    // adoptWorkedExample does one cheap raw list read first and opens an existing
    // seed copy (fork_upstream_recipe_uuid === the seed's public UUID) instead of
    // re-adopting. The spin-off failure (first-run path) still propagates so the
    // empty-state surfaces it (Rule 8). Raw oRPC client (createApiClient); the
    // seed path uses `sourcePublicUuid`, resolved against the D1 index server-side.
    const { uuid, created } = await adoptWorkedExample(
      recipeApiClient as unknown as WorkedExampleRecipeClient,
    );

    // Navigate into the recipe (same path as any spin-off success).
    handleSpinOffSuccess(uuid);

    // Governed first-recipe celebration — same anchor moment as a fresh create
    // (the governor's once-ever ledger gates the congratulate). The TOAST copy
    // branches on the adoption outcome so a reopened existing copy (the double-
    // copy guard) never falsely claims a fresh recipe was created.
    toast.success(workedExampleToastMessage(created));
    pipFireRef.current?.("first-recipe");

    // Refresh the SWR recipe-list cache so the seed appears in the library on
    // return. Non-fatal — the workspace already opened the recipe by uuid.
    void invalidate(userId, "recipe").catch((err) =>
      console.warn("[proof.cache] invalidate(recipe) after worked-example spin-off failed", {
        err,
      }),
    );
  }, [recipeApiClient, handleSpinOffSuccess, userId]);

  // ── Guide completion stamp (onboarding revival T2) ──────────────────────────
  // Fired by useGuide when the guide reaches a terminal state (all four beats
  // done, or dismissed). Stamps onboarding completion cross-device. Non-fatal:
  // the per-device ledger already suppresses locally, so a failed stamp only
  // means the guide may reappear on another device — logged, never surfaced.
  const completeOnboardingStamp = useCallback(async () => {
    try {
      await recipeApiClient.account.completeOnboarding({});
    } catch (err) {
      console.error({ event: "guide_complete_onboarding_failed", err });
    }
  }, [recipeApiClient]);

  // ── Library-landing palette activation handler (P1 fix 2026-05-09) ──────────
  //
  // Called when the user selects a result from the route-level <Palette> that
  // mounts on the Library landing route (currentRecipeUuid === null). The
  // Workspace owns its own handler for the recipe-open state.
  //
  // Recipe result → activate the recipe (mirrors the LibraryView's
  //   onActivateRecipe path: setCurrentRecipeUuid + setCurrentDocKey to the
  //   go-to variation's draft key so the editor opens immediately).
  // Artefact / batch → no dedicated route at MVP; close palette is enough
  //   (the palette closes itself in handleActivate before invoking this).
  // Variations are not a palette kind (2026-07-10 versions-first cleanup).
  function handlePaletteActivateLibrary(result: PaletteResult) {
    switch (result.kind) {
      case "recipe": {
        // Route through the shared activateRecipe callback so go-to logic,
        // race guard, and Rule 8 logging are centralised.
        activateRecipe(result.uuid);
        // D-NAV: push the URL so refresh opens this recipe.
        void navigate(`/app/r/${encodeURIComponent(result.uuid)}`);
        break;
      }
      // artefact / batch: no dedicated route at MVP — palette has already
      // closed itself, nothing further to do here.
      default:
        break;
    }
  }

  // ── Library-landing DesktopTopbar + UserMenu derivation ──────────────────────
  // Tablet-collapse wave: DesktopTopbar renders at ≥768 px; the legacy library
  // TopBar is retired. The Workspace-internal DesktopTopbar is hidden while
  // `display:none`; this route-level mount is the only topbar on the Library
  // landing. design.md §6.0a — discoverability on every route.
  //
  // Priority: @handle → display name → email local-part. Mirrors Workspace.tsx:1844.
  const libraryMenuLabel =
    handle != null || name != null || email
      ? deriveMenuLabel(handle ?? null, name ?? null, email)
      : undefined;

  // Extracted to a const so wrapping it in <GuideProvider> below adds no
  // indentation level to the ~880-line shell JSX (keeps the app.tsx diff minimal
  // — another wave owns larger app.tsx changes; plan 2026-07-02 T2).
  const appShell = (
    <div className="proof-app-route" data-testid="app-route">
      {/* Pip governor mount (plan §3 / Rev 3.3). The PipMomentsProvider is the
          SINGLETON moment-Pip owner — mounted ONCE here, wrapping the whole
          authenticated shell so every placement (the explorer list-end greet,
          the firsts congratulate, the mutate-failure settle, the go-to promote,
          the five-star tumble) can call usePipMoments().fire(<moment>). Its
          single render slot sits beside the sonner Toaster below; nothing else
          mounts an animating <PipMascot>. P1 (empty state) and P7 (404) are
          standalone idle renders and intentionally do NOT route through here. */}
      <PipMomentsProvider>
        {/* P3 bridge (Pip plan §4 P3) — populates pipFireRef with the governor's
          `fire` so handleCreateRecipe (declared outside this provider subtree)
          can play the first-recipe congratulate. Renders nothing; the governor
          still owns the once-ever ledger + the collision rule. */}
        <PipMomentsBridge fireRef={pipFireRef} />

        {/* RULE8-1: the app's single toast surface (sonner, wrapped in
          AppToaster so the palette is token-driven rather than sonner's own —
          design.md §4.2). Required for toast.error() calls in
          NewVersionSheet.tsx to surface to the user.
          Without this mount, toast.error() is a silent no-op (Rule 8 violation).
          `top-center` is mobile-friendly (above content, never behind the virtual keyboard).
          Renders into a portal; has no children. */}
        <AppToaster />

        {/* P0-7 (Wave 2 — identity): route-level new-recipe modal. Mounted as a
          top-level sibling of the LibraryView / Workspace shells (never inside a
          display:none / aria-hidden subtree) so every "New recipe" affordance —
          the LibraryView empty-state CTA and the title-row "New recipe" ghost
          control (contextual creation, nav-chrome-contextual-create) — opens it
          and the user always types a
          human name. `RecipeCreationModal` requires a non-empty name and throws
          on create failure, surfacing the error inline (Rule 8). The modal is
          responsive on its own (full-screen sheet <768px; 420px ≥768px). */}
        {newRecipeModalOpen && (
          <RecipeCreationModal
            mode="new"
            collections={collections}
            existingRecipeTitles={recipes.map((r) => r.title)}
            onCreate={async ({ title, collectionUuids }) => {
              const uuid = await handleCreateRecipe({ title, collectionUuids });
              setNewRecipeModalOpen(false);
              return uuid;
            }}
            onClose={() => setNewRecipeModalOpen(false)}
            // Paste-to-syntax (C-WP5) — adds the "Paste a recipe" sub-mode.
            pasteConvert={pasteConvert}
            onPasteConverted={handlePasteConverted}
          />
        )}

        {/* Change-recipe-emoji modal (library page). Mounted as a top-level
          sibling (never inside the display:none Workspace / LibraryView shells)
          so its overlay + focus trap are not hidden. The emoji picker inside is
          itself React.lazy-loaded, so opening this modal is the only path that
          fetches the frimousse chunk + the Emojibase dataset. Persist failures
          keep the modal open with an inline error (Rule 8). */}
        {emojiModalRecipeUuid !== null &&
          (() => {
            const target = recipes.find((r) => r.uuid === emojiModalRecipeUuid);
            if (!target) return null;
            return (
              <RecipeEmojiModal
                recipeUuid={target.uuid}
                recipeTitle={target.title}
                {...(target.emoji !== undefined ? { currentEmoji: target.emoji } : {})}
                onChange={(emoji) => handleChangeRecipeEmoji(target.uuid, emoji)}
                onClose={() => setEmojiModalRecipeUuid(null)}
              />
            );
          })()}

        {/* Wave 2 (onboarding-empty-r1): Guided wizard removed — wishlisted per
          `.plan/wishlist/onboarding-wizard.md`. New-user landing is the
          EmptyLibraryState rendered by LibraryView (Frame 21). */}

        {/* NavTray (nav-shell R1) — the SINGLE unified navigation surface,
          mounted ONCE at the route level, outside the Workspace's display:none
          subtree, so it is reachable from BOTH the Library-landing top bar and
          the Workspace-internal top bar (the "two avatars, one fix" P0 history —
          PRs #165/#170/#173 — is structurally closed: one tray, one mount).

          Desktop (≥1024px): docked open as the left column of the flex-row app
          shell (see APP_ROUTE_CSS). Mobile / tablet: a full-screen overlay
          summoned by the top-bar hamburger (`user-menu-trigger`), whose
          open-state is the lifted `userMenuOpen`. Every row target mirrors the
          retired UserMenu verbatim; `onSignOut` re-throws on failure so the
          caller surfaces it (Rule 8). */}
        {libraryMenuLabel !== undefined && (
          <NavTray
            open={userMenuOpen}
            onOpenChange={setUserMenuOpen}
            firstName={libraryMenuLabel}
            email={email}
            avatarInitials={libraryMenuLabel.slice(0, 2).toUpperCase()}
            activeKey={deriveNavTrayActiveKey(location.pathname)}
            // Desktop mini-rail (nav-shell R1 collapse): when a recipe is open the
            // docked tray collapses to the 48px icon rail (recipe-view density);
            // the browse surfaces (currentRecipeUuid === null) keep the full docked
            // tray. Ignored below 1024px — mobile stays the summoned overlay.
            collapsed={currentRecipeUuid !== null}
            onLibrary={() => {
              // The "Library" nav row opens the FULL library grid at /app/library
              // (dashboard/library split, 2026-07-11 — the bare /app is now the
              // dashboard HOME, reached via the wordmark). Clear the
              // settings-from-library visibility override (not URL-routed). When
              // a recipe is open, `/app/r/:slug → /app/library` crosses top-level
              // route objects, so AppRoute REMOUNTS with fresh state — the remount
              // clears the open recipe (not the close-reconciliation effect).
              setWorkspaceVisibleOverride(false);
              void navigate("/app/library");
            }}
            onFeed={() => {
              // B-WP11b: the Feed (/app/feed). Renders in-shell (shell-persistence
              // fix). Clear the Settings/Export override so the destination — not a
              // lingering Workspace panel — is what shows.
              setWorkspaceVisibleOverride(false);
              void navigate("/app/feed");
            }}
            onDiscover={() => {
              // B-WP9: Discover (/app/discover). Renders in-shell (shell-persistence
              // fix) — the SAME DiscoverBoards the public /discover page renders, so
              // the tray stays mounted (no hop to the public standalone page).
              setWorkspaceVisibleOverride(false);
              void navigate("/app/discover");
            }}
            onCooks={() => {
              // A-WP9: the Cooks ledger (/app/ledger). Renders in-shell.
              setWorkspaceVisibleOverride(false);
              void navigate("/app/ledger");
            }}
            onGroups={() => {
              // family-groups (plan 2026-07-02 §4.5): the Groups index (/app/g).
              // Renders in-shell.
              setWorkspaceVisibleOverride(false);
              void navigate("/app/g");
            }}
            onFriends={openFriendsFromNav}
            onProfile={() => {
              // Profile: public page when a handle exists; otherwise Settings →
              // Account (where the handle is set up), via the same visibility
              // override Settings uses.
              if (handle != null) {
                void navigate(`/u/${encodeURIComponent(handle)}`);
                return;
              }
              openSettingsFromAccountMenu();
            }}
            onSettings={openSettingsFromAccountMenu}
            onExports={() => {
              setWorkspaceVisibleOverride(true);
              exportsOpenerRef.current?.();
            }}
            onCustomLogic={() => {
              // In-shell (shell-persistence fix). Clear the override so the
              // editor — not a lingering Workspace panel — is what shows.
              setWorkspaceVisibleOverride(false);
              void navigate("/app/settings/custom-logic");
            }}
            onHelp={() => {
              // In-shell (shell-persistence fix).
              setWorkspaceVisibleOverride(false);
              void navigate("/app/help");
            }}
            onFeedback={() => {
              setFeedbackModalOpen(true);
            }}
            onSignOut={() => {
              // Rule 8: handleSignOut re-throws on failure so the caller (here)
              // must handle the rejection. The initiating surface has already
              // been replaced (the route flips to SignInGate via onSignedOut),
              // so there is nowhere to render a toast — log the stack.
              handleSignOut().catch((err: unknown) => {
                console.error({ event: "route_nav_tray_sign_out_failed", err });
              });
            }}
            onAdmin={() => navigate("/admin")}
          />
        )}

        {/* Content column — everything to the right of the docked NavTray on
          desktop (the top bar + Library view + Workspace). On mobile / tablet
          this is the full-width shell and the NavTray is an overlay above it. */}
        <div className="proof-app-content">
          {/* Library-landing top bar — the unified DesktopTopbar on EVERY breakpoint
          (nav-chrome-contextual-create). The mobile bottom capsule is gone; this
          bar carries the Library landing chrome (Search · Groups · Account) at
          375 px too, so Settings / Sign out / Profile / Exports stay reachable on
          every breakpoint (design.md §6.0a discoverability). There is NO Create
          control — new recipes come from the LibraryView title-row "New recipe"
          ghost control (contextual creation). Groups + the "Library" title are
          hidden <768 px by DesktopTopbar's own CSS (Rule 7 density); Groups stays
          reachable from the Account menu's Groups row.
          Workspace is `display:none` when no recipe is open so the route-level
          mount is the only DesktopTopbar in the tree on the Library landing. The
          two instances (this + Workspace-internal) are mutually exclusive on
          `currentRecipeUuid` — no double-bar regression.
          branding.md §5.2.1 — no banned words. */}
          {/* P0-14 (Wave 3): also gate on `!workspaceVisibleOverride`. When Settings
          (or another Workspace-mounted panel) is opened from the Library
          landing, `setWorkspaceVisibleOverride(true)` reveals the Workspace
          shell — which mounts its OWN DesktopTopbar (settings-mode, carrying the
          labelled "Library" escape per Bug #221). Without this guard BOTH
          topbars render → two competing chrome rows. The Workspace topbar is the
          single chrome row in that state and preserves the Library escape
          (design.md §6.0a). */}
          {libraryMenuLabel !== undefined &&
            currentRecipeUuid === null &&
            !workspaceVisibleOverride && (
              <DesktopTopbar
                // dashboard/library split (2026-07-11): the "Library" section
                // title shows only on the full library page (/app/library). The
                // bare /app dashboard's own greeting is its title, so the topbar
                // carries no section title there.
                {...(libraryRouteActive ? { title: "Library" } : {})}
                // The wordmark is the HOME affordance → /app (the dashboard), not
                // the library page. Wiring onLibrary turns the wordmark into a
                // labelled button; on the dashboard this is a harmless self-nav,
                // on /app/library it returns to the dashboard.
                onLibrary={() => {
                  void navigate("/app");
                }}
                // nav-shell R1: the destinations + account avatar moved into the
                // NavTray. On mobile / tablet this hamburger summons the tray
                // overlay; on desktop the tray is docked open and the control is
                // CSS-hidden. Both this bar and the Workspace-internal bar toggle
                // the SAME lifted `userMenuOpen` state, so the single route-level
                // <NavTray> is reachable from every surface (the "two avatars, one
                // fix" footgun is gone — there is one summon control per surface,
                // one tray).
                onMenu={() => setUserMenuOpen((v) => !v)}
                menuExpanded={userMenuOpen}
              />
            )}

          {/* The route-level UserMenu is retired (nav-shell R1) — the single
          NavTray above now owns every account + navigation destination on every
          breakpoint. It is the ONE mount, reachable from both the Library-landing
          top bar and the Workspace-internal top bar (see the NavTray block for
          the "two avatars, one fix" P0 history it structurally closes). */}

          {/* TopBar retired — tablet-collapse wave. DesktopTopbar (≥768 px,
          !isMobileViewport) is now the only chrome on every viewport above
          mobile. The legacy library-landing TopBar block is removed.
          Plan ref: .plan/features/2026-05-08-redesign-implementation-wave-plan.md §9 */}

          {/* P3.LIBRARY — /app lands on LibraryView when no recipe is open.
          L6 (Library is the landing route), D-LAND, A.3.3 (gap doc).
          When a recipe is opened (currentRecipeUuid !== null), the Workspace
          renders below — LibraryView is hidden so state is preserved during
          the Library → recipe transition. */}
          {/*
           * Bug A fix: was `display: contents` when visible, which collapses the
           * wrapper's principal box and leaves LibraryView without a definite
           * containing block for its `height: 100%`. The fix changes the visible
           * state to `display: flex; flex: 1 1 0; min-height: 0` so the wrapper
           * becomes a proper flex child of `.proof-app-route` (height: 100dvh,
           * flex-direction: column) and gives LibraryView a definite height to
           * fill. The hidden state uses `display: none` as before.
           *
           * H7 (2026-05-09): the Library route scrolls as ONE continuous document.
           * The shell owns the page-level scroll (overflowY: auto) so the
           * InProgressSection at the top scrolls up and out of view as the user
           * scrolls down through the recipe list — instead of being pinned while
           * an inner scroller engages independently. overflowX is clamped to
           * prevent horizontal jitter from descendant content. The
           * --capsule-clearance padding-bottom (defined in APP_ROUTE_CSS below,
           * retuned to a small safe-area gap now the floating capsule is gone)
           * still applies on this wrapper as the bottom breathing inset at the end
           * of the page-level scroll.
           *
           * Bug A (2026-06-09): the visible state is `display: block`, NOT flex.
           * Chromium does not include a FLEX container's padding-bottom in reachable
           * scroll space, so as a flex box the last recipe card scrolled behind the
           * bottom inset. As a block the padding-bottom is honoured. `flex: 1 1 0`
           * still governs this element AS a flex child of .proof-app-route.
           */}
          <div
            className="proof-library-view-shell"
            style={
              // Bug #221 fix (2026-05-23): when the route-level UserMenu lifts
              // `workspaceVisibleOverride` to surface a Workspace-mounted panel
              // (Settings / Feedback / Exports) on the Library landing, ALSO
              // hide the LibraryView so the Workspace's full-viewport panel is
              // the only thing the user sees. Otherwise both render as flex
              // children of `.proof-app-route` and the layout collapses.
              currentRecipeUuid === null && !workspaceVisibleOverride && !isSecondaryView
                ? {
                    // display:block (not flex) — see the Bug A note above. A flex
                    // scroll container drops padding-bottom from reachable scroll
                    // space; block honours it so the bottom inset is reachable.
                    display: "block",
                    flex: "1 1 0",
                    minHeight: 0,
                    width: "100%",
                    overflowY: "auto",
                    overflowX: "hidden",
                  }
                : { display: "none" }
            }
            aria-hidden={currentRecipeUuid !== null || workspaceVisibleOverride || isSecondaryView}
            data-testid="library-view-shell"
          >
            {/* Perf (Wave PERF — hidden-surface damping): while a recipe is open the
                shell is display:none BEHIND the workspace, yet its subtree stayed
                mounted and re-rendered with the 24-useState app god-component on every
                (now-damped) keystroke. Render null in that state so the subtree stops
                paying render cost it can never show.

                `currentRecipeUuid !== null` is the exact recipe-open condition. In that
                state the live branch below is ALWAYS DashboardHero — `libraryRouteActive`
                is false on `/app/r/:slug`, so LibraryView is never the hidden subtree
                here. It is not kept alive behind the workspace either: this branch
                returns null while a recipe is open, so LibraryView mounts fresh on close
                regardless of the route table (see the corrected libraryRouteActive note
                above for the measured remount facts).

                DashboardHero holds no state that must survive an open recipe — its
                friend-strip DATA is app.tsx-owned and SWR-cached (friendStripItems), and
                the greeting / status / library-preview are pure props. It is NOT, however,
                fetch-free on remount: closing a recipe re-mounts this subtree, so
                useMediaUrl re-mints a short-lived GET token per preview thumb / friend
                banner (media.mintGetUrl) and each FeedItemCard NodControls re-loads its
                nods on mount (FeedItemCard.tsx:166).
                We accept that small RPC burst + a friend-strip horizontal-scroll reset as
                the cost of not re-rendering the whole hidden subtree on every keystroke —
                none of it changes behaviour or loses user input. NO visual change on any
                breakpoint — the surface is hidden either way (Rule 7). */}
            {currentRecipeUuid !== null ? null : recipesLoadError && recipes.length === 0 ? (
              // #375 — the initial recipe.list failed with no cached snapshot to
              // fall back on. Surface a quiet retry instead of the false empty
              // state (Rule 8). A first-class "failed" branch belongs in
              // LibraryView; it is owned by a parallel agent this wave, so the
              // failed surface renders app-side here.
              <LibraryLoadError onRetry={() => window.location.reload()} />
            ) : !libraryRouteActive ? (
              /* Dashboard/library split (2026-07-11): the bare `/app` landing is
                 the DASHBOARD — greeting + status → From friends → Resume → the
                 condensed "Your library" preview. It NO LONGER renders the full
                 LibraryView (that moved to `/app/library`). The greeting + status
                 + Resume + preview are all derived from already-loaded `recipes`
                 (no new fetch). The "From friends" strip is populated by
                 `friendItems`, loaded lazily by the effect above via
                 loadFeedLane("friends") — the same path (and shared initial-page
                 cache) /app/feed uses. While that load is in flight / empty /
                 failed, `friendItems` is [] and the strip shows its calm doorway
                 (never a false emptiness claim). */
              <DashboardHero
                recipes={recipes}
                name={name}
                ready={recipesLoadState === "ready"}
                friendItems={friendStripItems}
                hasFriends={hasFriends}
                onInviteFriend={openFriendsInvite}
                onFindFriends={openFriendsFind}
                {...(handle !== null ? { viewerHandle: handle } : {})}
                onOpenRecipe={(uuid) => {
                  activateRecipe(uuid);
                  void navigate(`/app/r/${encodeURIComponent(uuid)}`);
                }}
                onOpenFeed={() => {
                  void navigate("/app/feed");
                }}
                onOpenLibrary={() => {
                  void navigate("/app/library");
                }}
                onNewRecipe={openNewRecipeModal}
              />
            ) : (
              /* `/app/library` — the FULL library grid on its own surface
                 (dashboard/library split). Unchanged internally from the pre-split
                 LibraryView except the redundant mono "Library" slab removal. */
              <>
                <LibraryView
                  recipes={recipes}
                  recipesLoadState={recipesLoadState}
                  collections={collections}
                  activeRecipeUuid={null}
                  onActivateRecipe={(uuid) => {
                    // Route through the shared activateRecipe callback (go-to logic,
                    // race guard, Rule 8 logging all centralised there).
                    activateRecipe(uuid);
                    // D-NAV: push the recipe URL so refresh/back preserve the open
                    // state. Use push (not replace) so back() returns to the library.
                    void navigate(`/app/r/${encodeURIComponent(uuid)}`);
                  }}
                  onSelectVariation={(variationUuid) => {
                    setCurrentDocKey(`draft:${variationUuid}`);
                  }}
                  selectedVariationUuid={currentVariationUuid}
                  onSaveMemberships={handleSaveMemberships}
                  onCreateCollection={handleCreateCollection}
                  onRenameCollection={handleRenameCollection}
                  onDeleteCollection={handleDeleteCollection}
                  // onSpinOff intentionally omitted (nav-chrome-contextual-create
                  // §4.3): LibraryView ignores it (`_onSpinOff`), and the old wiring
                  // wrongly called `recipe.create` (a blank recipe, no lineage) — a
                  // latent bug if the prop were ever revived. Self-duplication now
                  // lives on the recipe surface (RecipeHeader kebab "Duplicate" →
                  // real `recipe.spinOff`), not the library card.
                  // P0-7: the persistent "+ New recipe" row and empty-state CTA open the
                  // new-recipe modal (required human name). `onCreateRecipe` stays
                  // `() => Promise<void>` to satisfy the prop type; it resolves once the
                  // modal is open (the actual create happens on modal submit).
                  onCreateRecipe={async () => {
                    openNewRecipeModal();
                  }}
                  onCreateRecipeFromEmpty={() => {
                    openNewRecipeModal();
                  }}
                  // Beat-0 entry fork (plan T3) — the "Open a worked example" arm.
                  onOpenWorkedExample={handleOpenWorkedExample}
                  onShareLink={async (recipeUuid) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    await (recipeApiClient as any).recipe.setVisibility({
                      uuid: recipeUuid,
                      visibility: "public",
                    });
                    // Raw client bypasses withCache invalidation (Bug #186 pattern).
                    void invalidate(userId, "recipe").catch((err) =>
                      console.warn("[proof.cache] invalidate(recipe) after setVisibility failed", {
                        err,
                      }),
                    );
                  }}
                  onExportRecipe={async (recipeUuid) => {
                    await handleExportRecipe(recipeUuid);
                  }}
                  onRenameRecipe={async (recipeUuid, newTitle) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    await (recipeApiClient as any).recipe.update({
                      uuid: recipeUuid,
                      title: newTitle,
                    });
                    setRecipes((prev) =>
                      prev.map((r) => (r.uuid === recipeUuid ? { ...r, title: newTitle } : r)),
                    );
                    // Raw client bypasses withCache invalidation (Bug #186 pattern).
                    void invalidate(userId, "recipe").catch((err) =>
                      console.warn("[proof.cache] invalidate(recipe) after rename failed", { err }),
                    );
                  }}
                  onDeleteRecipe={async (recipeUuid) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    await (recipeApiClient as any).recipe.delete({ uuid: recipeUuid });
                    setRecipes((prev) => prev.filter((r) => r.uuid !== recipeUuid));
                    // Raw client bypasses withCache invalidation (Bug #186 pattern).
                    void invalidate(userId, "recipe").catch((err) =>
                      console.warn("[proof.cache] invalidate(recipe) after delete failed", { err }),
                    );
                    // Perf §2.C — recipe delete cascades collection_members in the
                    // DO, so the batched collection.listAllMembers cache is stale too.
                    void invalidate(userId, "collection").catch((err) =>
                      console.warn("[proof.cache] invalidate(collection) after delete failed", {
                        err,
                      }),
                    );
                  }}
                  onPrefetchRecipe={handlePrefetchRecipe}
                  apiClient={recipeApiClient}
                  // Wave 5 — DraftsDrawer navigation: activate the recipe +
                  // set the doc key to "draft:<variationUuid>" so the editor opens
                  // on the correct variation's draft.
                  onNavigateToDraft={(recipeUuid, variationUuid) => {
                    setCurrentRecipeUuid(recipeUuid);
                    setCurrentDocKey(`draft:${variationUuid}`);
                    // D-NAV: push the recipe URL so refresh preserves the open state.
                    void navigate(`/app/r/${encodeURIComponent(recipeUuid)}`);
                  }}
                  // Library cards do NOT carry an emoji-edit trigger (operator
                  // 2026-07-02: it was an accidental tap target when selecting a
                  // recipe). Changing the emoji lives only on the recipe viewer page
                  // (RecipeHeader → onChangeRecipeEmoji below).
                  // family-groups (plan 2026-07-02 §4.5): the in-Library Groups section.
                  groupsApi={groupsApi}
                  onOpenGroup={(groupUuid) =>
                    void navigate(`/app/g/${encodeURIComponent(groupUuid)}`)
                  }
                />
              </>
            )}
          </div>

          {/* Secondary tray destinations (shell-persistence fix, 2026-07-16) —
          Feed / Cooks (ledger) / Groups (index · detail · share) / Custom logic
          / Syntax help render HERE, inside the persistent shell content column,
          instead of the standalone routes that unmounted the wordmark top bar +
          NavTray. Mounted only when the current destination is one of these AND
          no Workspace-panel override is active (Settings / Export / Feedback take
          the full view via `workspaceVisibleOverride`). Each is the existing
          route wrapper reused verbatim — it resolves its own session + params +
          navigation, so no prop re-plumbing. The scroll container mirrors the
          library-view-shell so these views (e.g. the centred 640px Feed column)
          get a definite height to scroll within. The top bar above stays
          rendered so its mobile hamburger keeps summoning the NavTray; on desktop
          the tray is docked. design.md §6.0a — a labelled primary-nav entry stays
          reachable on every surface. */}
          {isSecondaryView && !workspaceVisibleOverride && (
            <div className="proof-app-secondary" data-testid="app-secondary">
              <Suspense fallback={null}>
                {appView.kind === "feed" && <FeedRoute />}
                {appView.kind === "cooks" && <LedgerRoute />}
                {appView.kind === "discover" && <DiscoverShellRoute />}
                {appView.kind === "groups" && <GroupsIndexRoute />}
                {appView.kind === "group" && <GroupRoute />}
                {appView.kind === "group-share" && <GroupShareRoute />}
                {appView.kind === "custom-logic" && <CustomLogicPage />}
                {appView.kind === "help" && <HelpPage />}
              </Suspense>
            </div>
          )}

          {/* Workspace — rendered (display:contents when a recipe is open;
          display:none when on the Library landing).
          Wave PERF-2.A — Workspace itself is now React.lazy-imported. Even
          when rendered hidden on the Library landing it does NOT block first
          paint: React Suspense streams the main bundle's UI (LibraryView
          first), then drops the Workspace chunk into the hidden subtree once
          the chunk finishes loading. This keeps the existing capsuleAccountRef
          wiring intact (the Workspace's useLayoutEffect still registers the
          ref once the chunk arrives) while pulling TipTap +
          ProseMirror + Yjs + the editor glue out of the main bundle.
          The Suspense fallback is `null` because the entire subtree is
          aria-hidden / display:none on the Library landing — there is nothing
          to fall back to. On a recipe-open transition the brief gap before
          the chunk resolves is bounded by the network roundtrip; the parent
          AppLoader is not appropriate because the Library is already painted. */}
          <div
            style={{
              // Bug #221 fix (2026-05-23): also visible when the route-level
              // UserMenu lifted `workspaceVisibleOverride` so Settings /
              // Feedback / Exports — all rendered inside the Workspace tree —
              // become visible on the Library landing.
              display: currentRecipeUuid !== null || workspaceVisibleOverride ? "contents" : "none",
              width: "100%",
              height: "100%",
            }}
            aria-hidden={currentRecipeUuid === null && !workspaceVisibleOverride}
            data-testid="workspace-shell"
          >
            <Suspense fallback={null}>
              <Workspace
                api={cachedApi ?? backend.api}
                // empty-Share-body v2 fix: editor "Save as version" freezes the live
                // draft y-doc via the editor's own provider (WS FIFO barrier).
                saveAsVersionApi={saveAsVersionApiShim}
                recipes={recipes}
                collections={collections}
                onSaveMemberships={handleSaveMemberships}
                onCreateCollection={handleCreateCollection}
                onRenameCollection={handleRenameCollection}
                onDeleteCollection={handleDeleteCollection}
                variationName={undefined}
                // currentVariationUuid is derived from currentDocKey (pattern A) so it
                // is always in sync — no separate state setter can fall out of sync.
                variationUuid={currentVariationUuid ?? undefined}
                cookCount={null}
                rating={null}
                currentRecipeUuid={currentRecipeUuid}
                // SEARCH-SHOWS-NOTHING fix (2026-06-14): the Workspace tree stays
                // mounted (display:none) on the Library landing, so its internal
                // <Palette> and the route-level <Palette> below (gated
                // currentRecipeUuid === null) both registered the global
                // `proof:open-palette` listener — one Search click opened BOTH and
                // keystrokes could land in the hidden 0×0 copy. Gate the Workspace
                // Palette on a recipe actually being open; the route-level Palette
                // owns every currentRecipeUuid === null state (Library landing AND
                // the Settings/Feedback/Exports override). Exactly one is live.
                paletteActive={currentRecipeUuid !== null}
                // P0-2 follow-up: thread bodyLoadState so Workspace can distinguish
                // "still loading" from "settled empty" in the cook shell.
                // "loading" until fetchDraftAttempt resolves/errors; "ready" after.
                bodyLoadState={bodyLoadState}
                // Fix #1.4: derive recipeContent from the editor's onContentChange event
                // rather than hard-coding "". Workspace passes this through to all
                // downstream consumers (ValuesPanel, ReadView, tab-title).
                //
                // COOK-FIRST-PAINT (2026-06-16): pass the IDENTITY-GATED body, not the
                // raw derivedRecipeContent. On a variation mismatch this is "" so the
                // Cook ReadView never paints the previous variation's body.
                recipeContent={recipeContentForActive}
                // Fix #1.3: thread the live Y.Doc to TipTapEditor so the BlocksBinding
                // activates and edits are CRDT-backed. Conditional spread avoids
                // exactOptionalPropertyTypes violation (ydoc?: Doc cannot receive undefined).
                //
                // DOC-IDENTITY (Mechanism 2): pass the doc's tag (`ydocDocKey`)
                // ALONGSIDE the doc so Workspace can bind it to the editor ONLY when
                // the key matches the active variation's expected `draft:<uuid>` key.
                // On the first render after an in-app switch the stored doc still
                // belongs to the OLD variation; the gate withholds it there.
                {...(currentYDocEntry !== null
                  ? { ydoc: currentYDocEntry.doc, ydocDocKey: currentYDocEntry.docKey }
                  : {})}
                // COOK-FIRST-PAINT (2026-06-16): wrap so each live update is tagged
                // with the variation open when it fires (gates out late events for a
                // just-closed variation).
                onContentChange={handleDerivedContentChange}
                workerUrl={WORKER_URL}
                attemptInFlight={attemptInFlight}
                syncState={syncState}
                bodyHydrated={bodyHydrated}
                onOpenAttempt={() => void 0}
                isDemo={false}
                dataSource="live"
                // BUG-01 + A7: thread the draft attempt (uuid AND status together —
                // Workspace's required prop) so the Save-as-version button and every
                // status-gated surface (rate-affordance chips, SectionRatingSheet,
                // VersionActions) can render. Fetched by the effect above whenever
                // currentVariationUuid changes. null while loading (surfaces hidden
                // gracefully during the async window).
                currentAttempt={currentAttempt}
                // Wave 3 R-2: onVersionMarkComplete removed (in_progress gone).
                // onVersionReopen: parent re-renders via currentAttempt from the attempt fetch.
                onVersionReopen={() => void 0}
                // NEW-BUG-1: rotate `currentAttempt` to the freshly-opened draft on
                // freeze success. Without this the draft attempt only refetches on a
                // variation change (the effect below is keyed on currentVariationUuid),
                // so a second consecutive freeze in the same session captures the
                // PREVIOUS freeze's frozen uuid — the amend then lands on v(N-1).
                // The freeze does not change the variation, so nothing else rotates it.
                onVersionSaveAsVersion={(result) => {
                  setCurrentAttempt({ uuid: result.newAttemptUuid, status: "draft" });
                }}
                // A-WP10 — dispatch the WS seed-draft-from-frozen call ("Start a
                // draft from vN"). The live draft y-doc is replaced through the
                // sync Worker; a dirty draft returns needs-confirm (the Workspace
                // opens its confirm sheet). On ack we drop the read-side caches so
                // the seeded draft body + provenance surface on the next fetch.
                onStartDraftFromVersion={async (input) => {
                  const result = await backend.seedDraftFromFrozen(input);
                  if (result.status === "ack") {
                    void invalidate(userId, "attempt").catch((err) =>
                      console.warn("[proof.cache] invalidate(attempt) after seed failed", { err }),
                    );
                    void invalidate(userId, "recipe").catch((err) =>
                      console.warn("[proof.cache] invalidate(recipe) after seed failed", { err }),
                    );
                  }
                  return result;
                }}
                onFeedbackSubmit={onFeedbackSubmit}
                onFeedbackPresignUrl={onFeedbackPresignUrl}
                // P0-7: legacy zero-arg fallback now opens the route-level
                // new-recipe modal (required human name). Workspace's own create
                // affordances use its internal modal via onCreateRecipeWithDetails.
                onCreateRecipe={async () => {
                  openNewRecipeModal();
                }}
                onCreateRecipeWithDetails={async ({ title, collectionUuids }) =>
                  handleCreateRecipe({ title, collectionUuids })
                }
                // Paste-to-syntax (C-WP5, F5) — the Workspace-internal New-recipe
                // modal gets the same paste wire so "Paste a recipe" is reachable
                // from every New-recipe path, not just the library landing.
                pasteConvert={pasteConvert}
                onPasteConverted={navigateToConvertedRecipe}
                // B-FR-W2 stale-audience fix (dev QA 2026-07-28): after a
                // version-audience publish/retract settles, drop the cached
                // attempt.list so a reload cannot re-seed the editor from the
                // pre-mutation `friends_shared` bit; the refresh signal re-runs
                // the readAttempts load when a background revalidation lands.
                onAudienceChanged={handleAudienceChanged}
                attemptListRefreshSignal={attemptListRefreshSignal}
                onRenameRecipe={async (recipeUuid, newTitle) => {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  await (recipeApiClient as any).recipe.update({
                    uuid: recipeUuid,
                    title: newTitle,
                  });
                  // Optimistically update in-memory recipes so the explorer/tab title
                  // reflects the new name without a refetch.
                  setRecipes((prev) =>
                    prev.map((r) => (r.uuid === recipeUuid ? { ...r, title: newTitle } : r)),
                  );
                  // Raw client bypasses withCache invalidation (Bug #186 pattern).
                  void invalidate(userId, "recipe").catch((err) =>
                    console.warn("[proof.cache] invalidate(recipe) after rename failed", { err }),
                  );
                }}
                // Operator UX bug U2 (2026-05-03) — destructive delete from the kebab.
                // Throws on RPC failure so the RecipeDeleteModal keeps its inline
                // error visible (Rule 8).
                onDeleteRecipe={async (recipeUuid) => {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  await (recipeApiClient as any).recipe.delete({ uuid: recipeUuid });
                  // Optimistically remove from in-memory state so the explorer/tab list
                  // reflects the deletion without a refetch.
                  setRecipes((prev) => prev.filter((r) => r.uuid !== recipeUuid));
                  // Raw client bypasses withCache invalidation (Bug #186 pattern).
                  void invalidate(userId, "recipe").catch((err) =>
                    console.warn("[proof.cache] invalidate(recipe) after delete failed", { err }),
                  );
                  // Perf §2.C — recipe delete cascades collection_members in the
                  // DO, so the batched collection.listAllMembers cache is stale too.
                  void invalidate(userId, "collection").catch((err) =>
                    console.warn("[proof.cache] invalidate(collection) after delete failed", {
                      err,
                    }),
                  );
                  // If the deleted recipe was the currently-open one, clear the
                  // navigation state so the workspace falls back to the U4 empty state.
                  if (currentRecipeUuid === recipeUuid) {
                    setCurrentRecipeUuid(null);
                    setCurrentDocKey(null);
                    // D-NAV: return URL to /app (recipe no longer exists).
                    void navigate("/app", { replace: true });
                  }
                }}
                // WS16 — "Set go-to" (Flow B, design.md §10.5). Optimistically flip
                // the in-memory `isGoTo` to the target variation (clearing it on
                // every OTHER variation of THIS recipe only), call the shipped
                // `recipe.setDefaultVariation` mutation, invalidate the recipe cache,
                // and revert on error (Bug #186 optimistic + revert pattern). Throws
                // on failure so VariationKebab's busy/finally surfaces the rejection
                // (Rule 8) and the toast in Workspace's onSetGoTo fires.
                onSetDefaultVariation={async (recipeUuid, branchUuid) => {
                  const prev = recipes;
                  setRecipes((rs) =>
                    rs.map((r) =>
                      r.uuid === recipeUuid
                        ? {
                            ...r,
                            variations: r.variations.map((v) => ({
                              ...v,
                              isGoTo: v.uuid === branchUuid,
                            })),
                          }
                        : r,
                    ),
                  );
                  try {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    await (recipeApiClient as any).recipe.setDefaultVariation({
                      recipeUuid,
                      branchUuid,
                    });
                    // Raw client bypasses withCache invalidation (Bug #186 pattern).
                    void invalidate(userId, "recipe").catch((err) =>
                      console.warn("[proof.cache] invalidate(recipe) after set-go-to failed", {
                        err,
                      }),
                    );
                  } catch (err) {
                    // Revert the optimistic flip and surface the failure (Rule 8).
                    setRecipes(prev);
                    console.error("[proof.go-to] recipe.setDefaultVariation failed", { err });
                    throw err;
                  }
                }}
                // Operator UX bug U4 (2026-05-03) — closing the active tab clears
                // the parent-owned `currentRecipeUuid` so the workspace falls back to
                // the empty state instead of leaving the editor stale.
                //
                // Bug #221 fix (2026-05-23): also clear `workspaceVisibleOverride`
                // so leaving Settings (raised from the Library landing) returns
                // the user to the actual Library view. Workspace's "Library"
                // affordance dispatches `onCloseRecipe`; without clearing the
                // override the Workspace would stay visible (rendering its empty
                // state) on top of the still-display:none LibraryView.
                onCloseRecipe={() => {
                  // Bug #2 — URL is the single authoritative signal for whether a
                  // recipe is open. The previous handler cleared the recipe state AND
                  // navigated as two unordered writers; with the Workspace tree still
                  // mounted (display:none), the state-clear and the route slug could
                  // disagree for a render, so the first Library click only refreshed
                  // the recipe and a second was needed. The recipe-identity clears
                  // (currentRecipeUuid / currentDocKey) now live in the close-
                  // reconciliation effect keyed on recipeSlugFromRoute → null, so this
                  // handler just navigates. `workspaceVisibleOverride` is cleared here
                  // because Settings (its sole other user) is NOT URL-routed: a
                  // Settings-from-Library escape has slug null already, so the
                  // reconciliation effect cannot see it — this is the one writer that
                  // can. It does not feed URL-restore, so it never raced the navigate.
                  setWorkspaceVisibleOverride(false);
                  // Dashboard/library split (2026-07-11): closing a recipe
                  // returns to the library GRID, not the /app dashboard —
                  // pre-split `/app` WAS the library, so "close" has always meant
                  // "back to the library". Navigate to /app/library to preserve
                  // that behaviour (the /app landing is now the dashboard).
                  void navigate("/app/library", { replace: true });
                }}
                accountEmail={email}
                accountName={name}
                accountHandle={handle}
                onSignOut={handleSignOut}
                onUpdateBio={handleUpdateBio}
                onUpdateHandle={handleUpdateHandle}
                onChangePassword={handleChangePassword}
                onDeleteAccount={handleDeleteAccount}
                onDeletePreview={handleDeletePreview}
                onOpenAdmin={() => navigate("/admin")}
                onVariationSelect={(variationUuid) => {
                  // currentVariationUuid is derived from currentDocKey (pattern A) —
                  // setting currentDocKey is the only write needed. This is a
                  // VARIATION-LEVEL select for the CURRENTLY-OPEN recipe only; the
                  // recipe identity (currentRecipeUuid) is unchanged. Cross-recipe
                  // activation goes through onActivateRecipe below.
                  setCurrentDocKey(`draft:${variationUuid}`);
                }}
                // WRONG-RECIPE-BODY-ON-SWITCH fix — full recipe navigation from the
                // in-Workspace Search palette / explorer drawer. Mirrors the
                // Library→click path and handlePaletteActivateLibrary via the shared
                // activateRecipe callback (go-to logic, race guard, Rule 8 logging
                // all centralised there).
                onActivateRecipe={(recipeUuid, variationUuid) => {
                  // MODE-PRESERVE (2026-06-16): carry the CURRENT mode sub-segment
                  // (/edit, /diff, …) across the cross-recipe Search switch. A user
                  // editing recipe B who jumps to recipe A via Search expects to keep
                  // editing — landing on A's bare (Cook) route discarded that intent
                  // and broke browser-forward /edit restore. Mirrors the A10 URL-
                  // canonicalisation fix above (app.tsx:928-932): the mode segment is
                  // path index 3 of ["app","r","<slug>","<sub?>"]. Read BEFORE the
                  // flushSync below (activateRecipe never touches location).
                  const modeSegment = location.pathname.split("/").filter(Boolean)[3];

                  // PRE-PAINT ACTIVATION (perf recipe-switch — in-app push path).
                  // MEASURED (screencast, 10/10): on an in-app recipe switch the
                  // OLD recipe's header AND body stay painted UNDER the NEW URL for
                  // ~50–140 ms before the correct body lands. CAUSE: `navigate()`
                  // (React-Router v7 data router) commits the new location on its
                  // own lane, while `activateRecipe`'s `setCurrentRecipeUuid` +
                  // `setCurrentDocKey` are ordinary state updates that land in a
                  // LATER paint — so a full, coherent render of the previous recipe
                  // (identity + doc + read-view all still the old value) commits
                  // under the new URL. The URL-driven paths (browser back/forward)
                  // do NOT flash because their re-activation runs in a pre-paint
                  // useLayoutEffect (app.tsx cross-recipe reconciler ~1641); the
                  // in-app PUSH path had no equivalent pre-paint barrier.
                  //
                  // FIX: `flushSync` forces the activation commit — recipe identity
                  // AND doc key together — to complete SYNCHRONOUSLY, before
                  // `navigate()` moves the URL. This routes the in-app push through
                  // the same "identity settled before the frame paints" sequencing
                  // the back/forward reconciler gives for free. Once the identity is
                  // committed, the existing machinery covers the doc-sync gap exactly
                  // as on a warm Library→open: the doc-identity gate withholds the
                  // stale doc (Workspace boundYdoc: ydocDocKey ≠ draft:<new var>),
                  // the read-view is empty (recipeContentForActive's variation gate),
                  // and the BodySnapshotOverlay fast-paints the NEW variation's
                  // last-saved body (or blank on a cold first switch). Net transition:
                  // old → NEW-header + snapshot/blank body → new body. Never
                  // old-under-new-URL. `recipe.list` ships variations inline, so
                  // activateRecipe resolves the go-to variation synchronously here
                  // (no branch.list in the switch path) — the flushSync commit is
                  // fully coherent (identity + docKey) with no async tail.
                  //
                  // Arm the push-aware one-shot BEFORE flushSync: the cross-recipe
                  // URL reconciler (a layout effect on currentRecipeUuid) fires
                  // INSIDE this flushSync commit while the URL is still stale, and
                  // without this ref it would bounce the freshly-committed identity
                  // back to the recipe being left. The reconciler disarms it when
                  // the URL catches up; the passive failsafe disarms it on any URL
                  // settle. Kept armed across the (sub-frame) gap until navigate's
                  // URL lands so a concurrent recipes revalidation cannot revert it.
                  pendingPushUuidRef.current = recipeUuid;
                  flushSync(() => {
                    activateRecipe(recipeUuid, variationUuid ? { variationUuid } : undefined);
                  });

                  // D-NAV: push the recipe URL so refresh/back preserve the open
                  // state (push, not replace — back() returns to the prior recipe).
                  void navigate(
                    `/app/r/${encodeURIComponent(recipeUuid)}${
                      modeSegment ? `/${encodeURIComponent(modeSegment)}` : ""
                    }`,
                  );
                }}
                // PERF-W1 (c) — warm the highlighted recipe's open-path data on
                // in-Workspace palette selection-change (Workspace threads this
                // straight through to its internal <Palette>). Same prefetch
                // entry point the Library card-hover uses.
                onPrefetchRecipe={handlePrefetchRecipe}
                // PR3b — spin-off title + success callback threaded into the
                // Workspace. recipeTitle drives the spin-off flow's header copy.
                // onSpinOffSuccess opens the new recipe after spin-off completes.
                // Conditional spread required by exactOptionalPropertyTypes: true.
                {...(currentRecipeUuid != null
                  ? {
                      // Miss-proof title resolution (WRONG-TITLE-ON-SWITCH): a fresh
                      // `recipes` entry always wins; the title cache fills the
                      // transient gap when a list-array revalidation has momentarily
                      // dropped the just-activated recipe; "this recipe" is the last
                      // resort for a never-seen uuid (should be unreachable in
                      // practice — every activation/create seeds the cache).
                      recipeTitle:
                        recipes.find((r) => r.uuid === currentRecipeUuid)?.title ??
                        recipeTitleCacheRef.current.get(currentRecipeUuid) ??
                        "this recipe",
                    }
                  : {})}
                {...(handleSpinOffSuccess !== undefined
                  ? { onSpinOffSuccess: handleSpinOffSuccess }
                  : {})}
                // PR3b — lineage + unresolvedRefs for LineageBanner + UnresolvedArtefactBanner.
                // Both are derived from in-memory state; see derivations above.
                {...(lineage !== undefined ? { lineage } : {})}
                {...(unresolvedRefs !== undefined ? { unresolvedRefs } : {})}
                onExportRecipe={handleExportRecipe}
                onExportLibrary={handleExportLibrary}
                // Recipe-header emoji (2026-06-16) — tapping the header emoji (or the
                // "add emoji" affordance) opens the SAME route-level RecipeEmojiModal
                // the library card opens; persistence routes through
                // handleChangeRecipeEmoji. The header reads the active recipe's emoji
                // from `recipes` (kept fresh by handleChangeRecipeEmoji's optimistic
                // update), so a change reflects in the header immediately.
                onChangeRecipeEmoji={(recipeUuid) => setEmojiModalRecipeUuid(recipeUuid)}
                // P0 sign-out fix (2026-05-10) STRUCTURAL CONSOLIDATION — controlled
                // UserMenu open state. Both DesktopTopbar instances (route-level
                // Library landing + Workspace-internal recipe-open) drive the same
                // setter, eliminating the "two avatars, one fix" footgun. The single
                // <UserMenu> mount is at the route level above the workspace-shell
                // wrapper.
                userMenuOpen={userMenuOpen}
                onUserMenuOpenChange={setUserMenuOpen}
                // P0 sign-out fix (2026-05-10) — imperative-escape-hatch refs that
                // let the route-level UserMenu's action rows reach Workspace-internal
                // state without prop-drilling the underlying setters. Same pattern
                // as `capsuleAccountRef`.
                settingsOpenerRef={settingsOpenerRef}
                exportsOpenerRef={exportsOpenerRef}
                feedbackOpenerRef={feedbackOpenerRef}
                // Bug #221 fix (2026-05-23): clear the workspace-shell visibility
                // override when one of the account-menu surfaces (Settings,
                // Feedback modal, Exports panel) dismisses without the user
                // having navigated into a recipe. The override is the
                // route-level flag that flips the workspace-shell wrapper from
                // `display: none` to `display: contents` so those surfaces are
                // visible on the Library landing.
                onAccountSurfaceDismissed={() => {
                  setWorkspaceVisibleOverride(false);
                }}
              />
            </Suspense>
          </div>

          {/* Library-landing mobile chrome — the floating MobileCapsule was retired
          (nav-chrome-contextual-create). The unified DesktopTopbar above now
          carries the Library landing chrome on mobile too (Search · Account),
          and new recipes come from the LibraryView title-row "New recipe" ghost
          control (contextual creation), so no bottom capsule mounts here. */}

          {/* Library-landing search palette mount (P1 fix 2026-05-09).
          Listens for the `proof:open-palette` CustomEvent dispatched by the
          DesktopTopbar Search button.
          Mounted ONLY when no recipe is open — the Workspace owns its own
          <Palette> mount when currentRecipeUuid !== null (gated by the
          `paletteActive` prop threaded above). The two mounts are now genuinely
          mutually exclusive — exactly one Palette is live in every state, so a
          single dialog ever renders and keystrokes always land in the visible
          copy (SEARCH-SHOWS-NOTHING fix 2026-06-14).
          Without this mount the event was silently dropped on the Library
          landing route and the Search button appeared dead.
          Bug 9 parity: onActivate is wired so selecting a result navigates to
          the recipe (mirrors Workspace's handlePaletteActivate). */}
          {currentRecipeUuid === null && (
            <Palette
              api={cachedApi ?? backend.api}
              recipes={recipes}
              onActivate={handlePaletteActivateLibrary}
              // PERF-W1 (c) — warm the highlighted recipe's open-path data on
              // selection-change so a palette switch opens instantly (same
              // prefetch entry point the library card-hover uses).
              onPrefetchRecipe={handlePrefetchRecipe}
            />
          )}
        </div>
        {/* /.proof-app-content — the content column ends here. The FeedbackModal
          below is a fixed-position overlay, so it stays outside the flex column
          (it portals over the whole shell regardless). */}

        {/* #221 Track D (d2) — route-level FeedbackModal, single-sourced here so
          "Send feedback" in the Account menu works on every route (the Library
          list route included). Opened by the NavTray's Send feedback row via
          setFeedbackModalOpen(true). Workspace keeps its own
          modal for the in-recipe RecipeHeader path; the two never both open
          because this one is only triggered from the route-level Account menu.
          design.md §2; branding.md §5; Rule 8 (modal surfaces its own errors). */}
        <FeedbackModal
          open={feedbackModalOpen}
          onClose={() => setFeedbackModalOpen(false)}
          onSubmit={onFeedbackSubmit}
          onRequestPresignUrl={onFeedbackPresignUrl}
        />
      </PipMomentsProvider>
    </div>
  );

  // Guide brain mounted once (plan 2026-07-02 T2). Wraps the shell so the T4
  // GuideStrip (in Workspace) can read it via useGuideContext(); no DOM node.
  return (
    <GuideProvider
      onboardingCompletedAt={onboardingCompletedAt}
      onComplete={completeOnboardingStamp}
    >
      {appShell}
    </GuideProvider>
  );
}

// ── App-level loading screen ──────────────────────────────────────────────────
// Shown while the session check is in-flight (AppRoute) or while the
// RemoteBackend initialises (WorkspaceContainer). Matches the inline loader in
// index.html so the transition from pre-JS loader to React mount is invisible.
//
// Token values mirror tokens.css §4.2:
//   Light: bg #f7f5f1, text #0a0a0a
//   Dark:  bg #0b0b0b, text #f0ede6
//
// Both prefers-color-scheme and .light/.dark class-based overrides are handled
// so the background is correct regardless of whether initTheme() has run.

const APP_LOADER_CSS = `
  .proof-app-loader {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: none;
    /* Light-mode default — warm off-white from tokens.css §4.2 */
    background: #f7f5f1;
    color: #0a0a0a;
    /* P0-12 (Wave 3): lead with Inter so first paint matches the locked shell
       family. This literal stack intentionally duplicates what --font-sans
       resolves to (tokens.css §4.1) because the AppLoader CSS is injected before
       the token stylesheet is guaranteed to have applied at first paint. */
    font-family: "Inter", ui-sans-serif, system-ui, -apple-system,
      BlinkMacSystemFont, "Segoe UI", sans-serif;
  }

  /* Class-based dark override — set by initTheme() */
  html.dark .proof-app-loader {
    background: #0b0b0b;
    color: #f0ede6;
  }

  /* Media-query dark fallback — for system appearance when .dark not yet set */
  @media (prefers-color-scheme: dark) {
    html:not(.light) .proof-app-loader {
      background: #0b0b0b;
      color: #f0ede6;
    }
  }

  .proof-app-loader__wordmark {
    font-size: 24px;
    font-weight: 500;
    letter-spacing: -0.01em;
    /* No animation. Quiet motion per design.md §2. */
  }

  .proof-app-loader__sub {
    margin-top: 8px;
    font-size: 13px;
    font-weight: 400;
    color: var(--muted);
    letter-spacing: 0;
  }
`;

if (typeof document !== "undefined") {
  const id = "proof-app-loader-css";
  if (!document.getElementById(id)) {
    const el = document.createElement("style");
    el.id = id;
    el.textContent = APP_LOADER_CSS;
    document.head.appendChild(el);
  }
}

/** Copy used for the post-update re-warm message. Branding.md §5: no exclamations,
 *  no first-person plural, terminal period. */
const REWARM_COPY = "Applying the latest version.";

function AppLoader() {
  // Read the post-update re-warm flag synchronously on first render.
  // The flag is set by purgeCachesAndReload() before location.replace() so
  // the very next boot cycle sees it while session loading is in-flight.
  const [showRewarm] = useState<boolean>(() => {
    try {
      return sessionStorage.getItem(RELOAD_FLAG_KEY) === "1";
    } catch {
      return false;
    }
  });

  // Clear the flag after the component mounts so it shows only once.
  // We do not use the setter — the message is shown for this single loader
  // render; once the workspace mounts, AppLoader unmounts and the flag is gone.
  useEffect(() => {
    if (showRewarm) {
      try {
        sessionStorage.removeItem(RELOAD_FLAG_KEY);
        // eslint-disable-next-line no-restricted-syntax -- best-effort: clears a one-shot re-warm UI flag, not user data; unavailable storage means there is nothing to clear (Rule 8 documented degradation).
      } catch {
        // sessionStorage unavailable — nothing to clear.
      }
    }
  }, [showRewarm]);

  return (
    <div className="proof-app-loader" aria-hidden="true">
      <span className="proof-app-loader__wordmark">proof.</span>
      {showRewarm && (
        <span className="proof-app-loader__sub" data-testid="app-loader-rewarm">
          {REWARM_COPY}
        </span>
      )}
    </div>
  );
}

// ── Offline banner ─────────────────────────────────────────────────────────────

const OFFLINE_BANNER_CSS = `
  .proof-offline-banner {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 100dvh;
    padding: var(--space-8, 32px) var(--space-4, 16px);
    font-family: var(--font-sans);
    background: var(--surface-base, #faf9f7);
    gap: var(--space-3, 12px);
  }

  .proof-offline-banner__wordmark {
    font-size: 18px;
    font-weight: 600;
    color: var(--text-primary, #1a1814);
    letter-spacing: -0.02em;
    margin-bottom: var(--space-2, 8px);
  }

  .proof-offline-banner__message {
    font-size: 13px;
    color: var(--text-secondary, #6b6560);
    text-align: center;
    max-width: 320px;
  }

  .proof-offline-banner__retry {
    display: inline-flex;
    align-items: center;
    height: 32px;
    padding: 0 var(--space-4, 16px);
    font-family: var(--font-sans);
    font-size: 12px;
    font-weight: 500;
    color: var(--text-primary, #1a1814);
    background: transparent;
    border: 1px solid var(--border-hairline, rgba(0,0,0,0.12));
    border-radius: var(--radius-button, 6px);
    cursor: pointer;
    transition: opacity 200ms ease;
    margin-top: var(--space-2, 8px);
  }

  .proof-offline-banner__retry:hover {
    opacity: 0.75;
  }
`;

if (typeof document !== "undefined") {
  const id = "proof-offline-banner-css";
  if (!document.getElementById(id)) {
    const el = document.createElement("style");
    el.id = id;
    el.textContent = OFFLINE_BANNER_CSS;
    document.head.appendChild(el);
  }
}

interface OfflineBannerProps {
  message: string;
  onRetry: () => void;
}

function OfflineBanner({ message, onRetry }: OfflineBannerProps) {
  return (
    <div className="proof-offline-banner" role="status" data-testid="offline-banner">
      <span className="proof-offline-banner__wordmark">proof.</span>
      <p className="proof-offline-banner__message">{message}</p>
      <button type="button" className="proof-offline-banner__retry" onClick={onRetry}>
        Retry
      </button>
    </div>
  );
}

// ── LibraryLoadError (#375) ───────────────────────────────────────────────────
// Quiet inline surface shown in the library shell when the initial recipe.list
// read failed with no cached snapshot to fall back on. Distinguishes a failed
// load from a genuinely empty library so a populated account never sees a false
// "Your library is empty." (Rule 8 — a failed read must be visible). Copy
// follows the house "Could not load … Check your connection and try again."
// idiom (branding.md §5: precise, no exclamations, no banned words).
function LibraryLoadError({ onRetry }: { onRetry: () => void }) {
  return (
    <div
      role="status"
      data-testid="library-load-error"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "var(--space-3, 12px)",
        minHeight: "40vh",
        padding: "var(--space-4, 16px)",
        textAlign: "center",
      }}
    >
      <p
        style={{
          margin: 0,
          maxWidth: "34ch",
          fontFamily: "var(--font-sans, 'Inter', sans-serif)",
          fontSize: "14px",
          lineHeight: 1.5,
          color: "var(--ink-2, #6B6862)",
        }}
      >
        Could not load your library. Check your connection and try again.
      </p>
      <button
        type="button"
        onClick={onRetry}
        style={{
          appearance: "none",
          border: "1px solid var(--line, #dcd7cc)",
          borderRadius: "8px",
          background: "transparent",
          padding: "8px 16px",
          minHeight: "40px",
          fontFamily: "var(--font-sans, 'Inter', sans-serif)",
          fontSize: "13px",
          color: "var(--ink-1, #23201B)",
          cursor: "pointer",
        }}
      >
        Try again
      </button>
    </div>
  );
}

// ── App route component ───────────────────────────────────────────────────────

const APP_ROUTE_CSS = `
  .proof-app-route {
    display: flex;
    flex-direction: column;
    height: 100dvh;
    width: 100%;
    overflow: hidden;
  }

  /*
   * nav-shell R1 — the content column that sits to the right of the docked
   * NavTray on desktop. On mobile / tablet it is the full-width shell (the
   * NavTray is a fixed overlay, out of flow). The column owns its own vertical
   * flow (top bar + Library view / Workspace).
   */
  .proof-app-content {
    display: flex;
    flex-direction: column;
    flex: 1 1 0;
    min-width: 0;
    min-height: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  /*
   * Shell-persistence fix (2026-07-16) — the scroll container for the secondary
   * tray destinations (Feed / Cooks / Groups / Custom logic / Syntax help) that
   * now render inside the shell content column. Mirrors the visible
   * .proof-library-view-shell metrics so each destination view (e.g. the centred
   * 640px Feed column, which sets no height itself) gets a definite height to
   * scroll within instead of overflowing the 100dvh shell. It is a flex child of
   * .proof-app-content, filling the space below the top bar; overflowX is clamped
   * to prevent horizontal jitter from descendant content.
   */
  .proof-app-secondary {
    flex: 1 1 0;
    min-height: 0;
    width: 100%;
    overflow-y: auto;
    overflow-x: hidden;
  }

  /*
   * Desktop (≥1024px): the app shell becomes a two-column flex row — the docked
   * NavTray (fixed 224px, its own border) on the left, the content column on
   * the right. The NavTray sets its own width/flex-basis in NavTray.css; here we
   * only flip the row direction so the two become side-by-side columns of the
   * 100dvh shell.
   */
  @media (min-width: 1024px) {
    .proof-app-route {
      flex-direction: row;
    }
  }

  /*
   * Library landing bottom clearance.
   * The floating MobileCapsule that used to overlay the bottom of the viewport
   * is retired (nav-chrome-contextual-create) — the unified top bar carries the
   * mobile chrome now. The --capsule-clearance token was retuned to
   * env(safe-area-inset-bottom, 0px) + 16px (tokens.css), so this simply keeps
   * a small safe-area-aware breathing gap below the last recipe row on mobile
   * (no more ~60-96 px of dead space). Token retained (not renamed) — see
   * tokens.css for the shared-consumer rationale.
   */
  @media (max-width: 767px) {
    .proof-library-view-shell {
      padding-bottom: var(--capsule-clearance);
    }
  }
`;

if (typeof document !== "undefined") {
  const id = "proof-app-route-css";
  if (!document.getElementById(id)) {
    const el = document.createElement("style");
    el.id = id;
    el.textContent = APP_ROUTE_CSS;
    document.head.appendChild(el);
  }
}

function AppRouteImpl() {
  const navigate = useNavigate();

  // ── Session cache — synchronous initialisation ────────────────────────────
  // Read the localStorage cache before the first render so we can skip the
  // { kind: "loading" } state entirely when a cache hit exists. This is the
  // load-bearing change for the 200–350 ms warm-path win:
  //
  //   Before: mount → loading state → fetch (200–350 ms) → render
  //   After:  mount → render from cache immediately → background revalidation
  //
  // readSessionCache() is synchronous and window-guarded. If the cache is
  // missing or corrupt it returns null and we fall back to the loading path.
  const [session, setSession] = useState<SessionState>(() => {
    const cached = readSessionCache();
    if (cached !== null) {
      // Both fresh and stale cache hits produce an immediate render.
      // Staleness only affects whether the background revalidation fires
      // immediately on mount (always) — the user sees something right away.
      const s = cached.session;
      // isAdmin defaults false for entries written before the field existed
      // (A9 fix) — the background revalidation rewrites the authoritative
      // value within one round-trip. tier defaults to the honest `free`
      // baseline for pre-entitlements entries, likewise rewritten on revalidate.
      if (s.kind === "authenticated")
        return {
          ...s,
          isAdmin: s.isAdmin === true,
          tier: s.tier === "paid" ? "paid" : "free",
          // planId defaults to `free` for pre-C-WP0 cache entries — the
          // background revalidation rewrites the authoritative value.
          planId:
            s.planId === "plus" || s.planId === "pro" || s.planId === "max" ? s.planId : "free",
        };
      if (s.kind === "unauthenticated") return { kind: "unauthenticated" };
    }
    return { kind: "loading" };
  });

  // Wave PERF-1 — reset the recipe-open timeline and stamp the routeEnter mark
  // on first render of /app (or any /app/r/:slug variant). The reset is
  // per-component-mount rather than per-pathname-change: every `/app**` path
  // resolves through one route object (routes/app-routes.ts) and AppRouteImpl
  // mounts exactly ONCE per SPA session, so the boundary is "the sweep that
  // begins when /app first matches". No-op in production (the perf seam is
  // dev / VITE_PERF_PROFILE only).
  //
  // AUDITED under route unification (2026-07-24). Unchanged by that change, but
  // worth stating: because nothing re-stamps `routeEnter` after the first mount,
  // only the FIRST recipe open of a session carries a mark-based timeline. Later
  // in-SPA opens are measured by wall clock — `perf-recipe-load-suite.spec.ts`
  // calls `window.__perf.reset()` itself, treats a missing `recipe.routeEnter`
  // as "no mark span" (openSpanFromMarks → null), falls back to wall clock, and
  // reports which source it used, so the numbers stay honest rather than
  // silently under-reporting. Adding a per-open sweep boundary is a real
  // instrumentation improvement, but it must not land as a mark stamped AFTER
  // the in-app switch's flushSync activation (that would report a switch as
  // faster than it is); left as a measurement-protocol follow-up in the plan.

  // R2 funnel instrumentation — the two browser-only session facts (display
  // mode + `appinstalled`). Called unconditionally (hooks rules) above the
  // early returns below; no-ops until the session is confirmed authenticated.
  useFunnelBeacon(session.kind === "authenticated");

  const perfBootstrappedRef = useRef(false);
  if (!perfBootstrappedRef.current) {
    perfBootstrappedRef.current = true;
    perfReset();
    perfMark(RECIPE_OPEN_MARKS.routeEnter);
  }

  const checkSession = useCallback(() => {
    setSession({ kind: "loading" });
    void fetchSession().then(setSession);
  }, []);

  /**
   * Re-fetch the session WITHOUT entering the loading state. Used by mutations
   * (update display name, etc.) that need to invalidate the cached payload but
   * must not unmount the Workspace tree mid-edit. Failures are silent — the
   * underlying mutation has already succeeded server-side; a transient network
   * blip on the refresh just delays UI sync until the next page load.
   *
   * On success, the new session is also written to the cache so the next mount
   * picks up the refreshed payload (e.g. after a display-name change).
   */
  const refreshSession = useCallback(() => {
    void fetchSession().then((next) => {
      // Only update on success — unauthenticated/network-error responses on
      // a refresh are not a sign-out signal (the original mutation already
      // proved auth was valid). Keep the existing session in those cases.
      if (next.kind === "authenticated") {
        setSession(next);
        writeSessionCache(next);
      }
    });
  }, []);

  // ── Background revalidation on mount ─────────────────────────────────────
  // Always fire a revalidation on mount:
  //   - If the cache was fresh: confirms the session is still valid.
  //   - If the cache was stale: fetches the authoritative session.
  //   - If the cache was empty: blocks on the result (sets session from loading).
  // The revalidation never enters the loading state when the cache provided
  // an initial value — it only calls setSession when the result differs or
  // when the initial state was "loading".
  useEffect(() => {
    const cached = readSessionCache();
    const hasInitialValue = cached !== null;

    void fetchSession().then((next) => {
      if (next.kind === "authenticated") {
        writeSessionCache(next);
      } else if (next.kind === "unauthenticated") {
        // Confirmed signed-out — write to cache so subsequent mounts are
        // immediate even in the unauthenticated state.
        writeSessionCache({ kind: "unauthenticated" });
      }
      // Always update state when there was no initial value (loading path).
      // When there was an initial value, only update if the result differs
      // from what the cache gave us — avoids a spurious re-render on the
      // common case where the session is still valid and unchanged.
      if (!hasInitialValue) {
        setSession(next);
      } else {
        // Update the session if the revalidation returned a materially
        // different state (e.g. unauthenticated when cache said authenticated,
        // or a changed userId/email/handle/name).
        setSession((prev) => {
          if (next.kind !== prev.kind) return next;
          if (next.kind === "authenticated" && prev.kind === "authenticated") {
            if (
              next.userId !== prev.userId ||
              next.email !== prev.email ||
              next.name !== prev.name ||
              next.handle !== prev.handle ||
              next.onboardingCompletedAt !== prev.onboardingCompletedAt ||
              next.isAdmin !== prev.isAdmin ||
              next.tier !== prev.tier ||
              next.planId !== prev.planId
            ) {
              return next;
            }
          }
          // network-error during revalidation: keep existing cached state.
          if (next.kind === "network-error") return prev;
          return prev;
        });
      }
    });
  }, []); // mount-only: intentionally runs once to revalidate the cached session

  // ── Multi-tab sign-out ────────────────────────────────────────────────────
  // When another tab calls clearSessionCache() before logout, localStorage
  // dispatches a `storage` event on this tab with newValue === null. We then
  // re-fetch the session — the cookie will already be cleared by the time the
  // fetch resolves, so the result will be `unauthenticated`.
  useEffect(() => {
    const unsub = onSessionCacheCleared(() => {
      void fetchSession().then((next) => {
        setSession(next);
        // Do not write to cache here — the clear was intentional (sign-out).
        // Writing unauthenticated back would immediately re-populate what was
        // just cleared; the next sign-in writes the new session.
      });
    });
    return unsub;
  }, []);

  const handleSessionEstablished = useCallback(() => {
    // Re-check session so the Workspace mounts with the real session.
    // Write to cache so the NEXT mount is immediate (warm path).
    void fetchSession().then((next) => {
      if (next.kind === "authenticated") {
        writeSessionCache(next);
      }
      setSession(next);
    });
  }, []);

  /**
   * Called by WorkspaceContainer after a successful authClient.logout().
   * Navigates to /sign-in (a dedicated auth route within /app/* wildcard) so
   * the URL is observably different from /app — satisfying the e2e step-8
   * assertion (`toHaveURL(/sign-in|login|\/$/)`) and ensuring a clean
   * `fetchSession()` re-check when AppRoute remounts on that path.
   *
   * The old pattern was `navigate("/")`, which the root index immediately
   * redirected back to /app, leaving the user in an authenticated shell with
   * a cleared cookie. This is the correct post-logout pattern.
   */
  const handleSignedOut = useCallback(() => {
    void navigate("/sign-in", { replace: true });
  }, [navigate]);

  // ── Loading ───────────────────────────────────────────────────────────────
  if (session.kind === "loading") {
    // Show the loader while the session check is in-flight — prevents a blank
    // frame between the HTML loader dismounting and the Workspace mounting.
    return <AppLoader />;
  }

  // ── Network error ─────────────────────────────────────────────────────────
  if (session.kind === "network-error") {
    return <OfflineBanner message={session.message} onRetry={checkSession} />;
  }

  // ── No session → sign-in gate ─────────────────────────────────────────────
  if (session.kind === "unauthenticated") {
    return <SignInGate onSessionEstablished={handleSessionEstablished} />;
  }

  // ── Authenticated: render Workspace with RemoteBackend ────────────────────
  // refreshSession is a non-disruptive re-fetch (does not enter loading state).
  // It is forwarded as `onSessionRefresh` so child mutations (display-name
  // update, etc.) can invalidate the cached session payload without unmounting
  // the Workspace tree mid-edit.
  return (
    <WorkspaceContainer
      userId={session.userId}
      email={session.email}
      name={session.name}
      onboardingCompletedAt={session.onboardingCompletedAt}
      handle={session.handle}
      onSessionRefresh={refreshSession}
      onSignedOut={handleSignedOut}
    />
  );
}

// ── AppRoute (default export) — T0.1 dev-anonymous gate wrapper ───────────────
//
// This wrapper has NO hooks. It either mounts the full auth flow (`AppRouteImpl`)
// or the dev-only anonymous shell (`DevAnonymousShell`), depending on two
// compile-time constants:
//
//   DEV_ANONYMOUS — true only in dev builds when `?dev=anonymous` is in the URL.
//   DevAnonymousShell — non-null only when `import.meta.env.DEV` is true.
//
// In production, Rollup statically evaluates both to false/null, eliminating
// this entire function body and the `DevAnonymousShell` import.
//
// Rules of Hooks: this component has no hooks, so the conditional is legal.
// `AppRouteImpl`'s hooks only execute when `AppRouteImpl` is mounted.
// `DevAnonymousShell`'s hooks only execute when it is mounted.
export default function AppRoute() {
  if (DEV_ANONYMOUS && DevAnonymousShell) {
    const Shell = DevAnonymousShell;
    return (
      <Suspense fallback={null}>
        <Shell />
      </Suspense>
    );
  }
  return <AppRouteImpl />;
}
